import React, { useState, useEffect, useMemo, useRef, Component } from 'react';
import { supabase } from './supabaseClient';
import { INITIAL_DATA } from './initialData';

// ==================== ERROR BOUNDARY ====================
// Catches React errors and prevents full app crash

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', background: '#0a0a0f', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif', padding: '20px' }}>
          <div style={{ maxWidth: '500px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{ fontSize: '20px', color: '#ef4444', marginBottom: '12px' }}>Something went wrong</h2>
            <p style={{ fontSize: '14px', color: '#6b6b7b', marginBottom: '20px', lineHeight: 1.6 }}>
              An error occurred while displaying this content. Try refreshing the page or navigating to a different tab.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 24px',
                background: '#3b82f6',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'inherit'
              }}
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ==================== DATE FORMATTING HELPERS ====================
// For displaying relative dates on breaking news items

function formatRelativeDate(dateString) {
  if (!dateString) return '';
  
  // Parse date as local time by adding T12:00:00 to avoid timezone issues
  const dateStr = dateString.includes('T') ? dateString : `${dateString}T12:00:00`;
  const date = new Date(dateStr);
  const now = new Date();
  
  // Compare just the dates, not times
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((nowOnly - dateOnly) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'TODAY';
  if (diffDays === 1) return 'YESTERDAY';
  
  // Format as "JAN 30, 2026"
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  }).toUpperCase();
}

// Get latest breaking news for a specific category
function getLatestBreakingNews(breakingNews, category) {
  if (!Array.isArray(breakingNews) || breakingNews.length === 0) return null;
  
  const sorted = [...breakingNews]
    .filter(n => !category || n.category === category)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  
  return sorted[0] || null;
}

// ==================== UTILITY FUNCTIONS ====================
function headlinesSimilar(h1, h2) {
  if (!h1 || !h2) return false;
  const w1 = h1.toLowerCase().replace(/[^a-z0-9\s]/g,'').split(/\s+/).filter(w=>w.length>3);
  const w2 = h2.toLowerCase().replace(/[^a-z0-9\s]/g,'').split(/\s+/).filter(w=>w.length>3);
  if (!w1.length || !w2.length) return false;
  return w1.filter(w=>w2.includes(w)).length / Math.min(w1.length, w2.length) >= 0.6;
}
function dedupeBreakingNews(items) {
  const r = [];
  for (const item of items) { if (!r.some(e=>headlinesSimilar(e.headline,item.headline))) r.push(item); }
  return r;
}
const LOW_QUALITY_SOURCES = new Set(['wion','wionews','sputnik','rt','oann','infowars','newsmax','breitbart','epochtimes','thegatewaypundit','zerohedge']);
function isLowQualitySource(s) {
  const n = (typeof s==='object'?s.name:s||'').toLowerCase().replace(/[^a-z]/g,'');
  return LOW_QUALITY_SOURCES.has(n);
}

// ==================== CATEGORY NEWS COMPONENT ====================
// Renders: breaking banner (most recent) + collapsible older updates
// All items kept permanently — never deleted
const CATEGORY_COLORS = {
  ice: '#dc2626', epstein: '#f59e0b', lawsuit: '#a855f7',
  constitutional: '#3b82f6', poll: '#22c55e', promise: '#f97316',
  environment: '#10b981', money: '#eab308',
  'foreign-policy': '#6366f1', 'healthcare-education': '#14b8a6'
};

const CategoryNews = React.memo(function CategoryNews({ breakingNews, category, renderSourceLinks, formatRelativeDate }) {
  const [expanded, setExpanded] = React.useState(false);
  const color = CATEGORY_COLORS[category] || '#ef4444';
  
  const allItems = (breakingNews || [])
    .filter(n => n.category === category && n.headline)
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  
  if (!allItems.length) return null;
  
  const [latest, ...older] = allItems;
  const INITIAL_SHOW = 5;
  const visibleOlder = expanded ? older : older.slice(0, INITIAL_SHOW);


  // Group older items by date for cleaner display
  const groupByDate = (items) => {
    const groups = {};
    items.forEach(item => {
      const key = formatRelativeDate(item.date);
      if (!groups[key]) groups[key] = [];
      groups[key].push(item);
    });
    return Object.entries(groups);
  };
  
  return (
    <div style={{ marginBottom: '24px' }}>
      {/* Latest item — prominent treatment */}
      <div id={'news-' + latest.id} style={{
        padding: '16px 18px',
        background: color + '0a',
        border: '1px solid ' + color + '30',
        borderRadius: '12px',
        marginBottom: older.length > 0 ? '16px' : '0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, animation: 'pulse 2s infinite' }} />
          <span style={{ fontSize: '10px', fontWeight: '700', color: color, letterSpacing: '1px', textTransform: 'uppercase' }}>Latest Update</span>
          <span style={{ fontSize: '10px', color: '#4a4a5a' }}>{formatRelativeDate(latest.date)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '8px' }}>
          <div style={{ fontSize: '15px', color: '#fff', fontWeight: '600', lineHeight: 1.4 }}>{latest.headline}</div>
          <ShareButton title={latest.headline} subtitle={latest.summary} source={latest.sources?.[0]?.name} category={category} style={{ flexShrink: 0 }} />
        </div>
        {latest.summary && <div style={{ fontSize: '13px', color: '#a8a8b8', lineHeight: 1.5 }}>{latest.summary}</div>}
        {latest.sources && latest.sources.length > 0 && (
          <div style={{ marginTop: '10px' }}>{renderSourceLinks(latest.sources)}</div>
        )}
      </div>
      
      {/* Timeline of older updates — grouped by date */}
      {older.length > 0 && (
        <div style={{ position: 'relative', paddingLeft: '20px', borderLeft: '2px solid ' + color + '20' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '12px', color: '#6b6b7b', fontWeight: '600' }}>
              Update Timeline ({older.length})
            </span>
            {older.length > INITIAL_SHOW && (
              <button onClick={() => setExpanded(!expanded)} style={{
                background: 'none', border: 'none', color: color, fontSize: '11px',
                cursor: 'pointer', fontFamily: 'inherit', fontWeight: '600', padding: '4px 8px'
              }}>
                {expanded ? 'Show Less ▲' : `Show All ${older.length} ▼`}
              </button>
            )}
          </div>
          {groupByDate(visibleOlder).map(([dateLabel, items]) => (
            <div key={dateLabel}>
              <div style={{ 
                fontSize: '10px', fontWeight: '700', color: color, letterSpacing: '0.5px',
                marginBottom: '8px', marginLeft: '-27px', paddingLeft: '34px',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute', left: '-7px', top: '2px',
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: '#0a0a0f', border: '2px solid ' + color + '60'
                }} />
                {dateLabel}
              </div>
              {items.map((item, idx) => (
                <div key={item.id || idx} id={'news-' + item.id} style={{
                  padding: '12px 14px',
                  marginBottom: '8px',
                  background: 'rgba(255,255,255,0.02)',
                  borderRadius: '8px',
                }}>
                  <div style={{ fontSize: '13px', color: '#e8e8ed', fontWeight: '600', lineHeight: 1.4 }}>{item.headline}</div>
                  {item.summary && <div style={{ fontSize: '12px', color: '#8a8a9a', lineHeight: 1.5, marginTop: '6px' }}>{item.summary}</div>}
                  {item.sources && item.sources.length > 0 && (
                    <div style={{ marginTop: '8px' }}>{renderSourceLinks(item.sources)}</div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

// ==================== TERM COMPARISON COMPONENT ====================
// Compare first term (2017-2021) vs second term (2025-present)

const TermComparison = React.memo(function TermComparison({ Card }) {
  const [showComparison, setShowComparison] = React.useState(false);

  if (!showComparison) {
    return (
      <div style={{ marginBottom: '24px', textAlign: 'center' }}>
        <button
          onClick={() => setShowComparison(true)}
          style={{
            padding: '12px 24px',
            background: 'rgba(59,130,246,0.15)',
            border: '1px solid rgba(59,130,246,0.3)',
            borderRadius: '10px',
            color: '#3b82f6',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          📊 Compare First Term vs Second Term
        </button>
      </div>
    );
  }

  return (
    <Card style={{ marginBottom: '24px', borderLeft: '3px solid #3b82f6' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ fontSize: '16px', color: '#3b82f6', fontWeight: '600', margin: 0 }}>
          First Term vs Second Term Comparison
        </h3>
        <button
          onClick={() => setShowComparison(false)}
          style={{
            background: 'none',
            border: 'none',
            color: '#6b6b7b',
            fontSize: '20px',
            cursor: 'pointer',
            padding: '4px'
          }}
        >
          ×
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))', gap: '20px' }}>
        {/* First Term */}
        <div>
          <div style={{ fontSize: '12px', color: '#6b6b7b', fontWeight: '600', marginBottom: '12px', textAlign: 'center' }}>
            FIRST TERM (2017-2021)
          </div>
          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={{ padding: '12px', background: '#0a0a0f', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '4px' }}>Border Wall</div>
              <div style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600' }}>52 miles of new wall</div>
              <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '2px' }}>Promised 1,000 miles</div>
            </div>
            <div style={{ padding: '12px', background: '#0a0a0f', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '4px' }}>Manufacturing Jobs</div>
              <div style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600' }}>-178,000 lost</div>
              <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '2px' }}>Promised millions added</div>
            </div>
            <div style={{ padding: '12px', background: '#0a0a0f', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '4px' }}>Healthcare Plan</div>
              <div style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600' }}>Never released</div>
              <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '2px' }}>Promised "in 2 weeks"</div>
            </div>
            <div style={{ padding: '12px', background: '#0a0a0f', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '4px' }}>National Debt</div>
              <div style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600' }}>+$7.8T added</div>
              <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '2px' }}>Promised to eliminate</div>
            </div>
          </div>
        </div>

        {/* Second Term */}
        <div>
          <div style={{ fontSize: '12px', color: '#6b6b7b', fontWeight: '600', marginBottom: '12px', textAlign: 'center' }}>
            SECOND TERM (2025-Present)
          </div>
          <div style={{ display: 'grid', gap: '10px' }}>
            <div style={{ padding: '12px', background: '#0a0a0f', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '4px' }}>Grocery Prices</div>
              <div style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600' }}>+3% increase</div>
              <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '2px' }}>Promised "way down"</div>
            </div>
            <div style={{ padding: '12px', background: '#0a0a0f', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '4px' }}>Manufacturing Jobs</div>
              <div style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600' }}>-72,000 lost</div>
              <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '2px' }}>First month alone</div>
            </div>
            <div style={{ padding: '12px', background: '#0a0a0f', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '4px' }}>ICE Shootings</div>
              <div style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600' }}>33+ incidents</div>
              <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '2px' }}>9 deaths, 19+ wounded</div>
            </div>
            <div style={{ padding: '12px', background: '#0a0a0f', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '4px' }}>Lawsuits Filed</div>
              <div style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600' }}>697+ lawsuits filed</div>
              <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '2px' }}>Historic record</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(239,68,68,0.06)', borderRadius: '8px', fontSize: '12px', color: '#fca5a5', lineHeight: 1.6 }}>
        <strong>Pattern:</strong> Consistent record of unfulfilled promises across both terms, with second term showing accelerated legal challenges and enforcement incidents.
      </div>
    </Card>
  );
});

// ==================== TIMELINE VISUALIZATION ====================
// Simple timeline chart showing trends over time

// ==================== HISTORICAL COMPARISON COMPONENT ====================
// Shows current administration numbers against prior administrations with visual bars

const COMPARISON_DATA = {
  environment: {
    title: 'ENVIRONMENTAL ROLLBACKS — HISTORICAL COMPARISON',
    source: 'Harvard Law School, Columbia Law, Brookings Institution',
    color: '#22c55e',
    metrics: [
      {
        label: 'Environmental Regulations Rolled Back',
        bars: [
          { admin: 'Trump Term 2', period: '14 months', value: 440, color: '#ef4444', highlight: true },
          { admin: 'Trump Term 1', period: '4 years', value: 98, color: '#f97316' },
          { admin: 'Bush', period: '8 years', value: 25, color: '#6b6b7b' },
          { admin: 'Obama', period: '8 years', value: 3, color: '#3b82f6', note: 'Added 400+ protections' },
        ],
      },
      {
        label: 'EPA Budget (Billions)',
        unit: '$',
        bars: [
          { admin: 'Trump Term 2', period: 'FY2026', value: 3.2, color: '#ef4444', highlight: true },
          { admin: 'Trump Term 1', period: 'avg', value: 6.1, color: '#f97316' },
          { admin: 'Obama', period: 'avg', value: 8.3, color: '#3b82f6' },
          { admin: 'Historical Avg', period: '2000-2024', value: 8.0, color: '#6b6b7b' },
        ],
        invertColor: true,
      },
    ],
  },
  ice: {
    title: 'IMMIGRATION ENFORCEMENT — HISTORICAL COMPARISON',
    source: 'ACLU, Vera Institute, State Dept, CATO Institute',
    color: '#dc2626',
    metrics: [
      {
        label: 'Agent-Involved Shootings',
        bars: [
          { admin: 'Trump Term 2', period: '14 months', value: 33, color: '#ef4444', highlight: true },
          { admin: 'Trump Term 1', period: '4 years', value: 6, color: '#f97316' },
          { admin: 'Obama', period: '8 years', value: 8, color: '#3b82f6' },
          { admin: 'Bush', period: '8 years', value: 5, color: '#6b6b7b' },
        ],
      },
      {
        label: 'Detention Deaths Per Year',
        bars: [
          { admin: '2025', period: 'Trump T2', value: 32, color: '#ef4444', highlight: true, note: 'Highest since 2004' },
          { admin: '2024', period: 'Biden', value: 9, color: '#3b82f6' },
          { admin: '2019', period: 'Trump T1', value: 8, color: '#f97316' },
          { admin: 'Avg 2010–2023', period: '', value: 7, color: '#6b6b7b' },
        ],
      },
      {
        label: 'Refugee Admissions',
        bars: [
          { admin: 'FY2024', period: 'Biden', value: 100000, color: '#3b82f6', displayValue: '100,000' },
          { admin: 'FY2017', period: 'Obama', value: 85000, color: '#22c55e', displayValue: '85,000' },
          { admin: 'FY2020', period: 'Trump T1', value: 11814, color: '#f97316', displayValue: '11,814' },
          { admin: 'FY2025', period: 'Trump T2', value: 1226, color: '#ef4444', highlight: true, displayValue: '1,226', note: '-98% from prior year' },
        ],
        invertColor: true,
      },
    ],
  },
};

const HistoricalComparison = React.memo(function HistoricalComparison({ category }) {
  const config = COMPARISON_DATA[category];
  if (!config) return null;

  return (
    <div style={{ marginBottom: '24px' }}>
      <div style={{ fontSize: '12px', color: '#6b6b7b', fontWeight: '600', marginBottom: '4px' }}>
        {config.title}
      </div>
      <div style={{ fontSize: '9px', color: '#4a4a5a', marginBottom: '16px' }}>
        Sources: {config.source}
      </div>

      {config.metrics.map((metric, mi) => {
        const maxVal = Math.max(...metric.bars.map(b => b.value));
        return (
          <div key={mi} style={{ marginBottom: mi < config.metrics.length - 1 ? '20px' : '0' }}>
            <div style={{ fontSize: '11px', color: '#a8a8b8', fontWeight: '600', marginBottom: '10px' }}>
              {metric.label}
            </div>
            {metric.bars.map((bar, bi) => {
              const barPct = Math.max((bar.value / maxVal) * 100, 2);
              const labelInside = barPct > 60;
              return (
              <div key={bi} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                <div style={{ width: 'min(100px, 30%)', flexShrink: 0, textAlign: 'right' }}>
                  <div style={{ fontSize: '11px', color: bar.highlight ? '#fff' : '#6b6b7b', fontWeight: bar.highlight ? '700' : '400' }}>
                    {bar.admin}
                  </div>
                  {bar.period && (
                    <div style={{ fontSize: '9px', color: '#4a4a5a' }}>{bar.period}</div>
                  )}
                </div>
                <div style={{ flex: 1, position: 'relative', height: '22px', overflow: 'hidden' }}>
                  <div style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: `${barPct}%`,
                    background: bar.highlight
                      ? `linear-gradient(90deg, ${bar.color}, ${bar.color}cc)`
                      : bar.color + '40',
                    borderRadius: '4px',
                    transition: 'width 0.6s ease',
                    boxShadow: bar.highlight ? `0 0 12px ${bar.color}40` : 'none',
                    display: 'flex', alignItems: 'center',
                    justifyContent: labelInside ? 'flex-end' : 'flex-start',
                    overflow: 'visible',
                  }}>
                    {labelInside && (
                      <div style={{
                        paddingRight: '6px',
                        fontSize: '12px', fontWeight: '700',
                        color: '#fff',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}>
                        {metric.unit || ''}{bar.displayValue || bar.value.toLocaleString()}
                        {bar.note && <span style={{ fontSize: '9px', opacity: 0.75, marginLeft: '4px', fontWeight: '400' }}>{bar.note}</span>}
                      </div>
                    )}
                  </div>
                  {!labelInside && (
                    <div style={{
                      position: 'absolute', left: `${barPct}%`,
                      top: '50%', transform: 'translateY(-50%)',
                      paddingLeft: '8px',
                      fontSize: '12px', fontWeight: '700',
                      color: bar.highlight ? bar.color : '#6b6b7b',
                      whiteSpace: 'nowrap',
                    }}>
                      {metric.unit || ''}{bar.displayValue || bar.value.toLocaleString()}
                      {bar.note && <span style={{ fontSize: '9px', color: '#4a4a5a', marginLeft: '6px', fontWeight: '400' }}>{bar.note}</span>}
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
});

// ==================== SEARCH COMPONENT ====================
// Intelligence Terminal aesthetic — investigative journalist's classified database

const SearchTab = React.memo(function SearchTab({ data, renderSourceLinks, formatRelativeDate }) {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [categoryFilter, setCategoryFilter] = React.useState('all');
  const [dateRange, setDateRange] = React.useState('all');
  const [sortOrder, setSortOrder] = React.useState('newest');
  const [searchHistory, setSearchHistory] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem('searchHistory') || '[]'); }
    catch { return []; }
  });
  const inputRef = React.useRef(null);

  const allSearchableItems = React.useMemo(() => {
    const items = [];
    (data.breakingNews || []).forEach(n => {
      if (n.headline && n.date) items.push({ ...n, type: 'news' });
    });
    (data.lawsuits || []).forEach(l => items.push({
      type: 'record', headline: l.title, summary: l.summary || l.description || '',
      date: l.filed || '', category: 'lawsuit', sources: l.sources || []
    }));
    (data.brokenPromises || []).forEach(p => items.push({
      type: 'record', headline: p.promise, summary: (p.reality || []).join('; '),
      date: p.datePromised || '', category: 'promise', sources: Array.isArray(p.sources) ? p.sources.map(s => typeof s === 'string' ? { name: s, url: '' } : s) : []
    }));
    (data.constitutionalConcerns || []).forEach(c => items.push({
      type: 'record', headline: c.title, summary: c.description || '',
      date: c.dateAdded || '', category: 'constitutional', sources: c.sources || []
    }));
    (data.iceVictims || []).forEach(v => items.push({
      type: 'record', headline: v.name, summary: [v.location, v.details].filter(Boolean).join(' — '),
      date: v.date || '', category: 'ice', sources: v.sources || []
    }));
    return items;
  }, [data.breakingNews, data.lawsuits, data.brokenPromises, data.constitutionalConcerns, data.iceVictims]);

  const allItems = allSearchableItems
    .sort((a, b) => sortOrder === 'newest'
      ? new Date(b.date) - new Date(a.date)
      : new Date(a.date) - new Date(b.date)
    );

  const highlightMatch = (text, query) => {
    if (!query || !query.trim() || !text) return text;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    if (parts.length === 1) return text;
    return parts.map((part, i) =>
      part.toLowerCase() === query.toLowerCase()
        ? <mark key={i} style={{ background: 'rgba(239,68,68,0.35)', color: '#fff', borderRadius: '2px', padding: '0 2px' }}>{part}</mark>
        : part
    );
  };

  const handleSearch = (query) => {
    if (query.trim() && !searchHistory.includes(query.trim())) {
      const newHistory = [query.trim(), ...searchHistory].slice(0, 10);
      setSearchHistory(newHistory);
      localStorage.setItem('searchHistory', JSON.stringify(newHistory));
    }
  };

  const searchResults = searchQuery.trim() === ''
    ? allItems
    : allItems.filter(item => {
        const query = searchQuery.toLowerCase();
        return (item.headline || '').toLowerCase().includes(query) ||
               (item.summary || '').toLowerCase().includes(query);
      });

  const dateFilteredResults = React.useMemo(() => {
    if (dateRange === 'all') return searchResults;
    const now = new Date();
    const cutoffDays = dateRange === '7days' ? 7 : dateRange === '30days' ? 30 : 0;
    if (cutoffDays === 0) return searchResults;
    const cutoffDate = new Date(now.getTime() - (cutoffDays * 24 * 60 * 60 * 1000));
    return searchResults.filter(item => new Date(item.date) >= cutoffDate);
  }, [searchResults, dateRange]);

  const filteredResults = categoryFilter === 'all'
    ? dateFilteredResults
    : dateFilteredResults.filter(item => item.category === categoryFilter);

  const categoryCounts = allItems.reduce((acc, item) => {
    acc[item.category] = (acc[item.category] || 0) + 1;
    return acc;
  }, {});

  const categoryLabels = {
    all: 'ALL',
    ice: 'ICE',
    epstein: 'EPSTEIN',
    lawsuit: 'LEGAL',
    constitutional: 'CONST.',
    poll: 'POLLS',
    promise: 'PROMISES',
    environment: 'ENV.',
    money: 'MONEY',
    'foreign-policy': 'FOREIGN',
    'healthcare-education': 'HEALTH/ED'
  };

  const MONO = "'Courier New', 'Lucida Console', monospace";

  const suggestions = ['ICE shootings', 'Epstein Trump', 'EPA rollbacks', 'crypto schemes', 'court rulings', 'Iran', 'tariffs', 'Medicaid'];

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto' }}>
      {/* Terminal Header */}
      <div style={{
        borderBottom: '1px solid #1e1e28',
        marginBottom: '24px',
        paddingBottom: '16px',
      }}>
        <div style={{ fontFamily: MONO, fontSize: '10px', color: '#3a3a4a', marginBottom: '8px', letterSpacing: '2px' }}>
          TRUMP ACCOUNTABILITY TRACKER // INTELLIGENCE DATABASE // {allItems.length} RECORDS INDEXED
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
          <h2 style={{ fontFamily: MONO, fontSize: 'clamp(18px, 4vw, 26px)', fontWeight: '700', color: '#ef4444', margin: 0, letterSpacing: '-0.5px' }}>
            SEARCH RECORDS
          </h2>
          <span style={{ fontFamily: MONO, fontSize: '11px', color: '#2a2a35' }}>[ CLASSIFIED DATABASE ]</span>
        </div>
      </div>

      {/* Terminal Search Input */}
      <div style={{
        marginBottom: '20px',
        border: '1px solid #1e1e28',
        borderRadius: '4px',
        background: '#080810',
        padding: '0',
        position: 'relative',
      }}
        onFocus={() => {}}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid #1a1a22',
          padding: '8px 14px',
          gap: '8px',
        }}>
          <span style={{ fontFamily: MONO, fontSize: '10px', color: '#3a3a4a', letterSpacing: '1px', flexShrink: 0 }}>QUERY:</span>
          <div style={{ display: 'flex', gap: '4px' }}>
            {['ICE', 'LEGAL', 'ENV', 'MONEY'].map(t => (
              <span key={t} style={{ fontFamily: MONO, fontSize: '8px', color: '#2a2a35', border: '1px solid #1e1e28', padding: '1px 4px', borderRadius: '2px' }}>{t}</span>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', padding: '14px 14px' }}>
          <span style={{ fontFamily: MONO, fontSize: '16px', color: '#ef4444', marginRight: '10px', flexShrink: 0 }}>{'>'}</span>
          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(searchQuery); }}
            placeholder="SEARCH RECORDS..."
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#e8e8ed',
              fontFamily: MONO,
              fontSize: '15px',
              letterSpacing: '0.5px',
              caretColor: '#ef4444',
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{ background: 'none', border: 'none', color: '#3a3a4a', cursor: 'pointer', fontFamily: MONO, fontSize: '14px', padding: '0 4px' }}
            >✕</button>
          )}
        </div>
      </div>

      {/* Filters Row — compact, single line */}
      <div style={{
        display: 'flex',
        gap: '16px',
        marginBottom: '20px',
        alignItems: 'center',
        flexWrap: 'wrap',
        borderBottom: '1px solid #1a1a22',
        paddingBottom: '16px',
      }}>
        <span style={{ fontFamily: MONO, fontSize: '9px', color: '#3a3a4a', letterSpacing: '1px', flexShrink: 0 }}>FILTER:</span>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { value: 'all', label: 'ALL TIME' },
            { value: '7days', label: '7 DAYS' },
            { value: '30days', label: '30 DAYS' },
          ].map(({ value, label }) => (
            <button key={value} onClick={() => setDateRange(value)} style={{
              padding: '3px 10px',
              background: 'none',
              border: `1px solid ${dateRange === value ? '#ef4444' : '#1e1e28'}`,
              color: dateRange === value ? '#ef4444' : '#3a3a4a',
              fontFamily: MONO, fontSize: '9px', letterSpacing: '1px',
              cursor: 'pointer', borderRadius: '2px',
            }}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', marginLeft: 'auto' }}>
          {[
            { value: 'newest', label: 'NEWEST' },
            { value: 'oldest', label: 'OLDEST' },
          ].map(({ value, label }) => (
            <button key={value} onClick={() => setSortOrder(value)} style={{
              padding: '3px 10px',
              background: sortOrder === value ? 'rgba(239,68,68,0.1)' : 'none',
              border: `1px solid ${sortOrder === value ? '#ef4444' : '#1e1e28'}`,
              color: sortOrder === value ? '#ef4444' : '#3a3a4a',
              fontFamily: MONO, fontSize: '9px', letterSpacing: '1px',
              cursor: 'pointer', borderRadius: '2px',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Category Tags — classified document style */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {Object.entries(categoryLabels).map(([key, label]) => {
          const count = key === 'all' ? allItems.length : (categoryCounts[key] || 0);
          if (count === 0 && key !== 'all') return null;
          const color = key === 'all' ? '#6b6b7b' : (CATEGORY_COLORS[key] || '#ef4444');
          const active = categoryFilter === key;
          return (
            <button key={key} onClick={() => setCategoryFilter(key)} style={{
              padding: '4px 10px',
              background: active ? color + '18' : 'transparent',
              border: `1px solid ${active ? color : '#1e1e28'}`,
              color: active ? color : '#3a3a4a',
              fontFamily: MONO, fontSize: '9px', letterSpacing: '1.5px', fontWeight: '700',
              cursor: 'pointer', borderRadius: '2px',
              transition: 'all 0.15s',
            }}>
              {label}
              <span style={{ marginLeft: '5px', opacity: 0.6, fontSize: '8px' }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Record count */}
      <div style={{ fontFamily: MONO, fontSize: '10px', color: '#2a2a35', marginBottom: '16px', letterSpacing: '1px' }}>
        {searchQuery
          ? `RECORDS FOUND: ${filteredResults.length}${categoryFilter !== 'all' ? ` [${categoryLabels[categoryFilter]}]` : ''}`
          : `SHOWING: ${filteredResults.length} RECORDS`
        }
      </div>

      {/* Suggestions / History when empty */}
      {searchQuery === '' && (
        <div style={{ marginBottom: '20px' }}>
          {searchHistory.length > 0 ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontFamily: MONO, fontSize: '9px', color: '#2a2a35', letterSpacing: '1px' }}>RECENT QUERIES:</span>
                <button onClick={() => { setSearchHistory([]); localStorage.removeItem('searchHistory'); }}
                  style={{ background: 'none', border: 'none', color: '#3a3a4a', fontFamily: MONO, fontSize: '9px', cursor: 'pointer', letterSpacing: '1px' }}>
                  [CLEAR]
                </button>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {searchHistory.slice(0, 5).map((q, i) => (
                  <button key={i} onClick={() => setSearchQuery(q)} style={{
                    padding: '3px 10px', background: 'none',
                    border: '1px solid #1e1e28', color: '#4a4a5a',
                    fontFamily: MONO, fontSize: '9px', letterSpacing: '0.5px',
                    cursor: 'pointer', borderRadius: '2px',
                  }}>{q}</button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontFamily: MONO, fontSize: '9px', color: '#2a2a35', marginBottom: '8px', letterSpacing: '1px' }}>SUGGESTED QUERIES:</div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {suggestions.map(s => (
                  <button key={s} onClick={() => { setSearchQuery(s); handleSearch(s); }} style={{
                    padding: '3px 10px', background: 'none',
                    border: '1px solid #1e1e28', color: '#4a4a5a',
                    fontFamily: MONO, fontSize: '9px', letterSpacing: '0.5px',
                    cursor: 'pointer', borderRadius: '2px',
                  }}>{s}</button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Results */}
      {filteredResults.length === 0 ? (
        <div style={{
          border: '1px solid #1a1a22',
          padding: '40px 24px',
          textAlign: 'center',
          borderRadius: '4px',
          background: '#080810',
        }}>
          <div style={{ fontFamily: MONO, fontSize: '11px', color: '#1e1e28', marginBottom: '12px' }}>
            {'┌─────────────────────────────────┐'}
          </div>
          <div style={{ fontFamily: MONO, fontSize: '12px', color: '#3a3a4a', letterSpacing: '1px' }}>
            SEARCH RETURNED 0 RECORDS
          </div>
          {searchQuery && (
            <div style={{ fontFamily: MONO, fontSize: '10px', color: '#2a2a35', marginTop: '8px' }}>
              NO MATCH FOR: "{searchQuery.toUpperCase()}"
            </div>
          )}
          <div style={{ fontFamily: MONO, fontSize: '11px', color: '#1e1e28', marginTop: '12px' }}>
            {'└─────────────────────────────────┘'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {filteredResults.map((item, idx) => {
            const color = CATEGORY_COLORS[item.category] || '#ef4444';
            const catLabel = categoryLabels[item.category] || (item.category || '').toUpperCase();
            return (
              <div
                key={item.id || idx}
                style={{
                  borderBottom: '1px solid #0f0f18',
                  padding: '16px 0',
                }}
              >
                {/* Meta line */}
                <div style={{
                  fontFamily: MONO,
                  fontSize: '9px',
                  color: '#3a3a4a',
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  flexWrap: 'wrap',
                }}>
                  <span style={{
                    color: color,
                    border: `1px solid ${color}50`,
                    padding: '1px 6px',
                    letterSpacing: '1.5px',
                    fontWeight: '700',
                  }}>[{catLabel}]</span>
                  {item.type === 'record' && (
                    <span style={{ fontSize: '9px', color: '#6b6b7b', opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>TRACKED</span>
                  )}
                  <span style={{ letterSpacing: '0.5px' }}>{item.date}</span>
                  {item.sources && item.sources.length > 0 && (
                    <span style={{ color: '#2a2a35' }}>
                      VIA {(item.sources[0]?.name || '').toUpperCase()}
                    </span>
                  )}
                </div>
                {/* Headline */}
                <div style={{
                  fontSize: '15px',
                  color: '#e8e8ed',
                  fontWeight: '600',
                  lineHeight: 1.45,
                  marginBottom: '8px',
                }}>
                  {highlightMatch(item.headline, searchQuery)}
                </div>
                {/* Summary */}
                {item.summary && (
                  <div style={{
                    fontSize: '13px',
                    color: '#6b6b7b',
                    lineHeight: 1.6,
                    marginBottom: '10px',
                  }}>
                    {highlightMatch(item.summary, searchQuery)}
                  </div>
                )}
                {/* Sources */}
                {item.sources && item.sources.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    {renderSourceLinks(item.sources)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});

// ==================== SOURCE URL MAPPING ====================
// Maps source names to their URLs for clickable links

const SOURCE_URLS = {
  // Major news
  'NPR': 'https://www.npr.org',
  'AP News': 'https://apnews.com',
  'Reuters': 'https://www.reuters.com',
  'Washington Post': 'https://www.washingtonpost.com',
  'New York Times': 'https://www.nytimes.com',
  'NYT': 'https://www.nytimes.com',
  'CNN': 'https://www.cnn.com',
  'CBS News': 'https://www.cbsnews.com',
  'NBC News': 'https://www.nbcnews.com',
  'ABC News': 'https://www.abcnews.go.com',
  'PBS': 'https://www.pbs.org',
  'Al Jazeera': 'https://www.aljazeera.com',
  'Axios': 'https://www.axios.com',
  'Politico': 'https://www.politico.com',
  'The Hill': 'https://thehill.com',
  'BBC': 'https://www.bbc.com',
  
  // Business/Finance
  'Bloomberg': 'https://www.bloomberg.com',
  'CNBC': 'https://www.cnbc.com',
  'Forbes': 'https://www.forbes.com',
  'Fortune': 'https://fortune.com',
  'WSJ': 'https://www.wsj.com',
  'Wall Street Journal': 'https://www.wsj.com',
  'MarketWatch': 'https://www.marketwatch.com',
  'Financial Times': 'https://www.ft.com',
  'Goldman Sachs': 'https://www.goldmansachs.com',
  'Deloitte': 'https://www.deloitte.com',
  
  // Government/Official
  'Treasury Dept': 'https://home.treasury.gov',
  'U.S. Treasury': 'https://fiscaldata.treasury.gov',
  'BLS': 'https://www.bls.gov',
  'EIA': 'https://www.eia.gov',
  'Federal Reserve': 'https://www.federalreserve.gov',
  'GAO': 'https://www.gao.gov',
  'DOJ': 'https://www.justice.gov',
  'DOJ Records': 'https://www.justice.gov',
  'Supreme Court': 'https://www.supremecourt.gov',
  'Federal Court Records': 'https://www.uscourts.gov',
  'Senate Banking Committee': 'https://www.banking.senate.gov',
  
  // Legal/Watchdog
  'ACLU': 'https://www.aclu.org',
  'CREW': 'https://www.citizensforethics.org',
  'Citizens for Ethics': 'https://www.citizensforethics.org',
  'Just Security': 'https://www.justsecurity.org',
  'SCOTUSblog': 'https://www.scotusblog.com',
  'Ballotpedia': 'https://ballotpedia.org',
  'PolitiFact': 'https://www.politifact.com',
  'Vera Institute': 'https://www.vera.org',
  'Brennan Center': 'https://www.brennancenter.org',
  
  // Other
  'Wikipedia': 'https://en.wikipedia.org',
  'Government Executive': 'https://www.govexec.com',
  'Rutgers Law': 'https://law.rutgers.edu',
  'House Oversight': 'https://oversight.house.gov',
  'American Oversight': 'https://www.americanoversight.org',
  'Center for American Progress': 'https://www.americanprogress.org',
  'CAP': 'https://www.americanprogress.org',
  'NAM': 'https://www.nam.org',
  'Newsweek': 'https://www.newsweek.com',
  'Time Magazine': 'https://time.com',
  'HuffPost': 'https://www.huffpost.com',
  'CBC': 'https://www.cbc.ca',
  'Daily Beast': 'https://www.thedailybeast.com',
  'Court Records': 'https://www.uscourts.gov',
  'Committee to Protect Journalists': 'https://cpj.org',
  'PEN America': 'https://pen.org',
  'EDF': 'https://www.edf.org',
  'New Jersey Monitor': 'https://newjerseymonitor.com',
  'The Guardian': 'https://www.theguardian.com/us-news',
  'Protect Democracy': 'https://protectdemocracy.org',
  'NationofChange': 'https://www.nationofchange.org',
  'Senate HSGAC Report': 'https://www.hsgac.senate.gov',
  'CBO': 'https://www.cbo.gov',
  'Brown University': 'https://www.brown.edu',
  'State Dept': 'https://www.state.gov',
  'Quincy Institute': 'https://quincyinst.org',
  'NBC': 'https://www.nbcnews.com',
  'ABC': 'https://abcnews.go.com',
  'CBS': 'https://www.cbsnews.com',
  'Fox News': 'https://www.foxnews.com',
  'Emerson': 'https://emersoncollegepolling.com',
  'Marist': 'https://maristpoll.marist.edu',
  'Civiqs': 'https://civiqs.com',
  'Gallup': 'https://news.gallup.com',
  'Pew Research': 'https://www.pewresearch.org',
  'Morning Consult': 'https://morningconsult.com',
  'YouGov': 'https://today.yougov.com',
  'Quinnipiac': 'https://poll.qu.edu',
  'Monmouth': 'https://www.monmouth.edu/polling-institute/',
  'Silver Bulletin': 'https://www.natesilver.net',
  'MPR News': 'https://www.mprnews.org',
  'Truthout': 'https://truthout.org',
  'CPJ': 'https://cpj.org',
};

// Helper to extract source name from URL
function getSourceNameFromUrl(url) {
  try {
    const hostname = new URL(url).hostname.replace('www.', '');
    const domainMap = {
      'npr.org': 'NPR',
      'apnews.com': 'AP News',
      'reuters.com': 'Reuters',
      'washingtonpost.com': 'Washington Post',
      'nytimes.com': 'NYT',
      'cnn.com': 'CNN',
      'cbsnews.com': 'CBS News',
      'nbcnews.com': 'NBC News',
      'abcnews.go.com': 'ABC News',
      'pbs.org': 'PBS',
      'aljazeera.com': 'Al Jazeera',
      'axios.com': 'Axios',
      'politico.com': 'Politico',
      'bbc.com': 'BBC',
      'bloomberg.com': 'Bloomberg',
      'cnbc.com': 'CNBC',
      'forbes.com': 'Forbes',
      'wsj.com': 'WSJ',
      'aclu.org': 'ACLU',
      'citizensforethics.org': 'CREW',
      'wikipedia.org': 'Wikipedia',
      'en.wikipedia.org': 'Wikipedia',
    };
    return domainMap[hostname] || hostname.split('.')[0].toUpperCase();
  } catch {
    return url;
  }
}

// Helper to render sources as clickable links
function renderSourceLinks(sources, linkStyle = {}) {
  if (!sources || !Array.isArray(sources) || sources.length === 0) return null;
  const defaultStyle = { color: '#6b6b7b', textDecoration: 'underline', fontSize: '11px', ...linkStyle };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
      {sources.filter(s => !isLowQualitySource(s)).map((source, i) => {
        let sourceName, sourceUrl;
        if (typeof source === 'object' && source !== null && source.url) {
          sourceName = source.name || getSourceNameFromUrl(source.url);
          sourceUrl = source.url;
        } else if (typeof source === 'object' && source !== null && source.name) {
          sourceName = source.name;
          sourceUrl = SOURCE_URLS[source.name] || null;
        } else if (typeof source === 'string' && source.startsWith('http')) {
          sourceUrl = source;
          sourceName = getSourceNameFromUrl(source);
        } else if (typeof source === 'string') {
          sourceName = source;
          sourceUrl = SOURCE_URLS[sourceName] || null;
        } else { return null; }
        const hasValidUrl = sourceUrl && sourceUrl.startsWith('http');
        let isDeepLink = false;
        if (hasValidUrl) { try { isDeepLink = new URL(sourceUrl).pathname.length > 2; } catch {} }
        return (
          <React.Fragment key={i}>
            {i > 0 && <span style={{ color: '#3a3a4a', margin: '0 4px' }}>•</span>}
            {hasValidUrl ? (
              <a href={sourceUrl} target="_blank" rel="noopener noreferrer" style={defaultStyle}>
                {sourceName}{isDeepLink ? ' ↗' : ''}
              </a>
            ) : (
              <span style={{ color: '#4a4a5a', fontSize: '11px' }}>{sourceName}</span>
            )}
          </React.Fragment>
        );
      }).filter(Boolean)}
    </div>
  );
}

// ==================== SHARE CARD GENERATOR ====================
// Generates branded image cards for social sharing

async function generateShareImage(title, subtitle, stat, source, category) {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 630;
  const ctx = canvas.getContext('2d');
  
  const colors = {
    ice: '#dc2626', epstein: '#f59e0b', lawsuit: '#a855f7',
    constitutional: '#3b82f6', poll: '#22c55e', promise: '#f97316',
    debt: '#ef4444', wealth: '#22c55e', default: '#ef4444'
  };
  const accent = colors[category] || colors.default;
  
  // Background
  ctx.fillStyle = '#0a0a0f';
  ctx.fillRect(0, 0, 1200, 630);
  
  // Accent stripe
  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, 6, 630);
  
  // Subtle gradient overlay
  const grad = ctx.createLinearGradient(0, 0, 1200, 630);
  grad.addColorStop(0, accent + '15');
  grad.addColorStop(1, 'transparent');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 1200, 630);
  
  // Category tag
  ctx.fillStyle = accent + '30';
  const tagText = (category || 'fact').toUpperCase();
  ctx.font = 'bold 14px -apple-system, BlinkMacSystemFont, sans-serif';
  const tagWidth = ctx.measureText(tagText).width + 24;
  ctx.beginPath();
  ctx.roundRect(48, 48, tagWidth, 28, 4);
  ctx.fill();
  ctx.fillStyle = accent;
  ctx.fillText(tagText, 60, 67);
  
  // Main stat (if provided)
  if (stat) {
    ctx.font = 'bold 72px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = accent;
    ctx.fillText(stat, 48, 170);
  }
  
  // Title
  ctx.font = 'bold 36px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = '#ffffff';
  const titleY = stat ? 230 : 130;
  const words = title.split(' ');
  let line = '';
  let y = titleY;
  for (const word of words) {
    const test = line + word + ' ';
    if (ctx.measureText(test).width > 1050) {
      ctx.fillText(line.trim(), 48, y);
      line = word + ' ';
      y += 48;
    } else {
      line = test;
    }
  }
  ctx.fillText(line.trim(), 48, y);
  
  // Subtitle
  if (subtitle) {
    ctx.font = '22px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = '#a8a8b8';
    const subWords = subtitle.split(' ');
    let subLine = '';
    let subY = y + 50;
    for (const word of subWords) {
      const test = subLine + word + ' ';
      if (ctx.measureText(test).width > 1050) {
        ctx.fillText(subLine.trim(), 48, subY);
        subLine = word + ' ';
        subY += 32;
      } else {
        subLine = test;
      }
    }
    ctx.fillText(subLine.trim(), 48, subY);
  }
  
  // Footer
  ctx.fillStyle = '#1e1e28';
  ctx.fillRect(0, 570, 1200, 60);
  ctx.font = 'bold 16px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = '#6b6b7b';
  ctx.fillText('Trump Accountability Tracker', 48, 607);
  if (source) {
    ctx.font = '14px -apple-system, BlinkMacSystemFont, sans-serif';
    ctx.fillStyle = '#4a4a5a';
    ctx.fillText('Source: ' + source, 48, 555);
  }
  ctx.font = '14px -apple-system, BlinkMacSystemFont, sans-serif';
  ctx.fillStyle = '#4a4a5a';
  const dateStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const dateWidth = ctx.measureText(dateStr).width;
  ctx.fillText(dateStr, 1152 - dateWidth, 607);
  
  return canvas.toDataURL('image/png');
}

function buildShareText(title, stat, source, category) {
  const url = window.location.origin;
  const text = (stat ? stat + ' — ' : '') + title;
  return text + '\n\n' + url + (source ? '\nSource: ' + source : '') + '\n\nTrump Accountability Tracker — Every claim sourced.';
}

async function shareContent(title, subtitle, stat, source, category, mode = 'default') {
  const catTab = { ice: 'ice', epstein: 'epstein', lawsuit: 'lawsuits', constitutional: 'constitution', poll: 'polls', promise: 'promises', environment: 'environment', money: 'money', 'foreign-policy': 'foreign-policy', 'healthcare-education': 'healthcare' };
  const tabId = catTab[category] || 'overview';
  const siteUrl = window.location.origin + '/#' + tabId;
  const shareText = buildShareText(title, stat, source, category);

  if (mode === 'bluesky') {
    const bskyText = encodeURIComponent((stat ? stat + ' — ' : '') + title + '\n\n' + siteUrl);
    window.open('https://bsky.app/intent/compose?text=' + bskyText, '_blank');
    return;
  }

  if (mode === 'email') {
    const subject = encodeURIComponent((stat ? stat + ' — ' : '') + title);
    const body = encodeURIComponent(shareText);
    window.open('mailto:?subject=' + subject + '&body=' + body);
    return;
  }

  if (mode === 'link') {
    await navigator.clipboard?.writeText(siteUrl);
    const toast = document.createElement('div');
    toast.textContent = '\u2713 Link copied';
    toast.style.cssText = 'position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#3b82f6;color:#fff;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;z-index:9999;animation:fadeOut 2s forwards';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2000);
    return;
  }

  if (mode === 'copy') {
    await navigator.clipboard?.writeText(shareText);
    const toast = document.createElement('div');
    toast.textContent = '\u2713 Copied to clipboard';
    toast.style.cssText = 'position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#22c55e;color:#000;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;z-index:9999;animation:fadeOut 2s forwards';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2000);
    return;
  }

  try {
    const imageData = await generateShareImage(title, subtitle, stat, source, category);

    // Try Web Share API first (mobile)
    if (navigator.share) {
      await navigator.share({
        title: title,
        text: (stat ? stat + ' — ' : '') + title + (source ? '\nSource: ' + source : ''),
        url: siteUrl
      });
    } else {
      // Fallback: download the image
      const link = document.createElement('a');
      link.download = 'trump-tracker-' + (category || 'fact') + '.png';
      link.href = imageData;
      link.click();
    }
  } catch (err) {
    console.error('Share failed:', err);
    await navigator.clipboard?.writeText(shareText);
  }
}

// Share button component with share options
const ShareButton = React.memo(function ShareButton({ title, subtitle, stat, source, category, style }) {
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    if (!showMenu) return;
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [showMenu]);

  return (
    <div ref={menuRef} style={{ position: 'relative', display: 'inline-block', ...style }}>
      <button
        onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
        style={{
          background: 'rgba(59,130,246,0.1)',
          border: '1px solid rgba(59,130,246,0.25)',
          borderRadius: '6px',
          padding: '6px 12px',
          color: '#3b82f6',
          fontSize: '11px',
          fontWeight: '600',
          cursor: 'pointer',
          fontFamily: 'inherit',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          transition: 'all 0.2s',
        }}
        onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(59,130,246,0.2)'; }}
        onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(59,130,246,0.1)'; }}
      >
        ↗ Share
      </button>
      {showMenu && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: '4px', zIndex: 100,
          background: '#1a1a22', border: '1px solid #2a2a35', borderRadius: '8px',
          padding: '4px', minWidth: '160px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
        }}>
          {[
            { label: '🦋 Bluesky', mode: 'bluesky' },
            { label: '📋 Copy Text', mode: 'copy' },
            { label: '📷 Save Image', mode: 'default' },
          ].map(opt => (
            <button key={opt.mode}
              onClick={(e) => { e.stopPropagation(); setShowMenu(false); shareContent(title, subtitle, stat, source, category, opt.mode); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                background: 'transparent', border: 'none', color: '#d4d4dc', fontSize: '12px',
                cursor: 'pointer', fontFamily: 'inherit', borderRadius: '4px',
              }}
              onMouseOver={(e) => { e.currentTarget.style.background = '#2a2a35'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >{opt.label}</button>
          ))}
        </div>
      )}
    </div>
  );
});

// ==================== RETRY LOGIC (Priority Action #4) ====================
// Exponential backoff for failed API requests

async function fetchWithRetry(fetchFn, maxAttempts = 3, baseDelay = 1000) {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const result = await fetchFn();
      return result;
    } catch (error) {
      console.error(`Attempt ${attempt} failed:`, error.message);
      if (attempt === maxAttempts) {
        throw error;
      }
      // Exponential backoff: 1s, 2s, 4s
      const delay = baseDelay * Math.pow(2, attempt - 1);
      console.log(`Retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
}

// ==================== ISOLATED LIVE COUNTERS ====================
// These components manage their own state to prevent full app re-renders
// This fixes copy/paste issues caused by 1-second updates

// "While You've Been Here" sticky counter — isolated to prevent full app re-renders
const LiveImpactBar = React.memo(function LiveImpactBar({ debtPerSecond }) {
  const [counters, setCounters] = useState({ debt: 0, gain: 0 });
  const openTime = useRef(Date.now());

  useEffect(() => {
    const rate = debtPerSecond || 92912.33;
    const gainRate = 123.07; // $4.2B over ~13 months (~34.1M seconds)
    const interval = setInterval(() => {
      const elapsed = (Date.now() - openTime.current) / 1000;
      setCounters({ debt: elapsed * rate, gain: elapsed * gainRate });
    }, 1000);
    return () => clearInterval(interval);
  }, [debtPerSecond]);

  if (counters.debt <= 0) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 999,
      background: 'linear-gradient(180deg, rgba(10,10,15,0) 0%, rgba(10,10,15,0.95) 30%, #0a0a0f 100%)',
      padding: '20px 16px 12px', textAlign: 'center',
      borderTop: '1px solid #1a1a22',
    }}>
      <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '4px' }}>SINCE YOU OPENED THIS PAGE</div>
      <div style={{ display: 'inline-flex', gap: '24px', fontSize: '13px' }}>
        <span>
          <span style={{ color: '#ef4444', fontWeight: '700', fontFamily: 'JetBrains Mono, monospace' }}>
            +${counters.debt >= 1e6 ? (counters.debt / 1e6).toFixed(1) + 'M' : Math.round(counters.debt).toLocaleString()}
          </span>
          <span style={{ color: '#4a4a5a', marginLeft: '4px' }}>debt added</span>
        </span>
        <span style={{ color: '#2a2a35' }}>|</span>
        <span>
          <span style={{ color: '#22c55e', fontWeight: '700', fontFamily: 'JetBrains Mono, monospace' }}>
            +${Math.round(counters.gain).toLocaleString()}
          </span>
          <span style={{ color: '#4a4a5a', marginLeft: '4px' }}>Trump gained</span>
        </span>
      </div>
    </div>
  );
});

// Wrapper components that isolate re-renders
const DebtDisplay = React.memo(function DebtDisplay({ debt }) {
  const [now, setNow] = useState(Date.now());
  
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);
  
  const baselineDate = new Date(debt?.baselineDate || '2026-01-07T00:00:00Z');
  const liveDebt = ((debt?.baseline || 38.43) * 1e12) + (((now - baselineDate.getTime()) / 1000) * (debt?.perSecond || 92912.33));
  const debtSinceInauguration = liveDebt - ((debt?.atInauguration || 36.18) * 1e12);
  
  return (
    <>
      <div style={{ fontSize: 'clamp(28px, 5vw, 36px)', fontWeight: '700', color: '#ef4444', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '-1px' }}>
        ${(liveDebt / 1e12).toFixed(6)}T
      </div>
      <div style={{ fontSize: '12px', color: '#6b6b7b', margin: '8px 0 16px' }}>+${Math.round(debt?.perSecond || 92912.33).toLocaleString()}/second</div>
      <div style={{ padding: '14px 16px', background: 'rgba(239,68,68,0.08)', borderRadius: '10px', border: '1px solid rgba(239,68,68,0.15)' }}>
        <div style={{ fontSize: '11px', color: '#8a8a9a', marginBottom: '4px' }}>Added This Term</div>
        <div style={{ fontSize: '24px', fontWeight: '700', color: '#ef4444', fontFamily: 'JetBrains Mono, monospace' }}>+${(debtSinceInauguration / 1e12).toFixed(6)}T</div>
      </div>
    </>
  );
});

const TermTimer = React.memo(function TermTimer() {
  const [now, setNow] = useState(Date.now());
  const INAUGURATION = new Date('2025-01-20T17:00:00Z');
  
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);
  
  const sec = Math.floor((now - INAUGURATION.getTime()) / 1000);
  const days = Math.floor(sec / 86400);
  const hours = String(Math.floor((sec % 86400) / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((sec % 3600) / 60)).padStart(2, '0');
  const seconds = String(sec % 60).padStart(2, '0');
  
  return (
    <span style={{ fontSize: 'clamp(16px, 4vw, 22px)', fontWeight: '700', fontFamily: 'JetBrains Mono, monospace', color: '#fff' }}>
      {days}<span style={{ color: '#4a4a5a' }}>d</span> {hours}<span style={{ color: '#4a4a5a' }}>h</span> {minutes}<span style={{ color: '#4a4a5a' }}>m</span> {seconds}<span style={{ color: '#4a4a5a' }}>s</span>
    </span>
  );
});


// ==================== REP LOOKUP COMPONENT ====================
// Looks up representatives by zip code using Google Civic Information API
// Falls back to manual links if API unavailable

// Module-level cache — survives component remounts caused by parent re-renders
const _repCache = { zip: '', reps: null };

const RepLookup = React.memo(function RepLookup({ breakingNews }) {
  const [zip, setZip] = React.useState(_repCache.zip);
  const [reps, setReps] = React.useState(_repCache.reps);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [selectedIssue, setSelectedIssue] = React.useState(null);
  
  // Get latest breaking news for call scripts
  const latestIssues = React.useMemo(() => {
    const bn = (breakingNews || []).sort((a,b) => new Date(b.date)-new Date(a.date)).slice(0, 5);
    return bn.map(item => ({
      id: item.id,
      title: item.headline,
      category: item.category,
      script: generateCallScript(item)
    }));
  }, [breakingNews]);
  
  const lookupReps = async () => {
    if (!zip || zip.length < 5) return;
    setLoading(true);
    setError(null);
    try {
      // @unitedstates project data via our proxy — always current, no API key
      const res = await fetch('/api/reps?zip=' + encodeURIComponent(zip));
      if (!res.ok) throw new Error('Could not find representatives');
      const data = await res.json();
      
      const officials = (data.results || []).map(rep => ({
        name: rep.name || '',
        office: rep.title === 'Senator' ? 'U.S. Senator — ' + (rep.state || '') : 'U.S. Representative — District ' + (rep.district || 'At-Large'),
        party: rep.party || '',
        phone: rep.phone || '',
        website: rep.website || '',
        contact_form: rep.contact_form || '',
        _districtMatch: data.districtMatch || 'state',
      }));
      if (officials.length === 0) throw new Error('No representatives found');
      _repCache.zip = zip;
      _repCache.reps = officials;
      setReps(officials);
    } catch (err) {
      // Fallback: try proxy again with error detail
      try {
        setError('Could not find representatives for this zip code. Try the manual links below.');
        setReps(null);
      } catch {
        setError('Could not look up representatives. Try the manual links below.');
        setReps(null);
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <>
      {/* Zip Code Lookup */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="postal-code"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
            onKeyDown={(e) => e.key === 'Enter' && lookupReps()}
            placeholder="Enter your zip code"
            style={{
              flex: 1, padding: '14px 18px', background: '#13131a', border: '1px solid #2a2a35',
              borderRadius: '10px', color: '#fff', fontSize: '16px', fontFamily: 'inherit',
              outline: 'none'
            }}
          />
          <button
            onClick={lookupReps}
            disabled={loading || zip.length < 5}
            style={{
              padding: '14px 24px', background: zip.length >= 5 ? '#3b82f6' : '#1a1a22',
              border: 'none', borderRadius: '10px', color: '#fff', fontSize: '14px',
              fontWeight: '600', cursor: zip.length >= 5 ? 'pointer' : 'default',
              fontFamily: 'inherit', opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? <><span style={{ display: 'inline-block', width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.6s linear infinite', marginRight: '8px', verticalAlign: 'middle' }} /> Looking up...</> : 'Find Reps'}
          </button>
        </div>
        {error && <div style={{ fontSize: '12px', color: '#ef4444', marginBottom: '12px' }}>{error}</div>}
      </div>
      
      {/* Results */}
      {reps && reps.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '12px', color: '#6b6b7b', marginBottom: '8px', fontWeight: '600' }}>YOUR REPRESENTATIVES</div>
          {reps[0]?._districtMatch === 'exact' ? (
            <div style={{ fontSize: '11px', color: '#22c55e', marginBottom: '12px', lineHeight: 1.5 }}>
              District match found — showing your specific representative and senators.
            </div>
          ) : (
            <div style={{ fontSize: '11px', color: '#4a4a5a', marginBottom: '12px', lineHeight: 1.5 }}>
              ⓘ Your 2 senators are always correct. Could not determine exact district — showing all state representatives.
            </div>
          )}
          <div style={{ display: 'grid', gap: '10px' }}>
            {reps.map((rep, i) => {
              const isSenator = rep.office.startsWith('U.S. Senator');
              const accentColor = isSenator ? '#d97706' : '#0891b2';
              return (
              <div key={i} style={{
                padding: '16px', background: '#13131a', borderRadius: '12px',
                border: '1px solid #1e1e28', borderLeft: `3px solid ${accentColor}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <span style={{
                      fontSize: '9px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px',
                      background: accentColor + '20', color: accentColor, letterSpacing: '0.5px'
                    }}>{isSenator ? 'SENATE' : 'HOUSE'}</span>
                    <div style={{ fontSize: '15px', color: '#fff', fontWeight: '600', marginTop: '4px' }}>{rep.name}</div>
                    <div style={{ fontSize: '12px', color: accentColor, marginTop: '2px', fontWeight: '500' }}>{rep.office}</div>
                    {rep.party && <div style={{ fontSize: '11px', color: rep.party.includes('Democrat') ? '#3b82f6' : rep.party.includes('Republican') ? '#ef4444' : '#6b6b7b', marginTop: '2px' }}>{rep.party}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {rep.phone && (
                      <a href={'tel:' + rep.phone} style={{
                        padding: '8px 14px', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)',
                        borderRadius: '8px', color: '#22c55e', fontSize: '12px', fontWeight: '600', textDecoration: 'none'
                      }}>📞 Call</a>
                    )}
                    {rep.website && (
                      <a href={rep.website} target="_blank" rel="noopener noreferrer" style={{
                        padding: '8px 14px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)',
                        borderRadius: '8px', color: '#3b82f6', fontSize: '12px', fontWeight: '600', textDecoration: 'none'
                      }}>Website</a>
                    )}
                  </div>
                </div>
                {rep.phone && <div style={{ fontSize: '12px', color: '#4a4a5a', marginTop: '8px', fontFamily: 'JetBrains Mono, monospace' }}>{rep.phone}</div>}
              </div>
            )})}
          </div>
        </div>
      )}
      
      {/* Issue-specific call scripts */}
      {reps && reps.length > 0 && latestIssues.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '12px', color: '#6b6b7b', marginBottom: '12px', fontWeight: '600' }}>CALL SCRIPTS — TAP AN ISSUE</div>
          <div style={{ display: 'grid', gap: '8px' }}>
            {latestIssues.map((issue, i) => {
              const colors = { ice: '#dc2626', epstein: '#f59e0b', lawsuit: '#a855f7', constitutional: '#3b82f6', poll: '#22c55e', promise: '#f97316', 'foreign-policy': '#6366f1', 'healthcare-education': '#14b8a6' };
              const c = colors[issue.category] || '#ef4444';
              const isOpen = selectedIssue === issue.id;
              return (
                <div key={i}>
                  <button
                    onClick={() => setSelectedIssue(isOpen ? null : issue.id)}
                    style={{
                      width: '100%', textAlign: 'left', padding: '14px 16px',
                      background: isOpen ? c + '15' : '#13131a',
                      border: '1px solid ' + (isOpen ? c + '40' : '#1e1e28'),
                      borderRadius: isOpen ? '12px 12px 0 0' : '12px',
                      color: '#e8e8ed', fontSize: '13px', fontWeight: '600',
                      cursor: 'pointer', fontFamily: 'inherit'
                    }}
                  >
                    <span style={{ color: c, fontSize: '10px', textTransform: 'uppercase', marginRight: '8px' }}>{issue.category}</span>
                    {issue.title}
                    <span style={{ float: 'right', color: '#4a4a5a' }}>{isOpen ? '▲' : '▼'}</span>
                  </button>
                  {isOpen && (
                    <div style={{
                      padding: '16px', background: '#0a0a0f', borderRadius: '0 0 12px 12px',
                      border: '1px solid ' + c + '40', borderTop: 'none'
                    }}>
                      <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '8px', fontWeight: '600' }}>SUGGESTED CALL SCRIPT</div>
                      <div style={{ fontSize: '13px', color: '#d4d4dc', lineHeight: 1.7, whiteSpace: 'pre-line' }}>{issue.script}</div>
                      <button
                        onClick={() => navigator.clipboard?.writeText(issue.script)}
                        style={{
                          marginTop: '12px', padding: '8px 16px', background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px',
                          color: '#6b6b7b', fontSize: '11px', cursor: 'pointer', fontFamily: 'inherit'
                        }}
                      >
                        📋 Copy Script
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
});

function generateCallScript(newsItem) {
  const base = 'Hi, my name is [YOUR NAME] and I\'m a constituent calling from [YOUR CITY]. ';
  const close = ' I\'d appreciate knowing where the office stands on this. Thank you for your time.';
  
  const scripts = {
    ice: base + 'I\'m calling about a recent incident involving federal immigration agents: "' + newsItem.headline + '." I\'m deeply concerned about the use of force by ICE and Border Patrol, and I urge support for oversight hearings and accountability measures for federal enforcement actions.' + close,
    constitutional: base + 'I\'m calling because of a serious constitutional concern: "' + newsItem.headline + '." The rule of law must apply to everyone, including the executive branch. I urge defending congressional authority and supporting judicial independence.' + close,
    lawsuit: base + 'I\'m calling regarding: "' + newsItem.headline + '." I believe in accountability and the rule of law, and I urge proper congressional oversight of the administration\'s legal actions.' + close,
    epstein: base + 'I\'m calling about the Epstein files: "' + newsItem.headline + '." Full transparency matters. I urge pushing for complete release of all documents and a proper investigation into any misconduct.' + close,
    poll: base + 'I\'m calling because public opinion shows growing concern about the administration\'s direction. As a constituent, I want to make sure my representative is listening and acting in the interest of the people they serve.' + close,
    promise: base + 'I\'m calling about a broken campaign promise: "' + newsItem.headline + '." Elected officials should be held to their word, and I urge holding the administration accountable for commitments made to voters.' + close,
    environment: base + 'I\'m calling about an environmental concern: "' + newsItem.headline + '." Climate change and environmental protection are critical for our future. I urge supporting legislation to reverse harmful environmental rollbacks and protect our public lands and clean air/water.' + close,
    money: base + 'I\'m calling about a corruption concern: "' + newsItem.headline + '." The President should not be profiting from the office. I urge supporting investigations into conflicts of interest and holding the administration accountable for self-dealing.' + close,
    'foreign-policy': base + 'I\'m calling about a foreign policy concern: "' + newsItem.headline + '." America\'s standing in the world matters, and I urge the office to prioritize diplomacy, alliances, and responsible international engagement.' + close,
    'healthcare-education': base + 'I\'m calling about a healthcare and education concern: "' + newsItem.headline + '." Access to quality healthcare and education is essential. I urge the office to oppose cuts and protect these vital public services.' + close,
  };
  
  return scripts[newsItem.category] || base + 'I\'m calling about: "' + newsItem.headline + '." I urge oversight and accountability on this matter.' + close;
}

function App() {
  const [data, setData] = useState(INITIAL_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [selectedWidget, setSelectedWidget] = useState('debt');
  // showMoreOverview removed — all sections visible for maximum impact
  const [selectedTheme, setSelectedTheme] = useState('dark');
  const [activeTab, setActiveTab] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    const validTabs = ['overview', 'search', 'constitution', 'lawsuits', 'money', 'environment', 'ice', 'epstein', 'polls', 'promises', 'action', 'foreign-policy', 'healthcare'];
    if (hash && !validTabs.includes(hash)) {
      window.location.hash = '';
      return 'overview';
    }
    return hash || 'overview';
  });
  const [scrollToId, setScrollToId] = useState(null);
  const [rotatorIdx, setRotatorIdx] = useState(0);
  const [rotatorFade, setRotatorFade] = useState(true);
  const rotatorTouchRef = useRef(null);
  
  useEffect(() => {
    const onHash = () => {
      const hash = window.location.hash.replace('#', '');
      setActiveTab(hash || 'overview');
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // Scroll to specific element after tab switch
  useEffect(() => {
    if (!scrollToId) return;
    // Small delay to let the new tab render
    const timer = setTimeout(() => {
      const el = document.getElementById(scrollToId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Brief highlight flash
        el.style.outline = '2px solid rgba(239,68,68,0.6)';
        el.style.outlineOffset = '4px';
        el.style.borderRadius = '12px';
        setTimeout(() => { el.style.outline = 'none'; }, 2000);
      }
      setScrollToId(null);
    }, 100);
    return () => clearTimeout(timer);
  }, [scrollToId, activeTab]);

  // Keyboard navigation: Ctrl+1 through Ctrl+9 for quick tab switching
  useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key >= '1' && e.key <= '9') {
        e.preventDefault();
        const tabIndex = parseInt(e.key) - 1;
        const tabs = ['overview', 'search', 'constitution', 'lawsuits', 'money', 'environment', 'ice', 'epstein', 'polls', 'foreign-policy', 'healthcare'];
        if (tabs[tabIndex]) {
          setActiveTab(tabs[tabIndex]);
          window.location.hash = tabs[tabIndex] === 'overview' ? '' : tabs[tabIndex];
        }
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  useEffect(() => {
  // 1. Initial Load: Get the latest data from the log table
  const fetchInitialData = async () => {
    const { data: latestLog, error } = await supabase
      .from('update_logs')
      .select('raw, timestamp')
      .order('timestamp', { ascending: false })
      .limit(1)
      .single();

    if (!error && latestLog?.raw) {
      try {
        const rawData = typeof latestLog.raw === 'string'
          ? JSON.parse(latestLog.raw)
          : latestLog.raw;
        setData(mergeWithBaseline(rawData));
        setLastSync(latestLog.timestamp);
        // This branch renders the page, so it must clear the loading skeleton.
        // Only fetchData() ever did that, and until update_logs became readable
        // this branch never ran — leaving the skeleton up forever.
        setLoading(false);
      } catch (parseErr) {
        console.error('Failed to parse log data:', parseErr);
        fetchData();
        return;
      }
    } else {
      // Fallback to main tracker if logs are empty/fail
      fetchData(); 
    }
  };

  // A thrown error (network failure) would otherwise leave loading true and
  // the skeleton up permanently, since nothing else clears it.
  fetchInitialData().catch((err) => {
    console.error('Initial log fetch failed:', err);
    fetchData();
  });

  // 2. Realtime Listener: Watch for NEW rows in update_logs
  let channel = supabase.channel('realtime-scraper-logs')
    .on('postgres_changes',
      {
        event: 'INSERT', // Listening for NEW logs being created
        schema: 'public',
        table: 'update_logs'
      },
      (payload) => {
        console.log("🚀 Scraper Finished! New Data Received:", payload.new.timestamp);

        // Extract the full JSON payload from the 'raw' column
        try {
          const freshData = typeof payload.new.raw === 'string'
            ? JSON.parse(payload.new.raw)
            : payload.new.raw;

          if (freshData) {
            const merged = mergeWithBaseline(freshData);
            setData(merged);
            setLastSync(new Date().toISOString());
          }
        } catch (parseErr) {
          console.error('Failed to parse realtime data:', parseErr);
        }
      }
    )
    .subscribe((status) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.warn('Realtime channel issue:', status, '— will retry in 10s');
        setTimeout(() => {
          supabase.removeChannel(channel);
          channel = supabase.channel('realtime-scraper-logs')
            .on('postgres_changes',
              { event: 'INSERT', schema: 'public', table: 'update_logs' },
              (payload) => {
                try {
                  const freshData = typeof payload.new.raw === 'string'
                    ? JSON.parse(payload.new.raw) : payload.new.raw;
                  if (freshData) {
                    setData(mergeWithBaseline(freshData));
                    setLastSync(new Date().toISOString());
                  }
                } catch (e) { console.error('Realtime parse error:', e); }
              }
            )
            .subscribe();
        }, 10000);
      }
    });

  return () => {
    supabase.removeChannel(channel);
  };
}, []);

// Auto-refresh on new deploys: poll index.html for new JS bundle hash
useEffect(() => {
  let currentScripts = null;
  const check = async () => {
    try {
      const res = await fetch('/?_v=' + Date.now(), { cache: 'no-store' });
      const html = await res.text();
      const scripts = (html.match(/static\/js\/main\.[a-f0-9]+\.js/g) || []).join();
      if (!currentScripts) { currentScripts = scripts; return; }
      if (scripts && scripts !== currentScripts) {
        window.location.reload();
      }
    } catch {}
  };
  const interval = setInterval(check, 5 * 60 * 1000); // check every 5 min
  check();
  return () => clearInterval(interval);
}, []);

  // Merge Supabase data with baseline - ensures brokenPromises & constitutionalConcerns
  // always use the latest hardcoded baseline from INITIAL_DATA
  // Replace the existing mergeWithBaseline in App.js with this:
  function mergeWithBaseline(supabaseData) {
    // 1. Start with your curated INITIAL_DATA as the master template
    const merged = { ...INITIAL_DATA };

    // 2. Define the ONLY keys the automated script is allowed to update
    const dynamicKeys = [
      'iceVictims', 
      'lawsuits', 
      'polls', 
      'epsteinFiles', 
      'iceStats',
      'wealth',
      'constitutionalConcerns',
      'environmentStats'
    ];

    // 3. Only overwrite if the Supabase data actually exists and isn't empty
    dynamicKeys.forEach(key => {
      const newVal = supabaseData[key];
      const isArray = Array.isArray(newVal);
      
      if (newVal && (isArray ? newVal.length > 0 : true)) {
        merged[key] = newVal;
      }
    });

    // 4. brokenPromises always curated
    merged.brokenPromises = INITIAL_DATA.brokenPromises;
    // 5. Merge breaking news from DB + baseline
    const dbBN = Array.isArray(supabaseData.breakingNews) ? supabaseData.breakingNews : [];
    const baseBN = Array.isArray(INITIAL_DATA.breakingNews) ? INITIAL_DATA.breakingNews : [];
    const seenIds = new Set();
    const uniqBN = [];
    const now = new Date();
    for (const item of [...dbBN, ...baseBN]) {
      if (!item || !item.headline) continue;
      if (item.date && new Date(item.date) > now) continue;
      if (item.id && seenIds.has(item.id)) continue;
      if (uniqBN.some(e => headlinesSimilar(e.headline, item.headline))) continue;
      if (isLowQualitySource(item.sources?.[0])) continue;
      if (item.id) seenIds.add(item.id);
      uniqBN.push(item);
    }
    // Keep ALL items permanently — CategoryNews handles UI pagination
    merged.breakingNews = uniqBN.sort((a,b) => new Date(b.date)-new Date(a.date));
    return merged;
  }

  async function fetchData() {
    setError(null);
    try {
      // Use retry logic for fetching
      const result = await fetchWithRetry(async () => {
        const { data: result, error } = await supabase
          .from('tracker_data')
          .select('data, updated_at')
          .eq('id', 'main')
          .single();

        if (error) throw error;



        return result;
      });
      
      if (result?.data && Object.keys(result.data).length > 0) {
  const mergedData = mergeWithBaseline(result.data);
  setData(mergedData);
  setLastSync(result.updated_at);
}
    } catch (err) {
      console.error('Error fetching data after retries:', err);
      setError(err.message);
      // Still show the page with initial/cached data
    } finally {
      setLoading(false);
    }
  }

  // These values are used outside the live counters (for static displays)
  const staticDebt = useMemo(() => {
    const baselineDate = new Date(data.debt?.baselineDate || '2026-01-07T00:00:00Z');
    const now = Date.now();
    const liveDebt = ((data.debt?.baseline || 38.43) * 1e12) + (((now - baselineDate.getTime()) / 1000) * (data.debt?.perSecond || 92912.33));
    return {
      liveDebt,
      debtSinceInauguration: liveDebt - ((data.debt?.atInauguration || 36.18) * 1e12)
    };
  }, [data.debt]);
  
  const wealthGain = (data.wealth?.current || 6.5) - (data.wealth?.previous || 2.3);
  const wealthGainPercent = Math.round((wealthGain / (data.wealth?.previous || 2.3)) * 100);

  const copyFact = (text) => {
    navigator.clipboard?.writeText(text + '\n\nSource: Trump Accountability Tracker\n' + window.location.origin);
    const toast = document.createElement('div');
    toast.textContent = '\u2713 Fact copied — paste and share!';
    toast.style.cssText = 'position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#22c55e;color:#000;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;z-index:9999;animation:fadeOut 2s forwards';
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 2000);
  };

  const fmt = (val, dec = 2) => {
    if (val >= 1e12) return `$${(val / 1e12).toFixed(dec)}T`;
    if (val >= 1e9) return `$${(val / 1e9).toFixed(dec)}B`;
    if (val >= 1e6) return `$${(val / 1e6).toFixed(dec)}M`;
    if (val >= 1e3) return `$${(val / 1e3).toFixed(dec)}K`;
    return `$${val.toLocaleString()}`;
  };
  const iceVictims = data.iceVictims || [];
  const iceStats = data.iceStats || {};
  const brokenPromises = data.brokenPromises || [];
  const constitutionalConcerns = data.new_constitutional || data.constitutionalConcerns || [];
  const wealth = data.wealth || {};

  // Build rotator items: broken promises, shuffled
  const rotatorItems = useMemo(() => {
    const items = [];
    brokenPromises.filter(p => p.status === 'BROKEN' || p.status === 'MOSTLY BROKEN').forEach(p => {
      items.push({
        type: 'promise',
        label: 'PROMISE vs REALITY',
        color: '#ef4444',
        title: `"${(p.quote || '').slice(0, 100)}${(p.quote || '').length > 100 ? '...' : ''}"`,
        detail: Array.isArray(p.reality) ? p.reality[0] : p.reality,
        tab: 'promises',
        linkText: 'View all broken promises →',
      });
    });
    // Shuffle using Fisher-Yates
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  }, [brokenPromises]);

  // Auto-cycle rotator every 7 seconds
  useEffect(() => {
    if (rotatorItems.length < 2) return;
    const interval = setInterval(() => {
      setRotatorFade(false);
      setTimeout(() => {
        setRotatorIdx(prev => (prev + 1) % rotatorItems.length);
        setRotatorFade(true);
      }, 400);
    }, 7000);
    return () => clearInterval(interval);
  }, [rotatorItems.length]);

  // Swipe handler for rotator
  const handleRotatorSwipe = (direction) => {
    setRotatorFade(false);
    setTimeout(() => {
      setRotatorIdx(prev => {
        if (direction === 'left') return (prev + 1) % rotatorItems.length;
        return (prev - 1 + rotatorItems.length) % rotatorItems.length;
      });
      setRotatorFade(true);
    }, 200);
  };




  const handleTabClick = (tabId, { scrollToTop = true, scrollTo = null } = {}) => {
    setActiveTab(tabId);
    window.location.hash = tabId === 'overview' ? '' : tabId;
    if (scrollTo) {
      setScrollToId(scrollTo);
    } else if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 20px' }}>
        {/* Skeleton Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div style={{ width: '120px', height: '24px', background: '#1e1e28', borderRadius: '20px', margin: '0 auto 16px', animation: 'pulse 1.5s ease-in-out infinite' }} />
          <div style={{ width: '300px', height: '36px', background: '#1e1e28', borderRadius: '8px', margin: '0 auto 12px', animation: 'pulse 1.5s ease-in-out infinite' }} />
          <div style={{ width: '200px', height: '16px', background: '#1e1e28', borderRadius: '4px', margin: '0 auto', animation: 'pulse 1.5s ease-in-out infinite' }} />
        </div>

        {/* Skeleton Cards */}
        {[1, 2, 3].map(i => (
          <div key={i} style={{ background: '#13131a', borderRadius: '16px', padding: '24px', marginBottom: '16px', border: '1px solid #1e1e28' }}>
            <div style={{ width: '40%', height: '20px', background: '#1e1e28', borderRadius: '4px', marginBottom: '12px', animation: 'pulse 1.5s ease-in-out infinite' }} />
            <div style={{ width: '100%', height: '16px', background: '#1e1e28', borderRadius: '4px', marginBottom: '8px', animation: 'pulse 1.5s ease-in-out infinite' }} />
            <div style={{ width: '90%', height: '16px', background: '#1e1e28', borderRadius: '4px', marginBottom: '8px', animation: 'pulse 1.5s ease-in-out infinite' }} />
            <div style={{ width: '60%', height: '16px', background: '#1e1e28', borderRadius: '4px', animation: 'pulse 1.5s ease-in-out infinite' }} />
          </div>
        ))}
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );

  // Editorial design system
  const EDITORIAL = {
    fontSerif: "'Libre Baskerville', 'Georgia', serif",
    fontSans: "'Inter', -apple-system, sans-serif",
    fontMono: "'JetBrains Mono', monospace",
    cardBg: '#111118',
    cardBorder: '#1c1c26',
    cardBorderHover: '#2a2a38',
    textPrimary: '#ededf0',
    textSecondary: '#8a8a9a',
    textTertiary: '#5a5a6a',
    textMuted: '#3a3a4a',
    divider: '#1a1a24',
    sectionGap: '40px',
    cardPadding: '28px',
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'search', label: 'Search', icon: '🔍' },
    { id: 'constitution', label: 'Constitution', icon: '📜' },
    { id: 'lawsuits', label: 'Legal', icon: '⚖️' },
    { id: 'money', label: 'Money', icon: '💰' },
    { id: 'environment', label: 'Environment', icon: '🌍' },
    { id: 'ice', label: 'ICE', icon: '⚠️' },
    { id: 'epstein', label: 'Epstein', icon: '📁' },
    { id: 'polls', label: 'Polls', icon: '📉' },
    { id: 'promises', label: 'Promises', icon: '❌' },
    { id: 'foreign-policy', label: 'Foreign', icon: '🌐' },
    { id: 'healthcare', label: 'Health', icon: '🏥' },
    { id: 'action', label: 'Act Now', icon: '📞' },
  ];

  const Card = ({ children, style, glow }) => (
    <div style={{
      background: EDITORIAL.cardBg,
      borderRadius: '14px',
      padding: EDITORIAL.cardPadding,
      border: `1px solid ${EDITORIAL.cardBorder}`,
      boxShadow: glow ? `0 0 40px ${glow}12, 0 2px 16px rgba(0,0,0,0.4)` : '0 2px 16px rgba(0,0,0,0.3)',
      ...style
    }}>{children}</div>
  );

  const PageHeader = ({ title, subtitle }) => (
    <div style={{ marginBottom: EDITORIAL.sectionGap, textAlign: 'center' }}>
      <h2 style={{ fontFamily: EDITORIAL.fontSerif, fontSize: 'clamp(24px, 5vw, 34px)', fontWeight: '700', color: EDITORIAL.textPrimary, margin: '0 0 10px 0', letterSpacing: '-0.3px', lineHeight: 1.2 }}>{title}</h2>
      {subtitle && <p style={{ fontFamily: EDITORIAL.fontSans, fontSize: '15px', color: EDITORIAL.textSecondary, margin: 0, maxWidth: '520px', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>{subtitle}</p>}
    </div>
  );

  // Modern nav button with larger icons
  const NavButton = ({ active, onClick, icon, label }) => (
    <button
      type="button"
      onClick={onClick}
      role="tab"
      aria-selected={active}
      aria-label={`${label} tab`}
      style={{
        flex: '0 0 auto',
        minWidth: '70px',
        padding: '12px 16px 10px',
        background: active ? 'rgba(239,68,68,0.1)' : 'transparent',
        border: 'none',
        borderBottom: active ? '3px solid #ef4444' : '3px solid transparent',
        borderRadius: '8px 8px 0 0',
        color: active ? '#fff' : '#6b6b7b',
        cursor: 'pointer',
        fontSize: '10px',
        fontWeight: '600',
        textAlign: 'center',
        fontFamily: 'inherit',
        touchAction: 'manipulation',
        transition: 'all 0.2s ease',
      }}
    >
      <div style={{ fontSize: '28px', marginBottom: '4px', lineHeight: 1 }}>{icon}</div>
      {label}
    </button>
  );

  const ActionLink = ({ onClick, children, color }) => (
    <button
      type="button"
      onClick={onClick}
      style={{ 
        display: 'block',
        width: '100%', 
        padding: '14px', 
        background: `${color}15`, 
        border: `1px solid ${color}40`, 
        borderRadius: '10px', 
        color: color, 
        fontSize: '13px', 
        fontWeight: '600',
        cursor: 'pointer',
        textAlign: 'center',
        fontFamily: 'inherit',
        touchAction: 'manipulation',
      }}
    >{children}</button>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0f', fontFamily: 'Inter, -apple-system, sans-serif', color: '#e8e8ed', lineHeight: 1.6, overflowX: 'hidden' }}>
      
      {/* Header */}
      <header style={{ background: 'linear-gradient(180deg, #0f0f14 0%, #0a0a0f 100%)', padding: '0 20px 32px', textAlign: 'center', borderTop: '3px solid #ef4444' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', paddingTop: '28px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '16px', background: 'rgba(239,68,68,0.08)', padding: '6px 14px', borderRadius: '20px', border: '1px solid rgba(239,68,68,0.15)' }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444', animation: 'pulse 2s infinite' }} />
            <span style={{ fontSize: '10px', letterSpacing: '2px', color: '#ef4444', fontWeight: '600' }}>LIVE TRACKER</span>
          </div>
          <h1 style={{ fontFamily: EDITORIAL.fontSerif, fontSize: 'clamp(30px, 6vw, 48px)', fontWeight: '700', color: '#fff', margin: '0 0 10px 0', letterSpacing: '-1px', lineHeight: 1.1 }}>Trump Accountability</h1>
          <p style={{ fontFamily: EDITORIAL.fontSans, fontSize: '15px', color: EDITORIAL.textSecondary, margin: '0 0 20px 0', letterSpacing: '0.3px' }}>Facts with sources. Updated hourly.</p>

          {/* Swipeable Headline Rotator */}
          {rotatorItems.length > 0 && (() => {
  const item = rotatorItems[rotatorIdx % rotatorItems.length];
  const total = rotatorItems.length;
  const currentIdx = rotatorIdx % total;
  return (
    <div
      style={{
        maxWidth: '600px', margin: '0 auto 20px', padding: '16px 20px 0',
        background: `${item.color}0a`, border: `1px solid ${item.color}25`,
        borderRadius: '12px', textAlign: 'left', cursor: 'pointer',
        opacity: rotatorFade ? 1 : 0, transition: 'opacity 0.3s ease',
        touchAction: 'pan-y', overflow: 'hidden',
      }}
      role="button"
      tabIndex={0}
      onClick={() => handleRotatorSwipe('left')}
      onKeyDown={(e) => e.key === 'Enter' && handleRotatorSwipe('left')}
      onTouchStart={(e) => { rotatorTouchRef.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (rotatorTouchRef.current === null) return;
        const diff = rotatorTouchRef.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) handleRotatorSwipe(diff > 0 ? 'left' : 'right');
        rotatorTouchRef.current = null;
      }}
    >
      {/* Content */}
      <div style={{ paddingBottom: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <span style={{ fontSize: '9px', letterSpacing: '1.5px', color: item.color, fontWeight: '700' }}>{item.label}</span>
          {item.date && <span style={{ fontSize: '9px', color: '#4a4a5a' }}>{formatRelativeDate(item.date)}</span>}
          <span style={{ fontSize: '9px', color: '#3a3a4a', marginLeft: 'auto' }}>{currentIdx + 1} / {total}</span>
        </div>
        <div style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '14px', color: '#d4d4dc', marginBottom: '8px', lineHeight: 1.5, fontStyle: item.type === 'promise' ? 'italic' : 'normal' }}>
          {item.title}
        </div>
        {item.detail && (
          <div style={{ fontSize: '12px', color: item.color, fontWeight: '600', lineHeight: 1.5 }}>
            {item.type === 'promise' ? 'Reality: ' : ''}{typeof item.detail === 'string' ? item.detail.slice(0, 120) : ''}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px' }}>
          <span style={{ fontSize: '9px', color: '#4a4a5a' }}>
            {typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches
              ? 'click to advance • auto-rotates'
              : 'swipe or tap • auto-rotates'}
          </span>
          <span
            style={{ fontSize: '10px', color: '#4a4a5a', cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); handleTabClick(item.tab); }}
            onKeyDown={(e) => e.key === 'Enter' && handleTabClick(item.tab)}
            role="button" tabIndex={0}
          >{item.linkText}</span>
        </div>
      </div>
      {/* Progress bar — keyed animation resets on each rotatorIdx change */}
      <div style={{ height: '2px', background: `${item.color}20`, margin: '0 -20px' }}>
        <div
          key={rotatorIdx}
          style={{
            height: '100%',
            background: item.color,
            width: '0%',
            animation: 'rotatorProgress 7s linear forwards',
          }}
        />
      </div>
    </div>
  );
})()}
          
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px', padding: '14px 24px', background: '#13131a', borderRadius: '12px', border: '1px solid #1e1e28', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
            <span style={{ fontSize: '11px', color: '#6b6b7b', fontWeight: '500' }}>TERM TO DATE</span>
            <TermTimer />
          </div>
          
          {lastSync && (
            <div style={{ fontSize: '10px', color: '#3a3a4a', marginTop: '16px' }}>
              Data updated: {new Date(lastSync).toLocaleString()} 
              <span style={{ marginLeft: '8px', color: '#4a4a5a' }}>
                ({Math.round((Date.now() - new Date(lastSync).getTime()) / 60000)} min ago)
              </span>
            </div>
          )}
        </div>
      </header>

      {/* Error Banner - shows when data fetch failed but we're showing cached data */}
      {error && (
        <div style={{ 
          background: 'rgba(239,68,68,0.1)', 
          borderBottom: '1px solid rgba(239,68,68,0.3)',
          padding: '12px 20px',
          textAlign: 'center'
        }}>
          <span style={{ fontSize: '12px', color: '#fca5a5' }}>
            ⚠️ Could not refresh data. Showing cached version.{' '}
            <button 
              onClick={fetchData}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#ef4444', 
                textDecoration: 'underline', 
                cursor: 'pointer',
                fontSize: '12px',
                fontFamily: 'inherit'
              }}
            >
              Retry
            </button>
          </span>
        </div>
      )}

      {/* Nav - Scrollable on mobile, centered on desktop */}
      <nav aria-label="Main navigation" role="navigation" style={{
        background: '#0a0a0f',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        borderBottom: '1px solid #1a1a22', 
        overflowX: 'auto', 
        WebkitOverflowScrolling: 'touch',
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', display: 'flex', justifyContent: 'center', gap: '4px', padding: '8px 16px', minWidth: 'max-content' }}>
          {tabs.map(t => (
            <NavButton key={t.id} active={activeTab === t.id} onClick={() => handleTabClick(t.id)} icon={t.icon} label={t.label} />
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 16px 80px', overflowX: 'hidden' }}>
        
        {/* OVERVIEW */}
        {activeTab === 'overview' && <>

          {/* Section 1: The Numbers (above-the-fold stats) */}
          <div style={{ marginBottom: EDITORIAL.sectionGap, animation: 'fadeInUp 0.5s ease forwards' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))', gap: '16px' }}>
            
            {/* Debt Card */}
            <Card glow="#ef4444">
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                  <span style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '13px', color: EDITORIAL.textSecondary, fontWeight: '700' }}>U.S. National Debt</span>
                  <span style={{ fontSize: '9px', color: '#ef4444', background: 'rgba(239,68,68,0.15)', padding: '4px 10px', borderRadius: '4px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: '#ef4444', animation: 'pulse 2s infinite' }}></span> LIVE</span>
                </div>
                <DebtDisplay debt={data.debt} />
                <a href="https://fiscaldata.treasury.gov/datasets/debt-to-the-penny/debt-to-the-penny" target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: '10px', fontSize: '10px', color: '#4a4a5a', textDecoration: 'underline' }}>Source: U.S. Treasury →</a>
                <div style={{ marginTop: '8px' }}><ShareButton title="U.S. National Debt Under Trump" subtitle="Live debt tracker with per-second updates" stat={fmt(((data.debt?.baseline||38.43)*1e12),0)} source="U.S. Treasury" category="debt" /></div>
              </div>
            </Card>

            {/* Wealth Card */}
            <Card glow="#22c55e">
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '13px', color: EDITORIAL.textSecondary, fontWeight: '700', marginBottom: '12px' }}>Trump Net Worth</div>
                <div style={{ fontSize: 'clamp(28px, 5vw, 36px)', fontWeight: '700', color: '#22c55e', letterSpacing: '-1px' }}>${wealth.current || 6.5}B</div>
                <div style={{ fontSize: '12px', color: '#6b6b7b', margin: '8px 0 12px' }}>Forbes • Rank #{wealth.rank || 645}</div>
                
                <div style={{ padding: '14px 16px', background: 'rgba(34,197,94,0.08)', borderRadius: '10px', border: '1px solid rgba(34,197,94,0.15)' }}>
                  <div style={{ fontSize: '11px', color: '#8a8a9a', marginBottom: '4px' }}>Gained Since Jan 2024</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#22c55e' }}>+${wealthGain.toFixed(1)}B <span style={{ fontSize: '14px', fontWeight: '500' }}>(+{wealthGainPercent}%)</span></div>
                </div>
                <a href="https://www.forbes.com/profile/donald-trump/" target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: '10px', fontSize: '10px', color: '#4a4a5a', textDecoration: 'underline' }}>Source: Forbes Billionaires →</a>
              </div>
            </Card>

            {/* Lawsuits Card */}
            <Card glow="#a855f7">
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '13px', color: EDITORIAL.textSecondary, fontWeight: '700', marginBottom: '12px' }}>Legal Challenges</div>
                <div style={{ fontSize: '48px', fontWeight: '700', color: '#a855f7', lineHeight: 1 }}>
                  697<span style={{ fontSize: '24px', color: '#6b6b7b' }}>+</span>
                </div>
                <div style={{ fontSize: '14px', color: '#a855f7', margin: '8px 0 16px', fontWeight: '500' }}>Lawsuits Filed</div>
                <a href="https://www.justsecurity.org/107087/tracker-litigation-legal-challenges-trump-administration/" target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: '10px', fontSize: '10px', color: '#4a4a5a', textDecoration: 'underline' }}>Source: Just Security →</a>
                <ActionLink onClick={() => handleTabClick('lawsuits')} color="#a855f7">View All Cases →</ActionLink>
              </div>
            </Card>

            {/* Broken Promises Card */}
            <Card glow="#f97316">
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '13px', color: EDITORIAL.textSecondary, fontWeight: '700', marginBottom: '12px' }}>Campaign Promises</div>
                <div style={{ fontSize: '48px', fontWeight: '700', color: '#f97316', lineHeight: 1 }}>
                  {brokenPromises.filter(p => p.status === 'BROKEN').length}<span style={{ fontSize: '24px', color: '#6b6b7b' }}>/{brokenPromises.length}</span>
                </div>
                <div style={{ fontSize: '14px', color: '#f97316', margin: '8px 0 16px', fontWeight: '500' }}>Broken So Far</div>
                <a href="https://www.politifact.com/personalities/donald-trump/" target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: '10px', fontSize: '10px', color: '#4a4a5a', textDecoration: 'underline' }}>Source: PolitiFact →</a>
                <ActionLink onClick={() => handleTabClick('promises')} color="#f97316">View All Promises →</ActionLink>
              </div>
            </Card>
          </div>
          </div>{/* end Section 1 */}

          {/* Section 2: Latest Developments (below stats — news as context, not hook) */}
          {(() => {
            const items = dedupeBreakingNews((data.breakingNews||[]).filter(n=>n.date&&n.headline).sort((a,b)=>new Date(b.date)-new Date(a.date))).slice(0,5);
            if (!items.length) return null;
            const catTab = {ice:'ice',epstein:'epstein',lawsuit:'lawsuits',constitutional:'constitution',poll:'polls',promise:'promises',environment:'environment',money:'money','foreign-policy':'foreign-policy','healthcare-education':'healthcare'};
            return (
              <div style={{ marginBottom: EDITORIAL.sectionGap, animation: 'fadeInUp 0.5s ease 0.1s both' }}>
                <div style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '18px', fontWeight: '700', color: EDITORIAL.textPrimary, marginBottom: '16px', paddingBottom: '12px', borderBottom: `1px solid ${EDITORIAL.divider}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width:'7px',height:'7px',borderRadius:'50%',background:'#ef4444',animation:'pulse 2s infinite',flexShrink:0 }} />
                  Latest Developments
                </div>
                <div style={{ display:'grid',gap:'0' }}>
                  {items.map((item,idx) => {
                    const cols = {ice:'#dc2626',epstein:'#f59e0b',lawsuit:'#a855f7',constitutional:'#3b82f6',poll:'#22c55e',promise:'#f97316',environment:'#10b981','foreign-policy':'#6366f1','healthcare-education':'#14b8a6'};
                    const c = cols[item.category]||'#ef4444';
                    return (
                      <div key={item.id||idx}
                        onClick={()=>handleTabClick(catTab[item.category]||'overview', { scrollTo: 'news-' + (item.id||idx) })}
                        style={{ padding:'14px 0',borderBottom:`1px solid ${EDITORIAL.divider}`,cursor:'pointer' }}>
                        <div style={{ display:'flex',alignItems:'center',gap:'8px',marginBottom:'5px' }}>
                          <span style={{ fontSize:'9px',color:c,fontWeight:'700',textTransform:'uppercase',letterSpacing:'1px',background:c+'15',padding:'2px 7px',borderRadius:'3px' }}>{item.category||''}</span>
                          <span style={{ fontSize:'10px',color:EDITORIAL.textMuted }}>{formatRelativeDate(item.date)}</span>
                        </div>
                        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:'8px' }}>
                          <div style={{ fontFamily:EDITORIAL.fontSerif,fontSize:'14px',color:EDITORIAL.textPrimary,fontWeight:'700',lineHeight:1.4 }}>{item.headline}</div>
                          <ShareButton title={item.headline} subtitle={item.summary} source={item.sources?.[0]?.name || item.sources?.[0]} category={item.category} style={{ flexShrink:0 }} />
                        </div>
                        {item.sources&&item.sources.length>0&&(
                          <div style={{ fontSize:'10px',marginTop:'5px',display:'flex',gap:'8px',flexWrap:'wrap' }}>
                            {item.sources.slice(0,3).map((s,si)=> {
                              const name = typeof s==='object'?s.name:s;
                              const url = typeof s==='object'?s.url:null;
                              return url ? (
                                <a key={si} href={url} target="_blank" rel="noopener noreferrer"
                                  style={{ fontSize:'10px',color:'#3b82f6',textDecoration:'none' }}>{name} ›</a>
                              ) : <span key={si} style={{ fontSize:'10px',color:EDITORIAL.textMuted }}>{name}</span>;
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Section 3: Approaching Milestones */}
          {(() => {
            const INAUGURATION = new Date('2025-01-20T17:00:00Z');
            const daysSince = Math.floor((Date.now() - INAUGURATION.getTime()) / 86400000);
            const nextDayMilestone = [100, 200, 365, 500, 730, 1000, 1461].find(d => d > daysSince);
            const daysUntilMilestone = nextDayMilestone ? nextDayMilestone - daysSince : null;
            const debtTrillions = staticDebt.liveDebt / 1e12;
            const nextDebtMilestone = Math.ceil(debtTrillions);
            const debtRemaining = ((nextDebtMilestone * 1e12) - staticDebt.liveDebt);
            const debtDaysLeft = Math.ceil(debtRemaining / ((data.debt?.perSecond || 92912.33) * 86400));

            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '12px', marginBottom: EDITORIAL.sectionGap, animation: 'fadeInUp 0.5s ease 0.2s both' }}>
                {daysUntilMilestone && (
                  <div style={{ padding: '16px', background: '#13131a', borderRadius: '12px', border: '1px solid #1e1e28', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', letterSpacing: '1px', color: '#6b6b7b', fontWeight: '600', marginBottom: '6px' }}>DAY {daysSince} OF 1,461</div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#ef4444' }}>{daysUntilMilestone}</div>
                    <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '2px' }}>days until day {nextDayMilestone}</div>
                    <div style={{ marginTop: '8px', height: '4px', background: '#1a1a22', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ width: `${(daysSince / 1461) * 100}%`, height: '100%', background: '#ef4444', borderRadius: '2px' }} />
                    </div>
                    <div style={{ fontSize: '9px', color: '#4a4a5a', marginTop: '4px' }}>{((daysSince / 1461) * 100).toFixed(1)}% of term complete</div>
                  </div>
                )}
                {debtDaysLeft > 0 && (
                  <div style={{ padding: '16px', background: '#13131a', borderRadius: '12px', border: '1px solid #1e1e28', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', letterSpacing: '1px', color: '#6b6b7b', fontWeight: '600', marginBottom: '6px' }}>DEBT MILESTONE</div>
                    <div style={{ fontSize: '28px', fontWeight: '800', color: '#f97316' }}>~{debtDaysLeft}d</div>
                    <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '2px' }}>until ${nextDebtMilestone}T national debt</div>
                    <div style={{ marginTop: '8px', height: '4px', background: '#1a1a22', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ width: `${((debtTrillions % 1) * 100)}%`, height: '100%', background: '#f97316', borderRadius: '2px' }} />
                    </div>
                    <div style={{ fontSize: '9px', color: '#4a4a5a', marginTop: '4px' }}>Currently ${debtTrillions.toFixed(2)}T</div>
                  </div>
                )}
                <div style={{ padding: '16px', background: '#13131a', borderRadius: '12px', border: '1px solid rgba(239,68,68,0.2)', textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', letterSpacing: '1px', color: '#6b6b7b', fontWeight: '600', marginBottom: '6px' }}>2026 MIDTERMS</div>
                  <div style={{ fontSize: '28px', fontWeight: '800', color: '#3b82f6' }}>{Math.ceil((new Date('2026-11-03') - Date.now()) / 86400000)}</div>
                  <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '2px' }}>days to vote</div>
                  <div style={{ fontSize: '9px', color: '#3b82f6', marginTop: '8px', fontWeight: '600' }}>Every House seat + 33 Senate seats</div>
                </div>
              </div>
            );
          })()}

          {/* Section 4: Trump's Personal Enrichment - THE MAIN STORY */}
          <Card style={{ marginBottom: EDITORIAL.sectionGap, background: 'linear-gradient(135deg, #111118 0%, #0d0d12 100%)', border: '1px solid rgba(34,197,94,0.3)', animation: 'fadeInUp 0.5s ease 0.3s both' }}>
            <div style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '11px', letterSpacing: '2px', color: '#22c55e', marginBottom: '6px', fontWeight: '700', textAlign: 'center', textTransform: 'uppercase' }}>Profiting While in Power</div>
            <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '16px', textAlign: 'center', lineHeight: 1.5 }}>While proposing $880B in Medicaid cuts and slashing EPA budget by 65%</div>
            
            {/* Main wealth gain */}
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: 'clamp(48px, 10vw, 64px)', fontWeight: '800', color: '#22c55e', lineHeight: 1 }}>
                +${wealthGain.toFixed(1)}B
              </div>
              <div style={{ fontSize: '14px', color: '#6b6b7b', marginTop: '8px' }}>Net worth gain since taking office</div>
              <div style={{ fontSize: '12px', color: '#22c55e', marginTop: '4px', fontWeight: '600' }}>+{wealthGainPercent}% increase</div>
            </div>

            {/* Comparison that makes sense */}
            <div style={{ display: 'grid', gap: '12px', marginBottom: '16px' }}>
              <div style={{ padding: '14px 16px', background: '#0a0a0f', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#6b6b7b' }}>Presidential salary he "donated"</span>
                <span style={{ fontSize: '14px', color: '#4a4a5a', fontWeight: '600' }}>$400K/year</span>
              </div>
              <div style={{ padding: '14px 16px', background: '#0a0a0f', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#6b6b7b' }}>What he gained instead</span>
                <span style={{ fontSize: '14px', color: '#22c55e', fontWeight: '600' }}>${wealthGain.toFixed(1)}B</span>
              </div>
              <div style={{ padding: '14px 16px', background: 'rgba(34,197,94,0.1)', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid rgba(34,197,94,0.2)' }}>
                <span style={{ fontSize: '12px', color: '#22c55e', fontWeight: '500' }}>That's equivalent to...</span>
                <span style={{ fontSize: '14px', color: '#22c55e', fontWeight: '700' }}>{Math.round((wealthGain * 1e9) / 400000).toLocaleString()} years of salary</span>
              </div>
            </div>

            <div style={{ fontSize: '11px', color: '#6b6b7b', textAlign: 'center' }}>
              Most of this came from crypto ventures launched <em>while in office</em>
            </div>
            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <ActionLink onClick={() => handleTabClick('money')} color="#22c55e">See Full Breakdown →</ActionLink>
              <button onClick={() => copyFact(`Trump "donated" his $400K presidential salary but gained +$${wealthGain.toFixed(1)}B while in office — equivalent to ${Math.round((wealthGain * 1e9) / 400000).toLocaleString()} years of salary. Most came from crypto ventures launched while president. Meanwhile, he proposed $880B in Medicaid cuts.`)}
                style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '8px', padding: '8px 14px', color: '#22c55e', fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}
              >📋 Copy This Fact</button>
            </div>
          </Card>

          {/* Section 5: Crisis Points — 2x2 condensed grid */}
          <div style={{ marginBottom: EDITORIAL.sectionGap, animation: 'fadeInUp 0.5s ease 0.4s both' }}>
            <div style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '18px', fontWeight: '700', color: EDITORIAL.textPrimary, marginBottom: '16px', paddingBottom: '12px', borderBottom: `1px solid ${EDITORIAL.divider}` }}>The Crisis Points</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(380px, 100%), 1fr))', gap: '14px' }}>
              {/* Constitutional Concerns */}
              <div onClick={() => handleTabClick('constitution')} style={{ padding: '20px', background: EDITORIAL.cardBg, borderRadius: '12px', border: `1px solid ${EDITORIAL.cardBorder}`, borderLeft: '3px solid #a855f7', cursor: 'pointer', transition: 'border-color 0.2s' }}>
                <div style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '14px', fontWeight: '700', color: '#c4b5fd', marginBottom: '8px' }}>Constitutional Concerns</div>
                <div style={{ fontFamily: EDITORIAL.fontMono, fontSize: '28px', fontWeight: '700', color: '#a855f7', marginBottom: '4px' }}>{(data.new_constitutional || data.constitutionalConcerns || []).length}</div>
                <div style={{ fontSize: '12px', color: EDITORIAL.textSecondary, marginBottom: '12px' }}>Documented concerns — court defiance, due process, press freedom</div>
                <span style={{ fontSize: '12px', color: '#a855f7', fontWeight: '600' }}>Explore →</span>
              </div>
              {/* ICE / Federal Agent Violence */}
              <div onClick={() => handleTabClick('ice')} style={{ padding: '20px', background: EDITORIAL.cardBg, borderRadius: '12px', border: `1px solid ${EDITORIAL.cardBorder}`, borderLeft: '3px solid #dc2626', cursor: 'pointer', transition: 'border-color 0.2s' }}>
                <div style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '14px', fontWeight: '700', color: '#fca5a5', marginBottom: '8px' }}>Federal Agent Violence</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '4px' }}>
                  <span style={{ fontFamily: EDITORIAL.fontMono, fontSize: '28px', fontWeight: '700', color: '#ef4444' }}>{data.iceStats?.totalShootings || 31}</span>
                  <span style={{ fontSize: '12px', color: EDITORIAL.textTertiary }}>shootings</span>
                </div>
                <div style={{ fontSize: '12px', color: EDITORIAL.textSecondary, marginBottom: '12px' }}>{data.iceStats?.usCitizensKilled || 2} U.S. citizens killed • {data.iceStats?.detentionDeaths2025 || 32} detention deaths</div>
                <span style={{ fontSize: '12px', color: '#fca5a5', fontWeight: '600' }}>Explore →</span>
              </div>
              {/* Epstein Files */}
              <div onClick={() => handleTabClick('epstein')} style={{ padding: '20px', background: EDITORIAL.cardBg, borderRadius: '12px', border: `1px solid ${EDITORIAL.cardBorder}`, borderLeft: '3px solid #f59e0b', cursor: 'pointer', transition: 'border-color 0.2s' }}>
                <div style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '14px', fontWeight: '700', color: '#fcd34d', marginBottom: '8px' }}>Epstein Files</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '4px' }}>
                  <span style={{ fontFamily: EDITORIAL.fontMono, fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>3.5M</span>
                  <span style={{ fontSize: '12px', color: EDITORIAL.textTertiary }}>pages released</span>
                </div>
                <div style={{ fontSize: '12px', color: EDITORIAL.textSecondary, marginBottom: '12px' }}>8 Trump flights on Epstein jet • 2.5M pages still withheld</div>
                <span style={{ fontSize: '12px', color: '#f59e0b', fontWeight: '600' }}>Full Timeline →</span>
              </div>
              {/* Golf / Taxpayer Cost */}
              <div onClick={() => handleTabClick('money')} style={{ padding: '20px', background: EDITORIAL.cardBg, borderRadius: '12px', border: `1px solid ${EDITORIAL.cardBorder}`, borderLeft: '3px solid #eab308', cursor: 'pointer', transition: 'border-color 0.2s' }}>
                <div style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '14px', fontWeight: '700', color: '#fde047', marginBottom: '8px' }}>Taxpayer-Funded Golf</div>
                <div style={{ fontFamily: EDITORIAL.fontMono, fontSize: '28px', fontWeight: '700', color: '#eab308', marginBottom: '4px' }}>$110.6M</div>
                <div style={{ fontSize: '12px', color: EDITORIAL.textSecondary, marginBottom: '12px' }}>{data.golf?.golfClubVisits2025 || 88} visits • 25% of days in office • $3.4M per Mar-a-Lago trip</div>
                <span style={{ fontSize: '12px', color: '#eab308', fontWeight: '600' }}>See Breakdown →</span>
              </div>
            </div>
          </div>

          {/* Section 6: Take Action — compact CTA */}
          <Card style={{
            marginBottom: EDITORIAL.sectionGap,
            background: 'linear-gradient(135deg, rgba(59,130,246,0.06) 0%, rgba(239,68,68,0.06) 100%)',
            border: '1px solid rgba(59,130,246,0.25)',
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '16px', fontWeight: '700', color: '#fff', marginBottom: '6px' }}>Your Voice Matters</div>
              <div style={{ fontSize: '13px', color: EDITORIAL.textSecondary, marginBottom: '16px', lineHeight: 1.6 }}>Contact your representatives about what you've read. Calls are more impactful than emails.</div>
              <ActionLink onClick={() => handleTabClick('action')} color="#3b82f6">Find Your Reps & Get Call Scripts →</ActionLink>
            </div>
          </Card>

          {/* Approval Rating — social proof closer */}
          {(() => {
            const polls = data.polls || {};
            const approve = polls.overall?.approve || 37;
            const disapprove = polls.overall?.disapprove || 57;
            const pollSource = polls.overall?.source || 'CNN/SSRS';
            const pollDate = polls.overall?.date || (polls.lastUpdated ? new Date(polls.lastUpdated).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase() : 'FEB 2026');
            return (
          <Card glow="#3b82f6">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '15px', color: '#fff', fontWeight: '700' }}>You're Not Alone</span>
              <span style={{ fontSize: '9px', color: '#3b82f6', background: 'rgba(59,130,246,0.15)', padding: '4px 10px', borderRadius: '4px', fontWeight: '600' }}>{pollDate}</span>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', color: '#22c55e', fontWeight: '600' }}>Approve</span>
                  <span style={{ fontFamily: EDITORIAL.fontMono, fontSize: '22px', fontWeight: '700', color: '#22c55e' }}>{approve}%</span>
                </div>
                <div style={{ height: '20px', background: '#1a1a22', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ width: approve + '%', height: '100%', background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)', borderRadius: '10px' }} />
                </div>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600' }}>Disapprove</span>
                  <span style={{ fontFamily: EDITORIAL.fontMono, fontSize: '22px', fontWeight: '700', color: '#ef4444' }}>{disapprove}%</span>
                </div>
                <div style={{ height: '20px', background: '#1a1a22', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ width: disapprove + '%', height: '100%', background: 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)', borderRadius: '10px' }} />
                </div>
              </div>
            </div>
            <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', fontSize: '12px', color: '#8a8a9a' }}>
              Net: <strong style={{ color: '#ef4444' }}>{polls.netApproval || -12.9}</strong> • Source: {pollSource} ({pollDate})
            </div>
            <div style={{ marginTop: '12px' }}>
              <ActionLink onClick={() => handleTabClick('polls')} color="#3b82f6">All Poll Data →</ActionLink>
            </div>
          </Card>
            );
          })()}


        </>}

        {/* PROMISES */}
        {activeTab === 'promises' && <>

          <PageHeader title="Broken Promises" subtitle="Exact quotes matched against verifiable outcomes" />

          {/* Term Comparison */}
          <TermComparison Card={Card} />

          {/* Latest Updates Section */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '2px solid #1e1e28'
            }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f97316', animation: 'pulse 2s infinite' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: 0 }}>Latest Updates</h3>
            </div>
            <CategoryNews breakingNews={data.breakingNews} category="promise" renderSourceLinks={renderSourceLinks} formatRelativeDate={formatRelativeDate} />
          </div>

          {/* Key Promises */}
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#6b6b7b',
            marginBottom: '16px',
            paddingBottom: '8px',
            borderBottom: '1px solid #1a1a22',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Tracked Promises
          </div>

          {brokenPromises.length === 0 ? (
            <Card><p style={{ color: '#6b6b7b', textAlign: 'center' }}>Loading promises data...</p></Card>
          ) : brokenPromises.map(p => (
            <Card key={p.id} style={{ marginBottom: '16px', borderLeft: `3px solid ${p.statusColor || '#ef4444'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', margin: 0, flex: 1 }}>{p.title || p.promise}</h3>
                <span style={{ fontSize: '10px', fontWeight: '600', padding: '4px 10px', borderRadius: '4px', background: `${p.statusColor || '#ef4444'}20`, color: p.statusColor || '#ef4444', marginLeft: '12px' }}>{p.status}</span>
              </div>
              <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', borderLeft: '2px solid #3a3a4a', marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: '#6b6b7b', marginBottom: '6px' }}>THE PROMISE</div>
                <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, fontStyle: 'italic', lineHeight: 1.6 }}>"{p.quote}"</p>
                <div style={{ fontSize: '11px', color: '#4a4a5a', marginTop: '8px' }}>— {p.date || p.datePromised}</div>
              </div>
              <div style={{ padding: '12px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: '8px', borderLeft: `2px solid ${p.statusColor || '#ef4444'}` }}>
                <div style={{ fontSize: '12px', color: '#ef4444', marginBottom: '6px' }}>REALITY</div>
                {Array.isArray(p.reality) ? (
                  <ul style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, paddingLeft: '18px', lineHeight: 1.8 }}>
                    {p.reality.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                ) : (
                  <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, lineHeight: 1.6 }}>{p.reality}</p>
                )}
              </div>
              <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: {renderSourceLinks(p.sources)}</div>
            </Card>
          ))}
        </>}

        {/* SEARCH */}
        {activeTab === 'search' && (
          <SearchTab
            data={data}
            renderSourceLinks={renderSourceLinks}
            formatRelativeDate={formatRelativeDate}
          />
        )}

        {/* CONSTITUTION */}
        {activeTab === 'constitution' && <>

          <PageHeader title="Constitutional Concerns" subtitle="Documented actions, rulings, and statements that challenge constitutional principles" />

          <div style={{ padding: '14px 16px', background: 'rgba(147,51,234,0.08)', borderRadius: '10px', marginBottom: '24px', fontSize: '12px', color: '#c4b5fd', border: '1px solid rgba(147,51,234,0.2)' }}>
            <strong>Note:</strong> This section presents documented facts—direct quotes, court rulings, and official actions. Each item includes primary sources for verification. <strong>{constitutionalConcerns.length || 9}</strong> concerns tracked.
          </div>

          {/* Latest Updates Section */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '2px solid #1e1e28'
            }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3b82f6', animation: 'pulse 2s infinite' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: 0 }}>Latest Updates</h3>
            </div>
            <CategoryNews breakingNews={data.breakingNews} category="constitutional" renderSourceLinks={renderSourceLinks} formatRelativeDate={formatRelativeDate} />
          </div>

          {/* Key Constitutional Concerns */}
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#6b6b7b',
            marginBottom: '16px',
            paddingBottom: '8px',
            borderBottom: '1px solid #1a1a22',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Key Constitutional Concerns
          </div>

          {/* Data-driven constitutional concerns */}
          {constitutionalConcerns.length > 0 ? (
  constitutionalConcerns.map((concern, idx) => {
    const colors = {
      'ONGOING': '#ef4444',
      'IN COURTS': '#3b82f6', 
      'COMPLETED': '#f59e0b',
      'COURT RULED UNCONSTITUTIONAL': '#ef4444'
    };
    const color = concern.statusColor || colors[concern.status] || '#a855f7';
    return (
      <Card key={concern.id || idx} style={{ marginBottom: '16px', borderLeft: `3px solid ${color}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '10px', fontWeight: '600', padding: '4px 10px', borderRadius: '4px', background: `${color}20`, color: color }}>
            {concern.amendment || 'LEGAL ACTION'}
          </span>
          {concern.status && <span style={{ fontSize: '9px', padding: '3px 8px', borderRadius: '4px', background: '#1a1a22', color: '#6b6b7b' }}>{concern.status}</span>}
        </div>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', margin: '0 0 12px 0' }}>{concern.title}</h3>
        <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '12px' }}>
          <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, lineHeight: 1.6 }}>{concern.description}</p>
          {concern.examples && concern.examples.length > 0 && (
            <ul style={{ fontSize: '12px', color: '#a8a8b8', margin: '10px 0 0 0', paddingLeft: '18px', lineHeight: 1.7 }}>
              {concern.examples.map((ex, i) => <li key={i}>{ex}</li>)}
            </ul>
          )}
        </div>
        {concern.quote && (
          <div style={{ padding: '12px 14px', background: `${color}10`, borderRadius: '8px', borderLeft: `2px solid ${color}` }}>
            <p style={{ fontSize: '13px', color: '#d4d4dc', margin: 0, lineHeight: 1.6, fontStyle: 'italic' }}>"{concern.quote}"</p>
            {concern.quoteSource && <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '6px' }}>— {concern.quoteSource}</div>}
          </div>
        )}
        <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: {renderSourceLinks(concern.sources)}</div>
      </Card>
    );
  })
          ) : (
            /* Fallback hardcoded content if no data in database */
            <>
              {/* Defying Court Orders */}
              <Card style={{ marginBottom: '16px', borderLeft: '3px solid #a855f7' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '10px', fontWeight: '600', padding: '4px 10px', borderRadius: '4px', background: 'rgba(168,85,247,0.2)', color: '#a855f7' }}>SEPARATION OF POWERS</span>
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', margin: '0 0 12px 0' }}>Administration Defies Federal Court Orders</h3>
                <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '12px' }}>
                  <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, lineHeight: 1.6 }}>
                    Multiple federal judges have issued rulings finding the administration in contempt or violation of court orders. Administration has defied or circumvented court orders in 1/3 of cases decided against it—unprecedented for any presidency.
                  </p>
                </div>
                <div style={{ padding: '12px 14px', background: 'rgba(168,85,247,0.06)', borderRadius: '8px', borderLeft: '2px solid #a855f7' }}>
                  <div style={{ fontSize: '12px', color: '#a855f7', marginBottom: '6px' }}>CONSTITUTIONAL PRINCIPLE</div>
                  <p style={{ fontSize: '13px', color: '#d4d4dc', margin: 0, lineHeight: 1.6 }}>
                    Article III establishes the judiciary as a co-equal branch. Executive defiance of court orders undermines judicial review—a cornerstone of constitutional checks and balances since Marbury v. Madison (1803).
                  </p>
                </div>
                <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: {renderSourceLinks(['Washington Post', 'AP News', 'Just Security'])}</div>
              </Card>

              {/* Due Process */}
              <Card style={{ marginBottom: '16px', borderLeft: '3px solid #f59e0b' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '10px', fontWeight: '600', padding: '4px 10px', borderRadius: '4px', background: 'rgba(245,158,11,0.2)', color: '#f59e0b' }}>5TH & 14TH AMENDMENTS</span>
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', margin: '0 0 12px 0' }}>Deportations Without Due Process</h3>
                <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '12px' }}>
                  <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, lineHeight: 1.6 }}>
                    225+ judges have ruled in 700+ cases that mandatory detention policy likely violates law and due process. U.S. citizens and legal residents detained without hearings.
                  </p>
                </div>
                <div style={{ padding: '12px 14px', background: 'rgba(245,158,11,0.06)', borderRadius: '8px', borderLeft: '2px solid #f59e0b' }}>
                  <div style={{ fontSize: '12px', color: '#f59e0b', marginBottom: '6px' }}>CONSTITUTIONAL TEXT</div>
                  <p style={{ fontSize: '13px', color: '#d4d4dc', margin: 0, lineHeight: 1.6, fontStyle: 'italic' }}>
                    "No person shall be... deprived of life, liberty, or property, without due process of law." — Fifth Amendment
                  </p>
                </div>
                <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: {renderSourceLinks(['ACLU', 'Politico', 'Just Security'])}</div>
              </Card>

              {/* Birthright Citizenship */}
              <Card style={{ marginBottom: '16px', borderLeft: '3px solid #3b82f6' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '10px', fontWeight: '600', padding: '4px 10px', borderRadius: '4px', background: 'rgba(59,130,246,0.2)', color: '#3b82f6' }}>14TH AMENDMENT</span>
                  <span style={{ fontSize: '9px', padding: '3px 8px', borderRadius: '4px', background: '#1a1a22', color: '#6b6b7b' }}>IN COURTS</span>
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', margin: '0 0 12px 0' }}>Birthright Citizenship Executive Order</h3>
                <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '12px' }}>
                  <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, lineHeight: 1.6 }}>
                    Executive Order 14160 seeks to deny birthright citizenship—directly contradicting the 14th Amendment text. Supreme Court will decide constitutionality in 2026.
                  </p>
                </div>
                <div style={{ padding: '12px 14px', background: 'rgba(59,130,246,0.06)', borderRadius: '8px', borderLeft: '2px solid #3b82f6' }}>
                  <div style={{ fontSize: '12px', color: '#3b82f6', marginBottom: '6px' }}>CONSTITUTIONAL TEXT</div>
                  <p style={{ fontSize: '13px', color: '#d4d4dc', margin: 0, lineHeight: 1.6, fontStyle: 'italic' }}>
                    "All persons born or naturalized in the United States, and subject to the jurisdiction thereof, are citizens." — 14th Amendment
                  </p>
                </div>
                <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: {renderSourceLinks(['Ballotpedia', 'SCOTUSblog', 'Rutgers Law'])}</div>
              </Card>

              {/* First Amendment */}
              <Card style={{ marginBottom: '16px', borderLeft: '3px solid #8b5cf6' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '10px', fontWeight: '600', padding: '4px 10px', borderRadius: '4px', background: 'rgba(139,92,246,0.2)', color: '#8b5cf6' }}>1ST AMENDMENT</span>
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', margin: '0 0 12px 0' }}>Threats Against Press and Protesters</h3>
                <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '12px' }}>
                  <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, lineHeight: 1.6 }}>
                    Two U.S. citizens shot by federal agents at immigration protests. AP, NPR, PBS sued over press access bans. Calls to revoke licenses of critical networks.
                  </p>
                </div>
                <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: {renderSourceLinks(['Committee to Protect Journalists', 'PEN America', 'NPR'])}</div>
              </Card>

              {/* January 6 Pardons */}
              <Card style={{ marginBottom: '16px', borderLeft: '3px solid #ef4444' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '10px', fontWeight: '600', padding: '4px 10px', borderRadius: '4px', background: 'rgba(239,68,68,0.2)', color: '#ef4444' }}>RULE OF LAW</span>
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', margin: '0 0 12px 0' }}>January 6th Mass Pardons</h3>
                <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '12px' }}>
                  <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, lineHeight: 1.6 }}>
                    Pardoned 1,500+ individuals convicted of Capitol attack crimes, including those who assaulted police officers and seditious conspiracy convicts.
                  </p>
                </div>
                <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: {renderSourceLinks(['DOJ Records', 'NPR', 'AP News'])}</div>
              </Card>

              {/* Equal Protection Violation */}
              <Card style={{ marginBottom: '16px', borderLeft: '3px solid #ef4444' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '10px', fontWeight: '600', padding: '4px 10px', borderRadius: '4px', background: 'rgba(239,68,68,0.2)', color: '#ef4444' }}>14TH AMENDMENT</span>
                  <span style={{ fontSize: '9px', padding: '3px 8px', borderRadius: '4px', background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>RULED UNCONSTITUTIONAL</span>
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', margin: '0 0 12px 0' }}>Political Discrimination in Grants (Jan 2026)</h3>
                <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '12px' }}>
                  <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, lineHeight: 1.6 }}>
                    Court ruled DOE cancelled grants based on whether recipients lived in states that voted for Trump in 2024—"purposeful segregation based on electoral support."
                  </p>
                </div>
                <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: {renderSourceLinks(['EDF', 'Federal Court Records'])}</div>
              </Card>
            </>
          )}
        </>}

        {/* LAWSUITS */}
        {activeTab === 'lawsuits' && <>


          <PageHeader title="Legal Challenges" subtitle="Unprecedented legal battles involving the administration" />

          {/* Latest Updates — top of tab */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '2px solid #1e1e28'
            }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#a855f7', animation: 'pulse 2s infinite' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: 0 }}>Latest Updates</h3>
            </div>
            <CategoryNews breakingNews={data.breakingNews} category="lawsuit" renderSourceLinks={renderSourceLinks} formatRelativeDate={formatRelativeDate} />
          </div>

<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: '12px', marginBottom: '24px' }}>
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(168,85,247,0.08)', borderRadius: '12px', border: '1px solid rgba(168,85,247,0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#a855f7' }}>697+</div>
              <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Lawsuits Against Admin</div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(59,130,246,0.08)', borderRadius: '12px', border: '1px solid rgba(59,130,246,0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#3b82f6' }}>24</div>
              <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Supreme Court Emergency Cases</div>
            </div>
          </div>

          <Card style={{ marginBottom: '24px', borderLeft: '3px solid #ef4444' }}>
            <div style={{ fontSize: '13px', color: '#d4d4dc', lineHeight: 1.7 }}>
              <strong style={{ color: '#ef4444' }}>Historic:</strong> 697+ lawsuits have been filed against the Trump administration — more than any presidency in history. According to SCOTUSblog, "Never before has the court been asked to rule on the legality of so many presidential actions in such a short period of time."
            </div>
            <a href="https://www.scotusblog.com/2026/01/looking-back-at-2025-the-supreme-court-and-the-trump-administration/" target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: '10px', fontSize: '10px', color: '#4a4a5a', textDecoration: 'underline' }}>Source: SCOTUSblog →</a>
          </Card>

          <Card style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '14px', color: '#a855f7', fontWeight: '600', marginBottom: '8px' }}>Major Areas of Litigation</div>
            <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '16px' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '12px' }}><span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#22c55e' }}></span> Blocked</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '12px' }}><span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#f59e0b' }}></span> Mixed/Partial</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '12px' }}><span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#3b82f6' }}></span> Pending</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '8px', height: '8px', borderRadius: '2px', background: '#ef4444' }}></span> Ongoing</span>
            </div>
            
            {[
              { area: 'Birthright Citizenship', status: 'Blocked', desc: 'Multiple courts blocked EO to end birthright citizenship. Supreme Court hearing expected Feb-Apr 2026.', color: '#22c55e', url: 'https://www.scotusblog.com/case-files/cases/trump-v-casa-inc/' },
              { area: 'Federal Workforce Cuts', status: 'Mixed', desc: 'Probationary employee firings ruled illegal. DOGE cuts face multiple challenges.', color: '#f59e0b', url: 'https://www.afge.org/article/summary-of-afge-lawsuits-against-trump--how-litigation-works-2/' },
              { area: 'Tariffs (IEEPA)', status: 'Pending', desc: 'Major case on Trump\'s use of emergency powers for tariffs. Supreme Court arguments underway.', color: '#3b82f6', url: 'https://www.scotusblog.com/case-files/cases/learning-resources-inc-v-trump/' },
              { area: 'Immigration Enforcement', status: 'Ongoing', desc: '100+ lawsuits on F-1 visa revocations alone. Minneapolis suing over Operation Metro Surge.', color: '#ef4444', url: 'https://www.aclu.org/news/immigrants-rights' },
              { area: 'Transgender Military Ban', status: 'Blocked', desc: 'Preliminary injunction granted. Administration appealed to Supreme Court.', color: '#22c55e', url: 'https://www.lambdalegal.org/in-court/cases/doe-v-trump' },
              { area: 'DEI Programs', status: 'Mixed', desc: 'Federal funding freezes temporarily blocked. Multiple state AG lawsuits pending.', color: '#f59e0b', url: 'https://www.justsecurity.org/107087/tracker-litigation-legal-challenges-trump-administration/' },
            ].map((item, i) => (
              <div key={i} style={{ padding: '14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '10px', borderLeft: `3px solid ${item.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '14px', color: '#fff', fontWeight: '600' }}>{item.area}</span>
                  <span style={{ fontSize: '10px', fontWeight: '600', padding: '3px 8px', borderRadius: '4px', background: `${item.color}20`, color: item.color }}>{item.status}</span>
                </div>
                <p style={{ fontSize: '12px', color: '#a8a8b8', margin: '0 0 8px 0', lineHeight: 1.5 }}>{item.desc}</p>
                <a href={item.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '10px', color: '#6b6b7b', textDecoration: 'underline' }}>View case details →</a>
              </div>
            ))}
          </Card>

          {/* Additional Context */}
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#6b6b7b',
            marginBottom: '16px',
            paddingBottom: '8px',
            borderBottom: '1px solid #1a1a22',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Additional Context
          </div>

          {/* Trump's Own Lawsuits */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #eab308' }}>
            <div style={{ fontSize: '14px', color: '#eab308', fontWeight: '600', marginBottom: '16px' }}>Lawsuits Filed BY Trump (2025-2026)</div>
            <p style={{ fontSize: '12px', color: '#6b6b7b', marginBottom: '16px' }}>The president has also filed numerous personal lawsuits while in office:</p>
            
            {[
              { target: 'IRS & Treasury', amount: '$10B', date: 'Jan 29, 2026', desc: 'Over tax return leaks to media', url: 'https://www.npr.org/2026/01/30/nx-s1-5693662/trump-sues-irs-and-treasury-for-10-billion-over-leaked-tax-information' },
              { target: 'BBC', amount: '$5B', date: 'Jan 2026', desc: 'Over documentary editing of Jan 6 remarks', url: 'https://www.foxnews.com/media/trump-files-powerhouse-10-billion-lawsuit-bbc-documentary-editing-jan-6-remarks' },
              { target: 'JPMorgan Chase', amount: '$5B', date: 'Jan 2026', desc: 'For closing his accounts in 2021', url: 'https://www.cbsnews.com/news/trump-sues-irs-treasury-10-billion-letting-tax-returns-leak/' },
              { target: 'New York Times', amount: 'Unspecified', date: '2025', desc: 'Defamation over business articles (dismissed, refiled)', url: 'https://www.nytimes.com/2025/trump-lawsuit' },
            ].map((suit, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '8px' }}>
                <div>
                  <div style={{ fontSize: '13px', color: '#fff', fontWeight: '500' }}>{suit.target}</div>
                  <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '2px' }}>{suit.desc}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '14px', color: '#eab308', fontWeight: '600' }}>{suit.amount}</div>
                  <a href={suit.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '9px', color: '#6b6b7b', textDecoration: 'underline' }}>{suit.date}</a>
                </div>
              </div>
            ))}
            
            <div style={{ padding: '12px 14px', background: 'rgba(234,179,8,0.08)', borderRadius: '8px', marginTop: '12px', fontSize: '12px', color: '#a8a8b8' }}>
              <strong style={{ color: '#eab308' }}>Total sought:</strong> $20B+ in personal lawsuits while serving as president
            </div>
          </Card>

          <Card style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '14px', color: '#ef4444', fontWeight: '600', marginBottom: '16px' }}>Supreme Court Record (2025)</div>
            <div style={{ padding: '14px', background: 'rgba(239,68,68,0.06)', borderRadius: '8px', marginBottom: '12px' }}>
              <p style={{ fontSize: '13px', color: '#d4d4dc', margin: 0, lineHeight: 1.6 }}>
                The Supreme Court overwhelmingly sided with the Trump administration in 2025, ruling in favor in <strong>23 of 24</strong> emergency docket cases. Only Justice Jackson voted against the administration in every case.
              </p>
            </div>
            <div style={{ fontSize: '12px', color: '#6b6b7b', lineHeight: 1.7 }}>
              The lone exception: <em>Margolin v. National Association of Immigration Judges</em> — the only case where Justices Thomas and Alito voted against the administration.
            </div>
          </Card>

          <Card>
            <div style={{ fontSize: '14px', color: '#3b82f6', fontWeight: '600', marginBottom: '16px' }}>Comprehensive Trackers</div>
            <div style={{ display: 'grid', gap: '10px' }}>
              <a href="https://www.justsecurity.org/107087/tracker-litigation-legal-challenges-trump-administration/" target="_blank" rel="noopener noreferrer" style={{ display: 'block', padding: '12px 14px', background: 'rgba(59,130,246,0.1)', borderRadius: '8px', textDecoration: 'none' }}>
                <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: '600' }}>Just Security Litigation Tracker</div>
                <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '2px' }}>Comprehensive database of all legal challenges</div>
              </a>
              <a href="https://www.lawfaremedia.org/projects-series/trials-of-the-trump-administration/tracking-trump-administration-litigation" target="_blank" rel="noopener noreferrer" style={{ display: 'block', padding: '12px 14px', background: 'rgba(59,130,246,0.1)', borderRadius: '8px', textDecoration: 'none' }}>
                <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: '600' }}>Lawfare Trump Administration Tracker</div>
                <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '2px' }}>Detailed case analysis and updates</div>
              </a>
              <a href="https://ballotpedia.org/Multistate_lawsuits_against_the_federal_government_during_the_Trump_administration,_2025-2026" target="_blank" rel="noopener noreferrer" style={{ display: 'block', padding: '12px 14px', background: 'rgba(59,130,246,0.1)', borderRadius: '8px', textDecoration: 'none' }}>
                <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: '600' }}>Ballotpedia Multistate Lawsuits</div>
                <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '2px' }}>State-level legal challenges</div>
              </a>
            </div>
          </Card>
        </>}

        {/* MONEY */}
        {activeTab === 'money' && <>
          <PageHeader title="Follow the Money" subtitle="How Trump is profiting from the presidency" />

          {/* Latest Updates Section */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '2px solid #1e1e28'
            }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#eab308', animation: 'pulse 2s infinite' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: 0 }}>Latest Updates</h3>
            </div>
            <CategoryNews breakingNews={data.breakingNews} category="money" renderSourceLinks={renderSourceLinks} formatRelativeDate={formatRelativeDate} />
          </div>

          {/* Key Financial Overview */}
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#6b6b7b',
            marginBottom: '16px',
            paddingBottom: '8px',
            borderBottom: '1px solid #1a1a22',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Financial Overview
          </div>

          {/* The Big Picture - Total Enrichment */}
          <Card glow="#22c55e" style={{ marginBottom: '24px' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '11px', letterSpacing: '1.5px', color: '#6b6b7b', marginBottom: '8px' }}>ESTIMATED WEALTH GAINED SINCE TAKING OFFICE</div>
              <div style={{ fontSize: 'clamp(42px, 8vw, 56px)', fontWeight: '800', color: '#22c55e', lineHeight: 1 }}>$4.2B+</div>
              <div style={{ fontSize: '13px', color: '#6b6b7b', marginTop: '8px' }}>Net worth: $2.3B → $6.5B (Forbes)</div>
            </div>
            
            {/* Wealth breakdown bar */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '8px' }}>WHERE IT COMES FROM</div>
              <div style={{ display: 'flex', height: '32px', borderRadius: '6px', overflow: 'hidden', marginBottom: '12px' }}>
                <div style={{ flex: '7', background: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '10px', color: '#fff', fontWeight: '600' }}>CRYPTO 73%</span>
                </div>
                <div style={{ flex: '2', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '9px', color: '#fff', fontWeight: '600' }}>TRUTH</span>
                </div>
                <div style={{ flex: '1', background: '#eab308', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '8px', color: '#000', fontWeight: '600' }}>RE</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '11px' }}>
                <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#8b5cf6', borderRadius: '2px', marginRight: '4px' }}></span>Crypto (~$11.6B potential)</span>
                <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#3b82f6', borderRadius: '2px', marginRight: '4px' }}></span>Truth Social (~$2B)</span>
                <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#eab308', borderRadius: '2px', marginRight: '4px' }}></span>Real Estate</span>
              </div>
            </div>
            
            <a href="https://www.nytimes.com/interactive/2025/07/02/business/donald-trump-wealth-net-worth.html" target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#4a4a5a', textDecoration: 'underline' }}>Source: NYT, Forbes, Accountable.US →</a>
          </Card>

          {/* Crypto Empire */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #8b5cf6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <span style={{ fontSize: '18px' }}>🪙</span>
              <span style={{ fontSize: '14px', color: '#a78bfa', fontWeight: '600' }}>The Crypto Empire</span>
            </div>
            
            <div style={{ padding: '16px', background: 'rgba(139,92,246,0.08)', borderRadius: '10px', marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', color: '#a78bfa', marginBottom: '4px' }}>HOUSE JUDICIARY COMMITTEE FINDING (NOV 2025)</div>
              <p style={{ fontSize: '14px', color: '#e8e8ed', margin: 0, lineHeight: 1.6, fontStyle: 'italic' }}>
                "Donald Trump has turned the Oval Office into the world's most corrupt crypto startup operation, minting staggering personal fortunes for him and his family."
              </p>
              <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '8px' }}>— Rep. Jamie Raskin, Ranking Member</div>
            </div>

            <div style={{ display: 'grid', gap: '10px', marginBottom: '16px' }}>
              {[
                { label: '$TRUMP Memecoin', value: '~$4.4B', note: 'Token holdings (80% owned by Trump entities)', color: '#8b5cf6' },
                { label: 'World Liberty Financial', value: '$550M+', note: 'Token sale revenue (75% to Trump)', color: '#a78bfa' },
                { label: 'Crypto income (H1 2025)', value: '$800M+', note: 'Cash realized from sales', color: '#c4b5fd' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '14px', background: '#0a0a0f', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '13px', color: '#fff', fontWeight: '500' }}>{item.label}</div>
                    <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '2px' }}>{item.note}</div>
                  </div>
                  <div style={{ fontSize: '18px', fontWeight: '700', color: item.color }}>{item.value}</div>
                </div>
              ))}
            </div>

            <div style={{ padding: '12px 14px', background: 'rgba(239,68,68,0.08)', borderRadius: '8px', fontSize: '12px', color: '#fca5a5', lineHeight: 1.6 }}>
              <strong>The catch:</strong> Buyers of $TRUMP memecoin are down 92% from peak. Melania's token crashed 99%. The Trumps profit; retail investors lose.
            </div>
            
            <a href="https://democrats-judiciary.house.gov/media-center/press-releases/new-report-exposes-the-trump-family-s-multi-billion-dollar-crypto-empire-fueled-by-self-dealing-and-corrupt-foreign-interests" target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: '12px', fontSize: '11px', color: '#4a4a5a', textDecoration: 'underline' }}>Source: House Judiciary Committee Democrats Report →</a>
          </Card>

          {/* Taxpayer-Funded Golf */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #ef4444' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <span style={{ fontSize: '18px' }}>⛳</span>
              <span style={{ fontSize: '14px', color: '#ef4444', fontWeight: '600' }}>Taxpayer-Funded Golf (2025)</span>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))', gap: '12px', marginBottom: '16px' }}>
              <div style={{ padding: '16px', background: 'rgba(239,68,68,0.08)', borderRadius: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#ef4444' }}>$110.6M</div>
                <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '4px' }}>Estimated cost to taxpayers</div>
              </div>
              <div style={{ padding: '16px', background: 'rgba(239,68,68,0.08)', borderRadius: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#f87171' }}>88</div>
                <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '4px' }}>Golf club visits (25% of days)</div>
              </div>
            </div>

            <div style={{ display: 'grid', gap: '8px', marginBottom: '16px' }}>
              {[
                { label: 'Cost per Mar-a-Lago trip', value: '~$3.4M', note: 'Air Force One, Secret Service, Coast Guard' },
                { label: 'Cost per Bedminster trip', value: '~$1.1M', note: 'Smaller aircraft' },
                { label: 'Scotland trip (July 2025)', value: '~$10M', note: '5-day trip, new course ribbon-cutting' },
                { label: 'First term total (2017-2021)', value: '$151.5M', note: 'On pace to exceed $300M this term' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#a8a8b8' }}>{item.label}</div>
                    <div style={{ fontSize: '10px', color: '#4a4a5a' }}>{item.note}</div>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#ef4444' }}>{item.value}</div>
                </div>
              ))}
            </div>
            
            <a href="https://www.gao.gov/products/gao-19-178" target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#4a4a5a', textDecoration: 'underline' }}>Source: GAO Report, CREW, HuffPost →</a>
          </Card>

          {/* Secret Service Payments TO Trump */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #eab308' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '18px' }}>🏨</span>
              <span style={{ fontSize: '14px', color: '#eab308', fontWeight: '600' }}>Secret Service Pays Trump's Businesses</span>
            </div>
            
            <p style={{ fontSize: '13px', color: '#a8a8b8', marginBottom: '16px', lineHeight: 1.6 }}>
              When agents protect Trump at his properties, they pay for rooms and meals at resorts he owns. This money goes directly into his pocket.
            </p>

            <div style={{ display: 'grid', gap: '8px', marginBottom: '16px' }}>
              {[
                { label: 'Second term (first months)', value: '$100K+', note: '"Just the tip of the iceberg" — CREW' },
                { label: 'First term total', value: '~$2M', note: 'FOIA records' },
                { label: 'Nightly rate charged', value: 'Up to $800+', note: 'Above government rate' },
                { label: 'Bedminster cottage', value: '$17K/month', note: 'For Secret Service use' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#a8a8b8' }}>{item.label}</div>
                    <div style={{ fontSize: '10px', color: '#4a4a5a' }}>{item.note}</div>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#eab308' }}>{item.value}</div>
                </div>
              ))}
            </div>

            <div style={{ padding: '12px 14px', background: 'rgba(234,179,8,0.08)', borderRadius: '8px', fontSize: '12px', color: '#fcd34d', lineHeight: 1.6 }}>
              <strong>Eric Trump claimed</strong> in 2019 the family charged Secret Service "like $50." Records show this was false.
            </div>
            
            <a href="https://www.citizensforethics.org/reports-investigations/crew-investigations/secret-service-has-spent-nearly-100k-at-trump-properties/" target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: '12px', fontSize: '11px', color: '#4a4a5a', textDecoration: 'underline' }}>Source: CREW FOIA Records →</a>
          </Card>

          {/* Other Revenue Streams */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #3b82f6' }}>
            <div style={{ fontSize: '14px', color: '#3b82f6', fontWeight: '600', marginBottom: '16px' }}>Other Revenue Streams (2025)</div>
            
            <div style={{ display: 'grid', gap: '8px', marginBottom: '12px' }}>
              {[
                { label: 'Trump Bibles', value: '$3M', note: 'Lee Greenwood partnership' },
                { label: 'Trump Watches', value: '$2.8M', note: 'Licensing fees' },
                { label: 'Trump Sneakers & Fragrances', value: '$2.5M', note: 'Licensing fees' },
                { label: '"45" Guitar', value: '$1M', note: 'Licensing fees' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#a8a8b8' }}>{item.label}</div>
                    <div style={{ fontSize: '10px', color: '#4a4a5a' }}>{item.note}</div>
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#3b82f6' }}>{item.value}</div>
                </div>
              ))}
            </div>
            
            <a href="https://time.com/7342470/trump-net-worth-wealth-crypto/" target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#4a4a5a', textDecoration: 'underline' }}>Source: TIME, Trump Financial Disclosure →</a>
          </Card>

          {/* The Contrast */}
          <Card style={{ background: 'linear-gradient(135deg, #13131a 0%, #0f0f14 100%)' }}>
            <div style={{ fontSize: '10px', letterSpacing: '2px', color: '#4a4a5a', marginBottom: '16px', fontWeight: '600', textAlign: 'center' }}>THE CONTRAST</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))', gap: '16px', textAlign: 'center' }}>
              <div style={{ padding: '20px', background: 'rgba(239,68,68,0.06)', borderRadius: '12px' }}>
                <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '8px' }}>NATIONAL DEBT ADDED</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#ef4444', fontFamily: 'JetBrains Mono, monospace' }}>+{fmt(staticDebt.debtSinceInauguration)}</div>
                <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '4px' }}>Your share: ~${Math.round((data.debt?.perHousehold || 285127) * (staticDebt.debtSinceInauguration / (36.18 * 1e12))).toLocaleString()}</div>
              </div>
              <div style={{ padding: '20px', background: 'rgba(34,197,94,0.06)', borderRadius: '12px' }}>
                <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '8px' }}>TRUMP'S GAIN</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#22c55e' }}>+$4.2B</div>
                <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '4px' }}>+183% since Jan 2024</div>
              </div>
            </div>
          </Card>
        </>}

        {/* ENVIRONMENT */}
        {activeTab === 'environment' && <>
          <PageHeader title="Environmental Rollbacks" subtitle="Tracking the dismantling of climate protections and conservation efforts" />

          {/* Latest Updates Section */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '2px solid #1e1e28'
            }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981', animation: 'pulse 2s infinite' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: 0 }}>Latest Updates</h3>
            </div>
            <CategoryNews breakingNews={data.breakingNews} category="environment" renderSourceLinks={renderSourceLinks} formatRelativeDate={formatRelativeDate} />
          </div>

          {/* Key Stats */}
          {(() => {
            const envStats = data.environmentStats || {};
            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: '12px', marginBottom: '24px' }}>
                <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(239,68,68,0.08)', borderRadius: '12px', border: '1px solid rgba(239,68,68,0.2)' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#ef4444' }}>{envStats.rollbacksTotal || 440}+</div>
                  <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Environmental Rollbacks</div>
                  <div style={{ fontSize: '9px', color: '#4a4a5a', marginTop: '2px' }}>Since Jan 2025</div>
                </div>
                <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(245,158,11,0.08)', borderRadius: '12px', border: '1px solid rgba(245,158,11,0.2)' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>-{((envStats.acresLostFromMonuments || 2000000) / 1000000).toFixed(0)}M</div>
                  <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Acres Lost from National Monuments</div>
                </div>
              </div>
            );
          })()}

          {/* Historical Comparison */}
          <HistoricalComparison category="environment" />

          {/* Endangered Species Act Repeal */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #ef4444' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '10px', fontWeight: '600', padding: '4px 10px', borderRadius: '4px', background: 'rgba(239,68,68,0.2)', color: '#ef4444' }}>ENDANGERED SPECIES ACT</span>
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', margin: '0 0 12px 0' }}>Endangered Species Act "Effectively Repealed"</h3>
            <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '12px' }}>
              <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, lineHeight: 1.7 }}>
                On February 12, 2026, Trump signed executive orders removing federal protections for endangered and threatened species, ending the 53-year-old law that protected over 1,600 species including bald eagles, gray wolves, and grizzly bears.
              </p>
            </div>
            <div style={{ display: 'grid', gap: '10px' }}>
              {[
                { detail: 'Removes "threatened" category entirely', color: '#ef4444' },
                { detail: 'Eliminates mandatory habitat protections', color: '#f59e0b' },
                { detail: 'Allows states to authorize "takings" (killing) of protected species', color: '#ef4444' },
                { detail: 'Scientists call it "catastrophic" for biodiversity', color: '#dc2626' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '10px 14px', background: '#0a0a0f', borderRadius: '6px', borderLeft: `3px solid ${item.color}`, fontSize: '12px', color: '#a8a8b8' }}>
                  {item.detail}
                </div>
              ))}
            </div>
            <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: NPR, Washington Post, Center for Biological Diversity</div>
          </Card>

          {/* Climate Agreements */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #3b82f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '10px', fontWeight: '600', padding: '4px 10px', borderRadius: '4px', background: 'rgba(59,130,246,0.2)', color: '#3b82f6' }}>PARIS AGREEMENT</span>
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', margin: '0 0 12px 0' }}>Withdrawn from Paris Climate Agreement (Again)</h3>
            <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '12px' }}>
              <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, lineHeight: 1.7 }}>
                On Day 1 (Jan 20, 2025), Trump signed an executive order to withdraw from the Paris Climate Agreement for the second time. The U.S. is the only major economy not committed to limiting global warming.
              </p>
            </div>
            <div style={{ padding: '12px 14px', background: 'rgba(59,130,246,0.06)', borderRadius: '8px', borderLeft: '2px solid #3b82f6' }}>
              <div style={{ fontSize: '12px', color: '#3b82f6', marginBottom: '6px' }}>IMPACT</div>
              <p style={{ fontSize: '13px', color: '#d4d4dc', margin: 0, lineHeight: 1.6 }}>
                UN Secretary-General called it "a major blow to global efforts" to limit warming to 1.5°C. U.S. emissions represent 13% of global total.
              </p>
            </div>
            <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: UN, Reuters, Climate Action Tracker</div>
          </Card>

          {/* EPA Rollbacks */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #f59e0b' }}>
            <div style={{ fontSize: '14px', color: '#f59e0b', fontWeight: '600', marginBottom: '16px' }}>EPA Regulation Rollbacks</div>
            <div style={{ display: 'grid', gap: '10px', marginBottom: '16px' }}>
              {[
                { title: 'Clean Air Act', detail: 'Weakened emissions standards for cars and trucks. Projected to add 1.8 billion tons of CO₂ by 2050.', status: 'Finalized Jan 2025' },
                { title: 'Clean Water Act', detail: 'Removed protections for wetlands and streams. 50% of U.S. wetlands now unprotected.', status: 'Finalized Feb 2025' },
                { title: 'Methane Regulations', detail: 'Eliminated requirement for oil/gas companies to detect and fix methane leaks.', status: 'Repealed Jan 2025' },
                { title: 'Mercury Standards', detail: 'Weakened limits on mercury emissions from coal plants despite health risks.', status: 'Pending litigation' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '14px', background: '#0a0a0f', borderRadius: '8px', borderLeft: '3px solid #f59e0b' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', color: '#fff', fontWeight: '600' }}>{item.title}</span>
                    <span style={{ fontSize: '9px', padding: '3px 8px', borderRadius: '4px', background: 'rgba(245,158,11,0.2)', color: '#f59e0b' }}>{item.status}</span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#a8a8b8', margin: 0, lineHeight: 1.5 }}>{item.detail}</p>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: EPA, Environmental Defense Fund, NYT</div>
          </Card>

          {/* Additional Context */}
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#6b6b7b',
            marginBottom: '16px',
            paddingBottom: '8px',
            borderBottom: '1px solid #1a1a22',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Additional Context
          </div>

          {/* National Parks & Public Lands */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #22c55e' }}>
            <div style={{ fontSize: '14px', color: '#22c55e', fontWeight: '600', marginBottom: '16px' }}>National Parks & Public Lands</div>
            <div style={{ display: 'grid', gap: '10px' }}>
              {[
                { location: 'Bears Ears National Monument', change: 'Reduced by 85% (1.35M acres)', impact: 'Opened to uranium mining' },
                { location: 'Grand Staircase-Escalante', change: 'Reduced by 50% (800K acres)', impact: 'Opened to coal mining' },
                { location: 'Northeast Canyons Marine Monument', change: 'Fully revoked (5,000 sq mi)', impact: 'Opened to commercial fishing' },
                { location: 'Arctic National Wildlife Refuge', change: 'Opened to drilling', impact: '1.6M acres affected' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px' }}>
                  <div style={{ fontSize: '13px', color: '#fff', fontWeight: '600', marginBottom: '4px' }}>{item.location}</div>
                  <div style={{ fontSize: '11px', color: '#22c55e', marginBottom: '2px' }}>{item.change}</div>
                  <div style={{ fontSize: '11px', color: '#6b6b7b' }}>{item.impact}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: National Parks Conservation Association, Wilderness Society</div>
          </Card>

          {/* Clean Energy Rollbacks */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #eab308' }}>
            <div style={{ fontSize: '14px', color: '#eab308', fontWeight: '600', marginBottom: '16px' }}>Clean Energy Subsidies Eliminated</div>
            <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '12px' }}>
              <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, lineHeight: 1.7 }}>
                One Big Beautiful Bill repealed $425 billion in clean energy tax credits from the Inflation Reduction Act, including credits for solar, wind, electric vehicles, and home efficiency upgrades.
              </p>
            </div>
            <div style={{ display: 'grid', gap: '8px' }}>
              {[
                { item: 'EV tax credit ($7,500)', status: 'Eliminated' },
                { item: 'Solar panel investment credit (30%)', status: 'Eliminated' },
                { item: 'Home energy efficiency rebates', status: 'Eliminated' },
                { item: 'Wind energy production credit', status: 'Eliminated' },
              ].map((entry, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 14px', background: '#0a0a0f', borderRadius: '6px' }}>
                  <span style={{ fontSize: '12px', color: '#a8a8b8' }}>{entry.item}</span>
                  <span style={{ fontSize: '11px', fontWeight: '600', color: '#ef4444' }}>{entry.status}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: CBO, Department of Energy, Bloomberg</div>
          </Card>

          {/* Fossil Fuel Expansion */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #a855f7' }}>
            <div style={{ fontSize: '14px', color: '#a855f7', fontWeight: '600', marginBottom: '16px' }}>Fossil Fuel Expansion</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: '12px', marginBottom: '16px' }}>
              <div style={{ padding: '14px', background: 'rgba(168,85,247,0.08)', borderRadius: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#a855f7' }}>+{data.environmentStats?.drillingPermitIncrease || 470}%</div>
                <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Increase in drilling permits</div>
              </div>
              <div style={{ padding: '14px', background: 'rgba(168,85,247,0.08)', borderRadius: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#c084fc' }}>+{data.environmentStats?.coalMined2025 || 125}M</div>
                <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Tons of coal mined (2025)</div>
              </div>
            </div>
            <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '12px' }}>
              <p style={{ fontSize: '13px', color: '#a8a8b8', margin: 0, lineHeight: 1.6 }}>
                • Opened ANWR to drilling<br/>
                • Fast-tracked Keystone XL pipeline<br/>
                • Expanded offshore drilling in Atlantic and Pacific<br/>
                • Lifted moratorium on coal leases on public lands
              </p>
            </div>
            <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: Bureau of Land Management, Energy Information Administration</div>
          </Card>
        </>}

        {/* ICE */}
        {activeTab === 'ice' && (() => {
          // Filter out invalid entries (no name or empty name)
          const validVictims = iceVictims.filter(v => v.name && v.name.trim() && v.name.trim() !== '');
          
          // Helper for age display
          const formatAge = (age) => (!age || age === 0) ? 'Unknown' : age;
          
          return <>
            <PageHeader title="Federal Agent Violence" subtitle="Shootings and deaths involving ICE, Border Patrol, and CBP" />

            {/* Latest Updates Section — top of tab */}
            <div style={{ marginBottom: '32px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '20px',
                paddingBottom: '12px',
                borderBottom: '2px solid #1e1e28'
              }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#dc2626', animation: 'pulse 2s infinite' }} />
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: 0 }}>Latest Updates</h3>
              </div>
              <CategoryNews breakingNews={data.breakingNews} category="ice" renderSourceLinks={renderSourceLinks} formatRelativeDate={formatRelativeDate} />
            </div>

            {/* Explanation */}
            <Card style={{ marginBottom: '24px', borderLeft: '3px solid #f59e0b' }}>
              <div style={{ fontSize: '13px', color: '#d4d4dc', lineHeight: 1.7 }}>
                This page tracks <strong style={{ color: '#fca5a5' }}>shootings by federal immigration agents</strong> (ICE, Border Patrol, CBP) and <strong style={{ color: '#fca5a5' }}>deaths in immigration detention</strong>. These are separate categories—shootings occur during enforcement operations, while detention deaths occur in custody facilities.
              </div>
            </Card>

            {/* Historical Comparison */}
            <HistoricalComparison category="ice" />

            {/* Shooting Stats */}
            <Card glow="#dc2626" style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#dc2626', animation: 'pulse 2s infinite' }} />
                <span style={{ fontSize: '14px', color: '#fff', fontWeight: '600' }}>Shootings by Federal Agents</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100px, 100%), 1fr))', gap: '12px' }}>
                <div style={{ textAlign: 'center', padding: '16px 12px', background: 'rgba(220,38,38,0.1)', borderRadius: '10px' }}>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#dc2626' }}>{iceStats.totalShootings || 30}+</div>
                  <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Total Shootings</div>
                  <div style={{ fontSize: '9px', color: '#4a4a5a', marginTop: '2px' }}>Since Jan 2025</div>
                </div>
                <div style={{ textAlign: 'center', padding: '16px 12px', background: 'rgba(220,38,38,0.1)', borderRadius: '10px' }}>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#ef4444' }}>{iceStats.shootingDeaths || 8}</div>
                  <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Killed</div>
                  <div style={{ fontSize: '9px', color: '#4a4a5a', marginTop: '2px' }}>By gunfire</div>
                </div>
                <div style={{ textAlign: 'center', padding: '16px 12px', background: 'rgba(220,38,38,0.1)', borderRadius: '10px' }}>
                  <div style={{ fontSize: '32px', fontWeight: '700', color: '#f87171' }}>2</div>
                  <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>US Citizens</div>
                  <div style={{ fontSize: '9px', color: '#4a4a5a', marginTop: '2px' }}>Killed</div>
                </div>
              </div>
              <div style={{ marginTop: '12px', padding: '10px 12px', background: '#0a0a0f', borderRadius: '6px', fontSize: '11px', color: '#8a8a9a' }}>
                <strong style={{ color: '#fca5a5' }}>Note:</strong> 5 US citizens have been shot total (2 killed: Renee Good, Alex Pretti). <a href="https://en.wikipedia.org/wiki/List_of_shootings_by_U.S._immigration_agents_in_the_second_Trump_administration" target="_blank" rel="noopener noreferrer" style={{ color: '#6b6b7b', textDecoration: 'underline' }}>Wikipedia</a>
              </div>
            </Card>

            {/* Detention Deaths */}
            <Card style={{ marginBottom: '24px', borderLeft: '3px solid #f97316' }}>
              <div style={{ fontSize: '14px', color: '#f97316', fontWeight: '600', marginBottom: '12px' }}>Deaths in Immigration Detention</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))', gap: '12px' }}>
                <div style={{ padding: '14px', background: 'rgba(249,115,22,0.1)', borderRadius: '10px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#f97316' }}>{iceStats.detentionDeaths2025 || 32}</div>
                  <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '4px' }}>Deaths in 2025</div>
                </div>
                <div style={{ padding: '14px', background: 'rgba(249,115,22,0.1)', borderRadius: '10px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#fb923c' }}>{iceStats.detentionDeaths2026 || 6}</div>
                  <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '4px' }}>Deaths in 2026</div>
                </div>
              </div>
              <div style={{ fontSize: '11px', color: '#8a8a9a', marginTop: '12px', padding: '10px 12px', background: '#0a0a0f', borderRadius: '6px' }}>
                <strong style={{ color: '#f97316' }}>Note:</strong> 2025 had the highest ICE detention deaths since 2004. December 2025 was the deadliest month on record.
              </div>
            </Card>

            {/* Section header for individual incidents */}
            <div style={{ fontSize: '12px', color: '#6b6b7b', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid #1e1e28' }}>
              <span style={{ color: '#fff', fontWeight: '600' }}>Documented Shooting Incidents</span> • {validVictims.length} on record
            </div>

            {/* Individual Victim Cards */}
            {validVictims.map(v => (
              <Card key={v.id} style={{ marginBottom: '16px', borderLeft: `3px solid ${v.citizenship === 'US Citizen' ? '#dc2626' : '#f97316'}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: 0 }}>{v.name}</h3>
                  {v.citizenship === 'US Citizen' && (
                    <span style={{ fontSize: '9px', fontWeight: '600', padding: '4px 8px', borderRadius: '4px', background: 'rgba(220,38,38,0.2)', color: '#fca5a5' }}>US CITIZEN</span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: '#a8a8b8', marginBottom: '4px' }}>
                  {formatAge(v.age) !== 'Unknown' && <>Age {formatAge(v.age)} • </>}
                  {v.citizenship && v.citizenship !== 'Unknown' && <>{v.citizenship} • </>}
                  {v.agency}
                </div>
                <div style={{ fontSize: '11px', color: '#6b6b7b', marginBottom: '12px' }}>
                  {v.date}{v.location && <> • {v.location}</>}
                </div>
                {v.details && (
                  <div style={{ padding: '14px', background: '#0a0a0f', borderRadius: '10px', marginBottom: '12px', fontSize: '13px', color: '#d4d4dc', lineHeight: 1.6 }}>{v.details}</div>
                )}
                {(v.officialResponse || v.witnessAccount) && (
                  <div style={{ display: 'grid', gridTemplateColumns: v.officialResponse && v.witnessAccount ? 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))' : '1fr', gap: '10px' }}>
                    {v.officialResponse && (
                      <div style={{ padding: '12px', background: 'rgba(220,38,38,0.08)', borderRadius: '8px' }}>
                        <div style={{ fontSize: '9px', color: '#fca5a5', marginBottom: '6px', fontWeight: '600', letterSpacing: '0.5px' }}>OFFICIAL STATEMENT</div>
                        <div style={{ fontSize: '11px', color: '#a8a8b8', lineHeight: 1.5 }}>{v.officialResponse}</div>
                      </div>
                    )}
                    {v.witnessAccount && (
                      <div style={{ padding: '12px', background: 'rgba(34,197,94,0.08)', borderRadius: '8px' }}>
                        <div style={{ fontSize: '9px', color: '#86efac', marginBottom: '6px', fontWeight: '600', letterSpacing: '0.5px' }}>WITNESS / OTHER ACCOUNT</div>
                        <div style={{ fontSize: '11px', color: '#a8a8b8', lineHeight: 1.5 }}>{v.witnessAccount}</div>
                      </div>
                    )}
                  </div>
                )}
                {v.sources && v.sources.length > 0 && (
                  <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: {renderSourceLinks(v.sources)}</div>
                )}
              </Card>
            ))}

            {/* Data source */}
            <div style={{ fontSize: '10px', color: '#4a4a5a', textAlign: 'center', marginTop: '24px' }}>
              Data compiled from: <a href="https://en.wikipedia.org/wiki/List_of_killings_by_law_enforcement_officers_in_the_United_States" target="_blank" rel="noopener noreferrer" style={{ color: '#6b6b7b', textDecoration: 'underline' }}>Wikipedia</a> • <a href="https://www.npr.org/sections/immigration" target="_blank" rel="noopener noreferrer" style={{ color: '#6b6b7b', textDecoration: 'underline' }}>NPR</a> • <a href="https://www.aclu.org/news/immigrants-rights" target="_blank" rel="noopener noreferrer" style={{ color: '#6b6b7b', textDecoration: 'underline' }}>ACLU</a> • <a href="https://www.vera.org/topics/immigration" target="_blank" rel="noopener noreferrer" style={{ color: '#6b6b7b', textDecoration: 'underline' }}>Vera Institute</a>
            </div>
          </>;
        })()}

        {/* SOURCES */}
        {activeTab === 'sources' && <>
          <PageHeader title="Sources & Methodology" subtitle="Every claim backed by reputable sources" />
          {[
            { c: 'National Debt', s: 'Treasury Debt to the Penny • JEC • CBO', icon: '📊' },
            { c: 'Wealth', s: 'Forbes • Bloomberg • NYT', icon: '💰' },
            { c: 'Golf/Travel', s: 'GAO 2019 Report • CREW • HuffPost', icon: '⛳' },
            { c: 'Self-Dealing', s: 'CREW • American Oversight • House Oversight', icon: '🏨' },
            { c: 'Promises', s: 'PolitiFact • CNN • NPR • BLS • EIA', icon: '📝' },
            { c: 'Constitution', s: 'Federal Court Records • ACLU • AP News • DOJ', icon: '📜' },
            { c: 'ICE/CBP', s: 'NPR • AP • ACLU • Vera Institute', icon: '⚠️' },
            { c: 'Lawsuits', s: 'Just Security • SCOTUSblog • Democracy Docket', icon: '⚖️' },
            { c: 'Environment', s: 'NRDC • Columbia Law • Harvard Law • EPA', icon: '🌿' },
            { c: 'Israel Aid', s: 'Brown University • State Dept • Quincy', icon: '🌍' }
          ].map((x, i) => (
            <Card key={i} style={{ marginBottom: '12px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                <span style={{ fontSize: '16px' }}>{x.icon}</span>
                <h3 style={{ fontSize: '14px', color: '#fff', margin: 0, fontWeight: '600' }}>{x.c}</h3>
              </div>
              <div style={{ fontSize: '12px', color: '#6b6b7b', paddingLeft: '26px' }}>{x.s}</div>
            </Card>
          ))}
          
          <Card style={{ marginTop: '24px' }}>
            <h3 style={{ fontSize: '14px', color: '#fff', margin: '0 0 12px 0', fontWeight: '600' }}>What This Is</h3>
            <p style={{ fontSize: '13px', color: '#6b6b7b', margin: 0, lineHeight: 1.7 }}>
              This is an <strong style={{ color: '#a8a8b8' }}>accountability tracker</strong>, not a neutral scorecard. It documents concerns about the current administration — broken promises, legal challenges, enrichment, and constitutional issues. It does not track achievements or kept promises. That editorial choice is intentional and transparent.
            </p>
          </Card>

          <Card style={{ marginTop: '12px' }}>
            <h3 style={{ fontSize: '14px', color: '#fff', margin: '0 0 12px 0', fontWeight: '600' }}>Source Standards</h3>
            <div style={{ display: 'grid', gap: '10px' }}>
              <div style={{ padding: '10px 14px', background: '#0a0a0f', borderRadius: '8px', borderLeft: '3px solid #22c55e' }}>
                <div style={{ fontSize: '12px', color: '#22c55e', fontWeight: '600', marginBottom: '4px' }}>Primary Sources (Preferred)</div>
                <div style={{ fontSize: '11px', color: '#6b6b7b', lineHeight: 1.5 }}>Government records, court filings, Treasury fiscal data, BLS statistics, congressional testimony, official vote tallies</div>
              </div>
              <div style={{ padding: '10px 14px', background: '#0a0a0f', borderRadius: '8px', borderLeft: '3px solid #3b82f6' }}>
                <div style={{ fontSize: '12px', color: '#3b82f6', fontWeight: '600', marginBottom: '4px' }}>Mainstream Reporting</div>
                <div style={{ fontSize: '11px', color: '#6b6b7b', lineHeight: 1.5 }}>AP, Reuters, NPR, PBS, major newspapers (NYT, WaPo, WSJ), network news. Used when primary documents aren't publicly accessible.</div>
              </div>
              <div style={{ padding: '10px 14px', background: '#0a0a0f', borderRadius: '8px', borderLeft: '3px solid #a855f7' }}>
                <div style={{ fontSize: '12px', color: '#a855f7', fontWeight: '600', marginBottom: '4px' }}>Specialized Trackers</div>
                <div style={{ fontSize: '11px', color: '#6b6b7b', lineHeight: 1.5 }}>Just Security (lawsuits), NRDC/Columbia Law (environment), CREW (ethics), PolitiFact (promises), Forbes (wealth). These organizations maintain dedicated, continuously updated databases.</div>
              </div>
              <div style={{ padding: '10px 14px', background: '#0a0a0f', borderRadius: '8px', borderLeft: '3px solid #ef4444' }}>
                <div style={{ fontSize: '12px', color: '#ef4444', fontWeight: '600', marginBottom: '4px' }}>Excluded Sources</div>
                <div style={{ fontSize: '11px', color: '#6b6b7b', lineHeight: 1.5 }}>RT, Sputnik, OANN, Infowars, Newsmax, Breitbart, Epoch Times, Gateway Pundit, and ZeroHedge are automatically filtered from breaking news updates.</div>
              </div>
            </div>
          </Card>

          <Card style={{ marginTop: '12px' }}>
            <h3 style={{ fontSize: '14px', color: '#fff', margin: '0 0 12px 0', fontWeight: '600' }}>How Data Updates</h3>
            <div style={{ display: 'grid', gap: '8px', fontSize: '12px', color: '#6b6b7b', lineHeight: 1.6 }}>
              <div><strong style={{ color: '#a8a8b8' }}>Breaking news:</strong> RSS feeds from major outlets are monitored hourly. New stories appear automatically without page refresh.</div>
              <div><strong style={{ color: '#a8a8b8' }}>National debt:</strong> Calculated in real time from Treasury baseline data and daily growth rate ($92,912/second based on JEC figures).</div>
              <div><strong style={{ color: '#a8a8b8' }}>Static figures</strong> (lawsuit counts, rollback totals, wealth estimates): Updated periodically by referencing the specialized trackers listed above. These may lag behind the absolute latest numbers by days or weeks.</div>
              <div><strong style={{ color: '#a8a8b8' }}>Wealth figures:</strong> Based on Forbes estimates and are volatile — crypto and stock holdings can swing billions in a single day. Treat as approximate.</div>
            </div>
          </Card>

          <Card style={{ marginTop: '12px' }}>
            <h3 style={{ fontSize: '14px', color: '#fff', margin: '0 0 12px 0', fontWeight: '600' }}>Known Limitations</h3>
            <div style={{ display: 'grid', gap: '6px', fontSize: '12px', color: '#6b6b7b', lineHeight: 1.6 }}>
              <div>• This tracker documents concerns only — it is not a comprehensive record of all presidential actions</div>
              <div>• Static numbers (lawsuits, rollbacks) may lag behind primary trackers by days or weeks</div>
              <div>• Net worth figures are estimates and fluctuate with market conditions</div>
              <div>• Golf trip costs use GAO first-term estimates — actual second-term costs may differ</div>
              <div>• "Broken promise" determinations involve editorial judgment about what constitutes fulfillment</div>
            </div>
          </Card>

          <Card style={{ marginTop: '12px', borderLeft: '3px solid #3b82f6' }}>
            <div style={{ fontSize: '13px', color: '#6b6b7b', lineHeight: 1.7 }}>
              <strong style={{ color: '#3b82f6' }}>Found an error?</strong> This project is open source. File an issue or submit a correction at{' '}
              <a href="https://github.com/mtsilverstein/Trump-Accountability-Tracker" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>GitHub</a>.
              All corrections must include a source.
            </div>
          </Card>
        </>}

        {/* TAKE ACTION */}
        {activeTab === 'action' && <>
          <PageHeader title="Take Action" subtitle="Your voice matters. Enter your zip code to find your representatives and get call scripts." />
          
          <RepLookup breakingNews={data.breakingNews} />
          
          {/* Fallback manual links */}
          <Card style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '14px', color: '#3b82f6', margin: '0 0 16px 0', fontWeight: '600' }}>Or Find Representatives Manually</h3>
            <div style={{ display: 'grid', gap: '10px' }}>
              <a href="https://www.house.gov/representatives/find-your-representative" target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', padding: '14px 16px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '8px', textDecoration: 'none' }}>
                <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: '600' }}>House of Representatives</div>
                <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '4px' }}>house.gov</div>
              </a>
              <a href="https://www.senate.gov/senators/senators-contact.htm" target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', padding: '14px 16px', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '8px', textDecoration: 'none' }}>
                <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: '600' }}>U.S. Senate</div>
                <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '4px' }}>senate.gov</div>
              </a>
            </div>
          </Card>

          <Card style={{ marginBottom: '24px' }}>
            <h3 style={{ fontSize: '14px', color: '#a855f7', margin: '0 0 16px 0', fontWeight: '600' }}>Organizations Fighting for Accountability</h3>
            
            <a 
              href="https://www.aclu.org/take-action" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ display: 'block', padding: '14px 16px', background: 'rgba(147,51,234,0.1)', border: '1px solid rgba(147,51,234,0.3)', borderRadius: '8px', marginBottom: '10px', textDecoration: 'none' }}
            >
              <div style={{ fontSize: '13px', color: '#a855f7', fontWeight: '600' }}>ACLU Action Center</div>
              <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '4px' }}>Defending civil liberties and constitutional rights</div>
            </a>

            <a 
              href="https://www.citizensforethics.org/" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ display: 'block', padding: '14px 16px', background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: '8px', marginBottom: '10px', textDecoration: 'none' }}
            >
              <div style={{ fontSize: '13px', color: '#eab308', fontWeight: '600' }}>CREW (Citizens for Ethics)</div>
              <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '4px' }}>Tracking corruption and conflicts of interest</div>
            </a>

            <a 
              href="https://www.commoncause.org/" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ display: 'block', padding: '14px 16px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: '8px', textDecoration: 'none' }}
            >
              <div style={{ fontSize: '13px', color: '#22c55e', fontWeight: '600' }}>Common Cause</div>
              <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '4px' }}>Protecting democracy and voting rights</div>
            </a>
          </Card>

          <Card style={{ borderLeft: '3px solid #ef4444', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '14px', color: '#ef4444', margin: '0 0 12px 0', fontWeight: '600' }}>Tips for Effective Calls</h3>
            <ul style={{ fontSize: '13px', color: '#a8a8b8', margin: 0, paddingLeft: '20px', lineHeight: 1.8 }}>
              <li>Be polite but firm — staffers track constituent sentiment</li>
              <li>State your name and that you're a constituent</li>
              <li>Focus on one specific issue per call</li>
              <li>Ask for the representative's position on the issue</li>
              <li>Calls are more impactful than emails</li>
            </ul>
          </Card>

          {/* Spread the Word — Embed Widgets */}
          <div style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '18px', fontWeight: '700', color: EDITORIAL.textPrimary, marginBottom: '16px', paddingBottom: '12px', borderBottom: `1px solid ${EDITORIAL.divider}` }}>Spread the Word</div>
          {(() => {
            const widgets = [
              { id: 'debt', name: 'National Debt Counter', icon: '💰' },
              { id: 'approval', name: 'Approval Rating', icon: '📊' },
              { id: 'lawsuits', name: 'Legal Challenges', icon: '⚖️' },
              { id: 'ice', name: 'ICE Shootings', icon: '⚠️' },
              { id: 'environment', name: 'Environmental Rollbacks', icon: '🌍' },
              { id: 'money', name: 'Wealth Gained', icon: '💵' },
              { id: 'headline', name: 'Latest Breaking News', icon: '📰' }
            ];
            const embedCode = `<script src="https://trumpaccountabilitytracker.vercel.app/api/embed?widget=${selectedWidget}&theme=${selectedTheme}"></script>`;
            return (
              <Card style={{ borderLeft: '3px solid #3b82f6' }}>
                <h3 style={{ fontFamily: EDITORIAL.fontSerif, fontSize: '16px', color: '#3b82f6', fontWeight: '700', marginBottom: '12px' }}>
                  Embed Widget on Your Site
                </h3>
                <p style={{ fontSize: '13px', color: EDITORIAL.textSecondary, marginBottom: '16px', lineHeight: 1.6 }}>
                  Add live tracker widgets to your website with a single line of code.
                </p>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', color: EDITORIAL.textTertiary, marginBottom: '8px', fontWeight: '600', letterSpacing: '0.5px' }}>SELECT WIDGET</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(140px, 100%), 1fr))', gap: '8px' }}>
                    {widgets.map(w => (
                      <button key={w.id} onClick={() => setSelectedWidget(w.id)}
                        style={{ padding: '10px', background: selectedWidget === w.id ? 'rgba(59,130,246,0.15)' : EDITORIAL.cardBg, border: `1px solid ${selectedWidget === w.id ? '#3b82f6' : EDITORIAL.cardBorder}`, borderRadius: '8px', color: selectedWidget === w.id ? '#3b82f6' : EDITORIAL.textSecondary, fontSize: '11px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}>
                        <div style={{ fontSize: '18px', marginBottom: '4px' }}>{w.icon}</div>
                        {w.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', color: EDITORIAL.textTertiary, marginBottom: '8px', fontWeight: '600', letterSpacing: '0.5px' }}>THEME</div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {[{ id: 'dark', label: 'Dark', bg: '#0a0a0f' }, { id: 'light', label: 'Light', bg: '#ffffff' }].map(t => (
                      <button key={t.id} onClick={() => setSelectedTheme(t.id)}
                        style={{ flex: 1, padding: '8px 16px', background: selectedTheme === t.id ? 'rgba(59,130,246,0.15)' : EDITORIAL.cardBg, border: `1px solid ${selectedTheme === t.id ? '#3b82f6' : EDITORIAL.cardBorder}`, borderRadius: '8px', color: selectedTheme === t.id ? '#3b82f6' : EDITORIAL.textSecondary, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit' }}>
                        <div style={{ width: '20px', height: '20px', borderRadius: '4px', background: t.bg, border: `1px solid ${EDITORIAL.cardBorder}`, marginBottom: '4px', margin: '0 auto' }} />
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: EDITORIAL.textTertiary, marginBottom: '8px', fontWeight: '600', letterSpacing: '0.5px' }}>EMBED CODE</div>
                  <div style={{ position: 'relative', padding: '12px', background: '#0a0a0f', borderRadius: '8px', border: `1px solid ${EDITORIAL.cardBorder}`, fontFamily: EDITORIAL.fontMono, fontSize: '11px', color: '#22c55e', overflowX: 'auto', cursor: 'pointer' }}
                    onClick={(e) => {
                      navigator.clipboard.writeText(embedCode);
                      const el = e.currentTarget;
                      const orig = el.style.borderColor;
                      el.style.borderColor = '#22c55e';
                      const toast = document.createElement('div');
                      toast.textContent = '✓ Copied to clipboard';
                      toast.style.cssText = 'position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#22c55e;color:#000;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;z-index:9999;animation:fadeOut 2s forwards';
                      document.body.appendChild(toast);
                      setTimeout(() => { toast.remove(); el.style.borderColor = orig; }, 2000);
                    }}
                    title="Click to copy"
                  >
                    {embedCode}
                  </div>
                  <div style={{ fontSize: '10px', color: EDITORIAL.textMuted, marginTop: '8px', textAlign: 'center' }}>
                    Click to copy • Widget updates automatically
                  </div>
                </div>
              </Card>
            );
          })()}
        </>}

        {/* POLLS */}
        {activeTab === 'polls' && <>

          <PageHeader title="Approval Ratings" subtitle="Public opinion tracking from major pollsters" />

          {/* Latest Updates Section */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '2px solid #1e1e28'
            }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', animation: 'pulse 2s infinite' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: 0 }}>Latest Updates</h3>
            </div>
            <CategoryNews breakingNews={data.breakingNews} category="poll" renderSourceLinks={renderSourceLinks} formatRelativeDate={formatRelativeDate} />
          </div>

          {/* Current Approval - Dynamic from data.polls */}
          {(() => {
            const polls = data.polls || {};
            const approve = polls.overall?.approve || 37;
            const disapprove = polls.overall?.disapprove || 57;
            const pollSource = polls.overall?.source || 'Quinnipiac';
            const pollDate = polls.overall?.date || 'Mar 2026';
            const netApproval = polls.netApproval || -13.9;
            return (
          <Card glow="#ef4444" style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '10px', letterSpacing: '2px', color: '#4a4a5a', marginBottom: '16px', fontWeight: '600', textAlign: 'center' }}>CURRENT APPROVAL ({pollDate.toUpperCase()})</div>
            
            {/* Approve / Disapprove — large numbers + full-width stacked bar */}
            <div style={{ marginBottom: '20px' }}>
              {/* Big numbers side by side */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '12px' }}>
                <div>
                  <div style={{ fontSize: 'clamp(40px, 8vw, 56px)', fontWeight: '800', color: '#22c55e', lineHeight: 1 }}>{approve}%</div>
                  <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '4px' }}>Approve</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 'clamp(40px, 8vw, 56px)', fontWeight: '800', color: '#ef4444', lineHeight: 1 }}>{disapprove}%</div>
                  <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '4px' }}>Disapprove</div>
                </div>
              </div>
              {/* Full-width stacked bar */}
              <div style={{ display: 'flex', height: '36px', borderRadius: '8px', overflow: 'hidden', background: '#1a1a22' }}>
                <div style={{ width: `${approve}%`, background: 'linear-gradient(90deg, #22c55e, #16a34a)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'width 0.6s ease' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#fff' }}>{approve}%</span>
                </div>
                <div style={{ width: `${disapprove}%`, background: 'linear-gradient(90deg, #ef4444, #dc2626)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'width 0.6s ease' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: '#fff' }}>{disapprove}%</span>
                </div>
                {(100 - approve - disapprove) > 0 && (
                  <div style={{ flex: 1, background: '#1a1a22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: '10px', color: '#4a4a5a' }}>{100 - approve - disapprove}%</span>
                  </div>
                )}
              </div>
              <div style={{ fontSize: '10px', color: '#4a4a5a', textAlign: 'right', marginTop: '6px' }}>{pollSource} {pollDate}</div>
            </div>

            <div style={{ padding: '14px 16px', background: '#0a0a0f', borderRadius: '10px', marginBottom: '16px' }}>
              <div style={{ fontSize: '12px', color: '#8a8a9a', marginBottom: '8px' }}>NET APPROVAL</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ fontSize: '28px', fontWeight: '700', color: netApproval < 0 ? '#ef4444' : '#22c55e' }}>{netApproval > 0 ? '+' : ''}{netApproval}</div>
                <div style={{ fontSize: '12px', color: '#6b6b7b' }}>
                  Approval minus disapproval<br/>
                  <span style={{ color: netApproval < 0 ? '#ef4444' : '#22c55e' }}>{netApproval < -10 ? 'Deeply underwater' : netApproval < 0 ? 'Underwater' : 'Positive'}</span>
                </div>
              </div>
            </div>
            
            <a href="https://www.natesilver.net/p/trump-approval-ratings-nate-silver-bulletin" target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px', color: '#4a4a5a', textDecoration: 'underline' }}>Source: Silver Bulletin →</a>
          </Card>
            );
          })()}

          {/* Poll Comparison — uses dynamic data with hardcoded fallback */}
          <Card style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '14px', color: '#fff', fontWeight: '600', marginBottom: '16px' }}>Recent Poll Results</div>
            
            {((data.polls?.recentPolls || []).filter(p =>
              p.pollster && p.approve > 0 && p.disapprove > 0 && (p.approve + p.disapprove) >= 70 && (p.approve + p.disapprove) <= 110
            ).length > 0
              ? data.polls.recentPolls.filter(p =>
                  p.pollster && p.approve > 0 && p.disapprove > 0 && (p.approve + p.disapprove) >= 70 && (p.approve + p.disapprove) <= 110
                )
              : [
              { pollster: 'Quinnipiac', approve: 37, disapprove: 57, date: 'Mar 6-8' },
              { pollster: 'Economist/YouGov', approve: 40, disapprove: 55, date: 'Mar 6-9' },
              { pollster: 'NPR/PBS/Marist', approve: 38, disapprove: 57, date: 'Mar 2-4' },
              { pollster: 'NBC News', approve: 44, disapprove: 54, date: 'Early Mar' },
              { pollster: 'CNN/SSRS', approve: 36, disapprove: 60, date: 'Feb 17-20' },
              { pollster: 'ABC/WaPo/Ipsos', approve: 39, disapprove: 60, date: 'Feb 12-17' },
            ]).map((poll, i) => {
              const total = (poll.approve || 0) + (poll.disapprove || 0);
              const undecided = Math.max(0, 100 - total);
              return (
              <div key={i} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '12px', color: '#a8a8b8', fontWeight: '500' }}>{poll.pollster}</span>
                  <span style={{ fontSize: '10px', color: '#4a4a5a' }}>{poll.date}</span>
                </div>
                <div style={{ display: 'flex', height: '24px', borderRadius: '4px', overflow: 'hidden', background: '#0a0a0f' }}>
                  <div style={{ width: `${poll.approve}%`, background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {poll.approve >= 12 && <span style={{ fontSize: '10px', fontWeight: '600', color: '#fff' }}>{poll.approve}%</span>}
                  </div>
                  <div style={{ width: `${poll.disapprove}%`, background: 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {poll.disapprove >= 12 && <span style={{ fontSize: '10px', fontWeight: '600', color: '#fff' }}>{poll.disapprove}%</span>}
                  </div>
                  {undecided > 0 && (
                    <div style={{ width: `${undecided}%`, background: '#1e1e28', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {undecided >= 8 && <span style={{ fontSize: '9px', fontWeight: '500', color: '#6b6b7b' }}>{undecided}%</span>}
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </Card>

          {/* Key Findings */}
          <Card style={{ marginBottom: '24px', borderLeft: '3px solid #f97316' }}>
            <div style={{ fontSize: '14px', color: '#f97316', fontWeight: '600', marginBottom: '16px' }}>Key Findings</div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '12px' }}>
              <div style={{ padding: '14px', background: '#0a0a0f', borderRadius: '8px' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#ef4444', marginBottom: '4px' }}>64%</div>
                <div style={{ fontSize: '12px', color: '#a8a8b8' }}>Disapprove of Trump's handling of <strong>tariffs</strong></div>
                <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '4px' }}>ABC/WaPo/Ipsos, Feb 2026</div>
              </div>

              <div style={{ padding: '14px', background: '#0a0a0f', borderRadius: '8px' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#f97316', marginBottom: '4px' }}>57%</div>
                <div style={{ fontSize: '12px', color: '#a8a8b8' }}>Overall <strong>disapprove</strong> of Trump's job performance</div>
                <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '4px' }}>Quinnipiac, Mar 2026</div>
              </div>

              <div style={{ padding: '14px', background: '#0a0a0f', borderRadius: '8px' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#eab308', marginBottom: '4px' }}>62%</div>
                <div style={{ fontSize: '12px', color: '#a8a8b8' }}>Oppose ICE <strong>enforcement tactics</strong></div>
                <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '4px' }}>ABC/WaPo/Ipsos, Feb 2026</div>
              </div>

              <div style={{ padding: '14px', background: '#0a0a0f', borderRadius: '8px' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6', marginBottom: '4px' }}>28%</div>
                <div style={{ fontSize: '12px', color: '#a8a8b8' }}>Independent approval — <strong>lowest of either term</strong></div>
                <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '4px' }}>Quinnipiac, Mar 2026</div>
              </div>

              <div style={{ padding: '14px', background: '#0a0a0f', borderRadius: '8px' }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#a855f7', marginBottom: '4px' }}>-13.9</div>
                <div style={{ fontSize: '12px', color: '#a8a8b8' }}>Net approval — <strong>second term low</strong></div>
                <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '4px' }}>Silver Bulletin average, Mar 2026</div>
              </div>
            </div>
          </Card>

          {/* Immigration Approval */}
          <Card style={{ marginBottom: '24px', borderLeft: '3px solid #dc2626' }}>
            <div style={{ fontSize: '14px', color: '#dc2626', fontWeight: '600', marginBottom: '12px' }}>Immigration Approval (Record Low)</div>
            <div style={{ fontSize: '36px', fontWeight: '700', color: '#dc2626', marginBottom: '8px' }}>40%</div>
            <div style={{ fontSize: '12px', color: '#6b6b7b', marginBottom: '16px' }}>Down from 50% in Feb 2025 — record low for second term</div>

            <div style={{ padding: '12px 14px', background: 'rgba(220,38,38,0.08)', borderRadius: '8px', fontSize: '12px', color: '#fca5a5', lineHeight: 1.6 }}>
              58% disapprove of Trump's handling of immigration. ICE enforcement tactics opposed by 62%. Immigration was Trump's strongest issue in the 2024 election.
            </div>
            <a href="https://www.ipsos.com/en-us/ABC-News-Washington-Post-Ipsos-Poll-February-2026" target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: '12px', fontSize: '11px', color: '#4a4a5a', textDecoration: 'underline' }}>Source: ABC/WaPo/Ipsos, Feb 2026 →</a>
          </Card>

          {/* Historical Context */}
          <Card>
            <div style={{ fontSize: '14px', color: '#a855f7', fontWeight: '600', marginBottom: '16px' }}>Historical Context</div>
            <div style={{ fontSize: '13px', color: '#a8a8b8', lineHeight: 1.7 }}>
              <p style={{ margin: '0 0 12px 0' }}>Trump's current approval (~39-44%) is comparable to his first term at this point (-16.4 net approval), but notably lower than other second-term presidents one year in:</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(90px, 100%), 1fr))', gap: '10px', marginTop: '12px' }}>
                <div style={{ textAlign: 'center', padding: '12px', background: '#0a0a0f', borderRadius: '8px' }}>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#ef4444' }}>-13.9</div>
                  <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Trump (2026)</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: '#0a0a0f', borderRadius: '8px' }}>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>-8.5</div>
                  <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Obama (2014)</div>
                </div>
                <div style={{ textAlign: 'center', padding: '12px', background: '#0a0a0f', borderRadius: '8px' }}>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#22c55e' }}>-10.4</div>
                  <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Bush (2006)</div>
                </div>
              </div>
            </div>
            <a href="https://www.nbcnews.com/politics/trump-administration/trumps-approval-rating-takes-hit-first-year-back-office-rcna253192" target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginTop: '16px', fontSize: '11px', color: '#4a4a5a', textDecoration: 'underline' }}>Source: NBC News →</a>
          </Card>
        </>}

        {/* FOREIGN POLICY */}
        {activeTab === 'foreign-policy' && <>
          <PageHeader title="Foreign Policy" subtitle="Tracking diplomatic actions, alliances, and international consequences" />

          {/* Latest Updates Section */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '2px solid #1e1e28'
            }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#6366f1', animation: 'pulse 2s infinite' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: 0 }}>Latest Updates</h3>
            </div>
            <CategoryNews breakingNews={data.breakingNews} category="foreign-policy" renderSourceLinks={renderSourceLinks} formatRelativeDate={formatRelativeDate} />
          </div>

          {/* Key Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: '12px', marginBottom: '24px' }}>
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(99,102,241,0.08)', borderRadius: '12px', border: '1px solid rgba(99,102,241,0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#6366f1' }}>~27%</div>
              <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Effective US Tariff Rate</div>
              <div style={{ fontSize: '9px', color: '#4a4a5a', marginTop: '2px' }}>Up from 2.5% pre-Trump</div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(239,68,68,0.08)', borderRadius: '12px', border: '1px solid rgba(239,68,68,0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#ef4444' }}>145%</div>
              <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Peak Tariff on China</div>
              <div style={{ fontSize: '9px', color: '#4a4a5a', marginTop: '2px' }}>Apr 2025</div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(245,158,11,0.08)', borderRadius: '12px', border: '1px solid rgba(245,158,11,0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>0</div>
              <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>International Orgs Joined</div>
              <div style={{ fontSize: '9px', color: '#4a4a5a', marginTop: '2px' }}>Exited WHO, Paris Agreement</div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(107,107,123,0.1)', borderRadius: '12px', border: '1px solid rgba(107,107,123,0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#6b6b7b' }}>370+</div>
              <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Days Ukraine War Continues</div>
              <div style={{ fontSize: '9px', color: '#4a4a5a', marginTop: '2px' }}>Promised "24 hours"</div>
            </div>
          </div>

          {/* Key Context Cards */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #6366f1' }}>
            <div style={{ fontSize: '14px', color: '#6366f1', fontWeight: '600', marginBottom: '16px' }}>Trade War Impact</div>
            <div style={{ display: 'grid', gap: '10px' }}>
              {[
                { title: 'SCOTUS Ruling', detail: 'Supreme Court ruled IEEPA tariffs illegal (Feb 2026). Trump pivoted to Section 122 with 10% global tariff for 150 days.', status: 'Ruled illegal' },
                { title: 'EU Framework', detail: 'US-EU deal set duties at 15% — still 6x pre-Trump levels. Allies treated as adversaries in trade negotiations.', status: 'Partial deal' },
                { title: 'Consumer Impact', detail: 'Goldman Sachs: consumers bear 50%+ of tariff costs. Average household paying $2,100+ more annually.', status: 'Ongoing' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '14px', background: '#0a0a0f', borderRadius: '8px', borderLeft: '3px solid #6366f1' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontSize: '13px', color: '#fff', fontWeight: '600' }}>{item.title}</span>
                    <span style={{ fontSize: '9px', padding: '3px 8px', borderRadius: '4px', background: 'rgba(99,102,241,0.2)', color: '#6366f1' }}>{item.status}</span>
                  </div>
                  <p style={{ fontSize: '12px', color: '#a8a8b8', margin: 0, lineHeight: 1.5 }}>{item.detail}</p>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: Tax Foundation, PIIE, Goldman Sachs, Supreme Court</div>
          </Card>

          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #ef4444' }}>
            <div style={{ fontSize: '14px', color: '#ef4444', fontWeight: '600', marginBottom: '16px' }}>Alliance & Diplomacy Tracker</div>
            <div style={{ display: 'grid', gap: '10px' }}>
              {[
                { action: 'Withdrew from WHO', date: 'Jan 2025 (effective Jan 2026)', color: '#ef4444' },
                { action: 'Withdrew from Paris Climate Agreement', date: 'Jan 2025', color: '#ef4444' },
                { action: 'Threatened military force over Greenland & Panama', date: 'Jan 2025', color: '#f59e0b' },
                { action: 'Zelenskyy ejected from White House after confrontation', date: 'Feb 2025', color: '#f59e0b' },
                { action: 'Cut US participation in NATO advisory groups', date: 'Jan 2026', color: '#ef4444' },
                { action: 'US-Israel strikes on Iran killed Supreme Leader', date: 'Feb 2026', color: '#dc2626' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '10px 14px', background: '#0a0a0f', borderRadius: '6px', borderLeft: `3px solid ${item.color}`, fontSize: '12px', color: '#a8a8b8' }}>
                  <div style={{ fontWeight: '600', color: '#d4d4dc' }}>{item.action}</div>
                  <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '2px' }}>{item.date}</div>
                </div>
              ))}
            </div>
          </Card>
        </>}

        {/* HEALTHCARE & EDUCATION */}
        {activeTab === 'healthcare' && <>
          <PageHeader title="Healthcare & Education" subtitle="Tracking policy changes affecting public health and education systems" />

          {/* Latest Updates Section */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '2px solid #1e1e28'
            }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#14b8a6', animation: 'pulse 2s infinite' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: 0 }}>Latest Updates</h3>
            </div>
            <CategoryNews breakingNews={data.breakingNews} category="healthcare-education" renderSourceLinks={renderSourceLinks} formatRelativeDate={formatRelativeDate} />
          </div>

          {/* Key Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: '12px', marginBottom: '24px' }}>
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(239,68,68,0.08)', borderRadius: '12px', border: '1px solid rgba(239,68,68,0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#ef4444' }}>11.8M</div>
              <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Projected to Lose Insurance</div>
              <div style={{ fontSize: '9px', color: '#4a4a5a', marginTop: '2px' }}>CBO estimate by 2034</div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(245,158,11,0.08)', borderRadius: '12px', border: '1px solid rgba(245,158,11,0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>114%</div>
              <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>ACA Premium Spike</div>
              <div style={{ fontSize: '9px', color: '#4a4a5a', marginTop: '2px' }}>Subsidies expired</div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(168,85,247,0.08)', borderRadius: '12px', border: '1px solid rgba(168,85,247,0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#a855f7' }}>-25%</div>
              <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>HHS Budget Cut</div>
              <div style={{ fontSize: '9px', color: '#4a4a5a', marginTop: '2px' }}>$127B → $95B</div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(20,184,166,0.08)', borderRadius: '12px', border: '1px solid rgba(20,184,166,0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#14b8a6' }}>3,564</div>
              <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Measles Cases</div>
              <div style={{ fontSize: '9px', color: '#4a4a5a', marginTop: '2px' }}>Highest since 1994</div>
            </div>
          </div>

          {/* RFK Jr. HHS Actions */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #14b8a6' }}>
            <div style={{ fontSize: '14px', color: '#14b8a6', fontWeight: '600', marginBottom: '16px' }}>RFK Jr.'s HHS Overhaul</div>
            <div style={{ display: 'grid', gap: '10px' }}>
              {[
                { action: 'Cut childhood vaccine schedule from 17 to 11 recommended shots', date: 'Jan 2026', color: '#ef4444' },
                { action: 'Directed CDC to stop recommending fluoride in drinking water', date: 'Apr 2025', color: '#f59e0b' },
                { action: 'Changed COVID vaccine from universal recommendation to individual choice', date: 'Oct 2025', color: '#f59e0b' },
                { action: 'Proposed consolidating NIH from 27 institutes to 8', date: 'Dec 2025', color: '#a855f7' },
                { action: 'Announced 20% HHS workforce reduction', date: 'Feb 2025', color: '#ef4444' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '10px 14px', background: '#0a0a0f', borderRadius: '6px', borderLeft: `3px solid ${item.color}`, fontSize: '12px', color: '#a8a8b8' }}>
                  <div style={{ fontWeight: '600', color: '#d4d4dc' }}>{item.action}</div>
                  <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '2px' }}>{item.date}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: STAT, PBS, Axios, KFF, Science</div>
          </Card>

          {/* Education Actions */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #a855f7' }}>
            <div style={{ fontSize: '14px', color: '#a855f7', fontWeight: '600', marginBottom: '16px' }}>Education Policy Changes</div>
            <div style={{ display: 'grid', gap: '10px' }}>
              {[
                { action: 'Signed executive order to dismantle Department of Education', date: 'Mar 2025', color: '#ef4444' },
                { action: 'Froze $2.3B in Harvard funding; Columbia forced into $221M settlement', date: 'May 2025', color: '#f59e0b' },
                { action: 'Rolled back Title IX protections for transgender students', date: 'Jan 2025', color: '#ef4444' },
                { action: 'Eliminated all federal DEI programs and training', date: 'Jan 2025', color: '#f59e0b' },
                { action: 'Resumed student loan collections after 5-year pause', date: 'May 2025', color: '#a855f7' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '10px 14px', background: '#0a0a0f', borderRadius: '6px', borderLeft: `3px solid ${item.color}`, fontSize: '12px', color: '#a8a8b8' }}>
                  <div style={{ fontWeight: '600', color: '#d4d4dc' }}>{item.action}</div>
                  <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '2px' }}>{item.date}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: 19th News, Brookings, Harvard Magazine, Higher Ed Dive</div>
          </Card>
        </>}

        {/* EPSTEIN FILES */}
        {activeTab === 'epstein' && <>

          <PageHeader title="Epstein Files" subtitle="What the released documents reveal about Trump's connections" />

          {/* Latest Updates Section */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '2px solid #1e1e28'
            }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f59e0b', animation: 'pulse 2s infinite' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#fff', margin: 0 }}>Latest Updates</h3>
            </div>
            <CategoryNews breakingNews={data.breakingNews} category="epstein" renderSourceLinks={renderSourceLinks} formatRelativeDate={formatRelativeDate} />
          </div>

          {/* Key Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: '12px', marginBottom: '24px' }}>
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(245,158,11,0.08)', borderRadius: '12px', border: '1px solid rgba(245,158,11,0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#f59e0b' }}>8</div>
              <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Flights on Epstein's Jet</div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(239,68,68,0.08)', borderRadius: '12px', border: '1px solid rgba(239,68,68,0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#ef4444' }}>4</div>
              <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Flights with Maxwell Aboard</div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(168,85,247,0.08)', borderRadius: '12px', border: '1px solid rgba(168,85,247,0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#a855f7' }}>3M+</div>
              <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Pages Released Jan 30</div>
            </div>
            <div style={{ textAlign: 'center', padding: '16px', background: 'rgba(107,107,123,0.1)', borderRadius: '12px', border: '1px solid rgba(107,107,123,0.2)' }}>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#6b6b7b' }}>2.5M</div>
              <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '4px' }}>Pages Still Withheld</div>
            </div>
          </div>

          {/* Flight Log Details */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #f59e0b' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '10px', fontWeight: '600', padding: '4px 10px', borderRadius: '4px', background: 'rgba(245,158,11,0.2)', color: '#f59e0b' }}>FLIGHT LOGS</span>
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', margin: '0 0 12px 0' }}>Trump Flew on Epstein's Jet "Many More Times" Than Known</h3>
            <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '12px' }}>
              <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, lineHeight: 1.7 }}>
                According to a January 2020 email from an SDNY prosecutor, flight records show Trump flew on Epstein's private jet at least 8 times between 1993 and 1996—"many more times than previously has been reported."
              </p>
            </div>
            <div style={{ display: 'grid', gap: '10px' }}>
              {[
                { detail: 'On one flight in 1993, only passengers were Trump and Epstein', color: '#f59e0b' },
                { detail: 'On another flight, only 3 passengers: Trump, Epstein, and a 20-year-old woman (name redacted)', color: '#ef4444' },
                { detail: 'At least 4 flights included Ghislaine Maxwell', color: '#a855f7' },
                { detail: '"Two passengers were women who would be possible witnesses in a Maxwell case"', color: '#f59e0b' },
              ].map((item, i) => (
                <div key={i} style={{ padding: '10px 14px', background: '#0a0a0f', borderRadius: '6px', borderLeft: `3px solid ${item.color}`, fontSize: '12px', color: '#a8a8b8' }}>
                  {item.detail}
                </div>
              ))}
            </div>
            <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>
              <a href="https://www.nbcnews.com/politics/justice-department/justice-department-releases-3rd-batch-jeffrey-epstein-files-mention-tr-rcna250533" target="_blank" rel="noopener noreferrer" style={{ color: '#6b6b7b', textDecoration: 'underline' }}>Source: NBC News</a> •
              <a href="https://www.aljazeera.com/news/2025/12/23/newly-released-epstein-files-spotlight-trumps-past-jet-trips-with-maxwell" target="_blank" rel="noopener noreferrer" style={{ color: '#6b6b7b', textDecoration: 'underline', marginLeft: '8px' }}>Al Jazeera</a>
            </div>
          </Card>

          {/* Additional Context */}
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#6b6b7b',
            marginBottom: '16px',
            paddingBottom: '8px',
            borderBottom: '1px solid #1a1a22',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Additional Context
          </div>

          {/* Mar-a-Lago Connection */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #ef4444' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '10px', fontWeight: '600', padding: '4px 10px', borderRadius: '4px', background: 'rgba(239,68,68,0.2)', color: '#ef4444' }}>MAR-A-LAGO</span>
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', margin: '0 0 12px 0' }}>Mar-a-Lago Subpoenaed for Employment Records</h3>
            <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '12px' }}>
              <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, lineHeight: 1.7 }}>
                Documents reveal Mar-a-Lago was subpoenaed in 2021 for employment records as part of the Maxwell investigation. Virginia Giuffre, one of Epstein's most prominent accusers, was recruited while working as a locker-room attendant at Mar-a-Lago in 2000.
              </p>
            </div>
            <div style={{ padding: '12px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: '8px', borderLeft: '2px solid #ef4444' }}>
              <div style={{ fontSize: '12px', color: '#ef4444', marginBottom: '6px' }}>FROM THE FILES</div>
              <p style={{ fontSize: '13px', color: '#d4d4dc', margin: 0, lineHeight: 1.6, fontStyle: 'italic' }}>
                A 2012 message found in the files asks: "What does JE think of going to Mar-a-Lago after xmas instead of his island?"
              </p>
            </div>
            <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: {renderSourceLinks(['CBS News', 'Wikipedia', 'Court Records'])}</div>
          </Card>

          {/* Conflict of Interest */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #a855f7' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{ fontSize: '10px', fontWeight: '600', padding: '4px 10px', borderRadius: '4px', background: 'rgba(168,85,247,0.2)', color: '#a855f7' }}>CONFLICT OF INTEREST</span>
            </div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#fff', margin: '0 0 12px 0' }}>Trump's Former Lawyer Overseeing Release</h3>
            <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px', marginBottom: '12px' }}>
              <p style={{ fontSize: '14px', color: '#d4d4dc', margin: 0, lineHeight: 1.7 }}>
                Deputy Attorney General Todd Blanche—who is overseeing the Epstein file releases—is Trump's former personal defense attorney. Blanche previously held a private meeting with Ghislaine Maxwell in prison, during which Maxwell reportedly "gushed" about Trump.
              </p>
            </div>
            <div style={{ padding: '12px 14px', background: 'rgba(168,85,247,0.06)', borderRadius: '8px' }}>
              <div style={{ fontSize: '12px', color: '#a855f7', marginBottom: '6px' }}>BLANCHE'S STATEMENT</div>
              <p style={{ fontSize: '13px', color: '#d4d4dc', margin: 0, lineHeight: 1.6, fontStyle: 'italic' }}>
                "We didn't protect President Trump. We didn't protect or not protect anybody."
              </p>
            </div>
            <div style={{ fontSize: '10px', color: '#4a4a5a', marginTop: '12px' }}>Sources: {renderSourceLinks(['Daily Beast', 'NPR', 'ABC News'])}</div>
          </Card>

          {/* Timeline */}
          <Card style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '14px', color: '#3b82f6', fontWeight: '600', marginBottom: '16px' }}>Broken Promise Timeline</div>
            <div style={{ display: 'grid', gap: '12px' }}>
              {[
                { date: 'June 2024', event: 'Trump says he "would" release Epstein files', color: '#22c55e' },
                { date: 'Nov 19, 2025', event: 'Trump signs Epstein Files Transparency Act (427-1 vote forced his hand)', color: '#3b82f6' },
                { date: 'Dec 19, 2025', event: 'DEADLINE: DOJ required to release all files', color: '#ef4444' },
                { date: 'Dec 19, 2025', event: 'DOJ MISSES deadline, releases only ~500K heavily redacted pages', color: '#ef4444' },
                { date: 'Dec 23, 2025', event: 'Larger release reveals 8 Trump flights on Epstein jet', color: '#f59e0b' },
                { date: 'Jan 30, 2026', event: '3M more pages released, 2.5M still withheld', color: '#f59e0b' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: item.color, marginTop: '4px', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '11px', color: '#6b6b7b' }}>{item.date}</div>
                    <div style={{ fontSize: '13px', color: '#d4d4dc' }}>{item.event}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* What Trump Has Said */}
          <Card style={{ marginBottom: '16px', borderLeft: '3px solid #6b6b7b' }}>
            <div style={{ fontSize: '14px', color: '#6b6b7b', fontWeight: '600', marginBottom: '16px' }}>What Trump Has Said</div>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px' }}>
                <p style={{ fontSize: '13px', color: '#d4d4dc', margin: 0, fontStyle: 'italic' }}>"I was never on Epstein's Plane, or at his 'stupid' Island."</p>
                <div style={{ fontSize: '10px', color: '#ef4444', marginTop: '6px' }}>— Trump, 2024 (contradicted by flight logs)</div>
              </div>
              <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px' }}>
                <p style={{ fontSize: '13px', color: '#d4d4dc', margin: 0, fontStyle: 'italic' }}>"He's a terrific guy. He's a lot of fun to be with. It is even said that he likes beautiful women as much as I do, and many of them are on the younger side."</p>
                <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '6px' }}>— Trump, New York Magazine, 2002</div>
              </div>
              <div style={{ padding: '12px 14px', background: '#0a0a0f', borderRadius: '8px' }}>
                <p style={{ fontSize: '13px', color: '#d4d4dc', margin: 0, fontStyle: 'italic' }}>"I wish her well."</p>
                <div style={{ fontSize: '10px', color: '#6b6b7b', marginTop: '6px' }}>— Trump on Maxwell's arrest, 2020 (said twice when pressed)</div>
              </div>
            </div>
          </Card>

          {/* Important Context */}
          <Card>
            <div style={{ padding: '14px 16px', background: 'rgba(59,130,246,0.08)', borderRadius: '10px', fontSize: '13px', color: '#93c5fd', lineHeight: 1.7 }}>
              <strong>Important:</strong> Trump has not been charged with any crimes related to Epstein. The DOJ has stated some documents contain "unfounded and false" claims. However, Trump's documented friendship with Epstein, his flights on Epstein's plane, and the recruitment of victims from his club are verified facts. The administration's handling of the file release—missing the deadline, excessive redactions, and having Trump's former lawyer oversee the process—has drawn bipartisan criticism.
            </div>
            <div style={{ marginTop: '16px', display: 'grid', gap: '10px' }}>
              <a href="https://en.wikipedia.org/wiki/Relationship_of_Donald_Trump_and_Jeffrey_Epstein" target="_blank" rel="noopener noreferrer" style={{ display: 'block', padding: '12px 14px', background: 'rgba(59,130,246,0.1)', borderRadius: '8px', textDecoration: 'none' }}>
                <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: '600' }}>Wikipedia: Trump-Epstein Relationship</div>
                <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '2px' }}>Comprehensive timeline with sources</div>
              </a>
              <a href="https://abcnews.go.com/Politics/timeline-trump-epsteins-relationship-trump-falling/story?id=124241038" target="_blank" rel="noopener noreferrer" style={{ display: 'block', padding: '12px 14px', background: 'rgba(59,130,246,0.1)', borderRadius: '8px', textDecoration: 'none' }}>
                <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: '600' }}>ABC News: Detailed Timeline</div>
                <div style={{ fontSize: '11px', color: '#6b6b7b', marginTop: '2px' }}>From the 1990s through today</div>
              </a>
            </div>
          </Card>
        </>}
      </main>

      <footer style={{ borderTop: '1px solid #1a1a22', padding: '32px 20px 80px', maxWidth: '900px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '14px', color: '#6b6b7b', fontWeight: '600', marginBottom: '6px' }}>Trump Accountability Tracker</div>
          <div style={{ fontSize: '12px', color: '#4a4a5a', lineHeight: 1.6 }}>
            Non-partisan, fact-based. Every claim sourced. Auto-updates hourly.
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <button type="button" onClick={() => handleTabClick('sources')}
            style={{ background: 'none', border: '1px solid #1e1e28', borderRadius: '6px', padding: '8px 16px', color: '#6b6b7b', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit' }}
          >Sources & Methodology</button>
          <button type="button" onClick={() => {
            const text = 'Check out the Trump Accountability Tracker — every claim sourced, updated in real time.\n\n#TrumpAccountability\n' + window.location.origin;
            navigator.clipboard?.writeText(text);
            const toast = document.createElement('div');
            toast.textContent = '\u2713 Link copied — share it!';
            toast.style.cssText = 'position:fixed;top:20px;left:50%;transform:translateX(-50%);background:#3b82f6;color:#fff;padding:8px 16px;border-radius:8px;font-size:13px;font-weight:600;z-index:9999;animation:fadeOut 2s forwards';
            document.body.appendChild(toast);
            setTimeout(() => toast.remove(), 2000);
          }}
            style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '6px', padding: '8px 16px', color: '#3b82f6', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '600' }}
          >📋 Copy Site Link</button>
        </div>

        <div style={{ fontSize: '11px', color: '#3a3a4a', textAlign: 'center', lineHeight: 1.7 }}>
          Data from U.S. Treasury, Forbes, PolitiFact, Just Security, ACLU, CREW, GAO, and more.
          <br />Found an error? <button type="button" onClick={() => handleTabClick('sources')} style={{ background: 'none', border: 'none', color: '#4a4a5a', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit' }}>Report it here</button>.
        </div>
      </footer>

      {/* "While You've Been Here" sticky counter — isolated component */}
      <LiveImpactBar debtPerSecond={data.debt?.perSecond} />

      <style>{`
        @keyframes rotatorProgress {
          from { width: 0%; }
          to { width: 100%; }
        }
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;600;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse {
          0%, 100% { animation-timing-function: cubic-bezier(0.4, 0, 0.6, 1); opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes fadeOut {
          0% { opacity: 1; transform: translateX(-50%) translateY(0); }
          70% { opacity: 1; transform: translateX(-50%) translateY(0); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
        }
        * { box-sizing: border-box; }
        ::selection { background: rgba(239,68,68,.3) }
        nav::-webkit-scrollbar { display: none }
        nav { -ms-overflow-style: none; scrollbar-width: none; }
        a, button { touch-action: manipulation; -webkit-touch-callout: none; transition: opacity 0.15s, filter 0.15s; }
        button:hover:not(:disabled) { filter: brightness(1.15); }
        a:hover { filter: brightness(1.2); }
        @media print {
          body { background: white !important; color: black !important; }
          nav, header, button, .no-print { display: none !important; }
          * { color: black !important; background: white !important; border-color: #ccc !important; }
          a { text-decoration: underline; }
          a[href]:after { content: " (" attr(href) ")"; font-size: 0.8em; }
        }
      `}</style>
    </div>
  );
}

// Wrap App with ErrorBoundary for production safety
function AppWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default AppWithErrorBoundary;
